/* eslint-disable */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { isObject, pick } from 'lodash';
import humps from 'humps';
import qs from 'qs';
import { FileRepresenting, processFilesInParams } from './asset';

// Use qs to format query parameters (correctly serializes nested objects)
const requestInterceptor = (config: AxiosRequestConfig) => {
  config.paramsSerializer = (params: any) => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encodeValuesOnly: true,
    });
  };

  return config;
};

interface EntityResponse<T> {
  data: T;
  alternateLink?: string;
}

export interface PaginatedResponse<T> {
  data: T;
  page: number;
  perPage: number;
  totalPages: number;
  totalCount: number;
  loadNext?: () => Promise<PaginatedResponse<T>>;
  alternateLink?: string;
  nextPage?: number;
  permissions: Set<string>;
}

type Authenticator = () => Promise<string | undefined>;
type HeaderInterceptor = (headers: Record<string, string>) => Promise<void>;

export class API {
  readonly baseUrl: string;
  private readonly headerInterceptor: HeaderInterceptor | undefined;
  private readonly membershipAuthenticate: Authenticator | undefined;
  private readonly httpClient: AxiosInstance;

  constructor(options: { baseUrl?: string; membership?: Authenticator; headerInterceptor?: HeaderInterceptor } = {}) {
    const defaults = {
      baseUrl: 'https://ks-core-staging.herokuapp.com/v2/',
    };
    const config = { ...defaults, ...options };
    this.baseUrl = config.baseUrl;
    this.membershipAuthenticate = config.membership;
    this.headerInterceptor = config.headerInterceptor;
    const axiosInstance = axios.create({
      baseURL: this.baseUrl,
    });
    axiosInstance.interceptors.request.use(requestInterceptor);
    this.httpClient = axiosInstance;
  }

  /** GET /activities */
  public async indexActivities(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** The id of the subject to get activities for */
    subjectId?: string | null;
    /** The type of the subject to get activities for */
    subjectType?: string | null;
    /** The type of activity to list. */
    type?: string | null;
    /** A plain object that will be matched against the activities' meta field */
    meta?: {
      shipmentRelevant?: boolean | null;
      productImportant?: boolean | null;
    } | null;
    /** The name of the action (in case of type `change_activity`, per instance). */
    activityAction?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          content?: string;
          createdAt: Date;
          action: string;
          modifications: {};
          meta?: {};
          recordId?: string;
          type: string;
          comments: Array<{
            id?: string;
            content?: string;
            createdAt?: Date;
            user?: {
              id: string;
              name: string;
              email: string;
              /** A file asset */
              avatar?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            };
          }>;
          user: {
            id: string;
            name: string;
            email: string;
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/activities`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, ['page', 'perPage', 'subjectId', 'subjectType', 'type', 'meta', 'activityAction'])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexActivities', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /activities */
  public async createActivity(params: {
    type?: string | null;
    activityAction?: string | null;
    recordId?: string | null;
    recordType?: string | null;
    subjectId?: string | null;
    subjectType?: string | null;
    content?: string | null;
    modifications?: {} | null;
    meta?: {} | null;
  }): Promise<
    EntityResponse<
      {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        content?: string;
        createdAt: Date;
        action: string;
        modifications: {};
        meta?: {};
        recordId?: string;
        type: string;
        comments: Array<{
          id?: string;
          content?: string;
          createdAt?: Date;
          user?: {
            id: string;
            name: string;
            email: string;
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
        }>;
        user: {
          id: string;
          name: string;
          email: string;
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/activities`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'type',
          'activityAction',
          'recordId',
          'recordType',
          'subjectId',
          'subjectType',
          'content',
          'modifications',
          'meta',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /activities/{activity-id} */
  public async updateActivity(params: {
    /** Identifier (UUID). */
    activityId: string;
    content?: string | null;
    meta?: {} | null;
  }): Promise<
    EntityResponse<
      {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        content?: string;
        createdAt: Date;
        action: string;
        modifications: {};
        meta?: {};
        recordId?: string;
        type: string;
        comments: Array<{
          id?: string;
          content?: string;
          createdAt?: Date;
          user?: {
            id: string;
            name: string;
            email: string;
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
        }>;
        user: {
          id: string;
          name: string;
          email: string;
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/activities/${params.activityId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['content', 'meta'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /activities/{activity-id} */
  public async destroyActivity(params: {
    /** Identifier (UUID). */
    activityId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/activities/${params.activityId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /additional_products */
  public async indexAdditionalProducts(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** List only products of this type. */
    type?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The type of product it is (ex: discount).  Not to be confused with merch product's `type` properties (ex: T-Shirt). */
          type?: string;
          name?: string;
          description?: string;
          sku?: string;
          /** A list of the product's variants */
          productVariants?: Array<{
            /** A unique identifier in the UUID format. */
            id: string;
            description: string;
            sku?: string;
            price?: number;
            netPrice?: number;
            taxRate?: number;
          }>;
          categories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              shop?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The actual name of this shop, displayable to the customer. */
                name?: string;
                /** Default locale (language & formats) of this shop. */
                defaultLocale?: string;
                /** Is the 'waiting queue' feature enabled for this shop? */
                waitingQueue?: boolean;
                /** Is this shop listed in the sitemap? */
                sitemap?: boolean;
                /** Extra information for this shop. */
                meta?: {};
                domain?: string;
                orderMergingAllowed?: boolean;
                paymentBlacklist?: string[];
                /** A Base64 reprensentation of the image. */
                logo?: FileRepresenting | string;
                /** Minimum amount to get free shipping */
                freeShippingThreshold?: number;
              };
            }
          >;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/additional_products`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'type'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexAdditionalProducts', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** GET /back_payments/download_urls */
  public async getDownloadUrlsForPendingBackPayments(): Promise<
    EntityResponse<{
      bankTransfer?: string;
      paypal?: string;
      bankTransferAndBook?: string;
      paypalAndBook?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/back_payments/download_urls`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /book_keeping_export */
  public async getBookKeepingExportLink(params: {
    /** Includes all invoices booked from this date on */
    startDate: Date;
    /** Includes all invoices booked up until this date */
    endDate: Date;
  }): Promise<
    EntityResponse<{
      link?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/book_keeping_export`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['startDate', 'endDate'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /categories */
  public async indexCategories(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the products by. */
    sortBy?: string | null;
    /** To query only for specific types of categories an array of types can be supplied */
    types?: string[] | null;
  }): Promise<
    PaginatedResponse<
      Array<
        | ({
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          })
        | ({
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          })
        | ({
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            shop?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The actual name of this shop, displayable to the customer. */
              name?: string;
              /** Default locale (language & formats) of this shop. */
              defaultLocale?: string;
              /** Is the 'waiting queue' feature enabled for this shop? */
              waitingQueue?: boolean;
              /** Is this shop listed in the sitemap? */
              sitemap?: boolean;
              /** Extra information for this shop. */
              meta?: {};
              domain?: string;
              orderMergingAllowed?: boolean;
              paymentBlacklist?: string[];
              /** A Base64 reprensentation of the image. */
              logo?: FileRepresenting | string;
              /** Minimum amount to get free shipping */
              freeShippingThreshold?: number;
            };
          })
        | ({
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** Date at which the tour starts (date of its first event). */
            startDate?: Date;
            /** Date at which the tour stops (date of its last event). */
            endDate?: Date;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            artists?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** A file asset */
                headerImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                sliderImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                backgroundImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            events?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The name of this event. Usually this is some title or the festivals name. */
                name?: string;
                /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
                artist?: string;
                /** Array of assigned artists */
                artists?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                    slug: string;
                  } & {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  } & {
                    /** Main categories that should be shown in the menu. */
                    mainCategory?: boolean;
                    parentCategoryId?: string;
                    parentCategory?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A unique identifier in the UUID format. */
                      shopId?: string;
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    };
                    /** Subcategories of this category. */
                    subcategories?: Array<
                      {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        /** The category's name. */
                        name?: string;
                        /** The type of this category */
                        type?: string;
                      }
                    >;
                    /** The order of the category relative to it's sibling categories */
                    order?: number;
                    /** Alternative names for this category */
                    alternativeNames?: string[];
                    /** Short abstract describing the category */
                    abstract?: string;
                    /** Longer description of the category */
                    description?: string;
                    createdAt?: Date;
                    updatedAt?: Date;
                    featuredAt?: string;
                    subtitle?: string;
                    /** If false, the subtitle will not be included when the Shop API is reading the category. */
                    subtitleInShop?: boolean;
                    sortOrderHomepage?: number;
                    sortOrderTicketTopLeft?: number;
                    sortOrderTicketTopRight?: number;
                    sortOrderTicketBottom?: number;
                    comment?: string;
                    /** Extra information for this category. */
                    meta?: {};
                    /** A file asset */
                    squareImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    portraitImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    landscapeImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                  } & {
                    /** A file asset */
                    headerImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    sliderImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    ticketImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    backgroundImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                  } & {
                    tours?: Array<
                      {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                        slug: string;
                      } & {
                        /** The category's name. */
                        name?: string;
                        /** The type of this category */
                        type?: string;
                      } & {
                        /** Main categories that should be shown in the menu. */
                        mainCategory?: boolean;
                        parentCategoryId?: string;
                        parentCategory?: {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          /** A unique identifier in the UUID format. */
                          shopId?: string;
                          name?: string;
                          /** The type of this category */
                          type?: string;
                        };
                        /** Subcategories of this category. */
                        subcategories?: Array<
                          {
                            /** A unique identifier in the UUID format. */
                            id: string;
                          } & {
                            /** The category's name. */
                            name?: string;
                            /** The type of this category */
                            type?: string;
                          }
                        >;
                        /** The order of the category relative to it's sibling categories */
                        order?: number;
                        /** Alternative names for this category */
                        alternativeNames?: string[];
                        /** Short abstract describing the category */
                        abstract?: string;
                        /** Longer description of the category */
                        description?: string;
                        createdAt?: Date;
                        updatedAt?: Date;
                        featuredAt?: string;
                        subtitle?: string;
                        /** If false, the subtitle will not be included when the Shop API is reading the category. */
                        subtitleInShop?: boolean;
                        sortOrderHomepage?: number;
                        sortOrderTicketTopLeft?: number;
                        sortOrderTicketTopRight?: number;
                        sortOrderTicketBottom?: number;
                        comment?: string;
                        /** Extra information for this category. */
                        meta?: {};
                        /** A file asset */
                        squareImage?: {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          url: string;
                          originalUrl?: string;
                          tags: string[];
                          alt?: string;
                          altDe?: string;
                          altEn?: string;
                          /** Object of sizes mapping to urls */
                          thumbs?: {
                            10: string;
                            75: string;
                            100: string;
                            150: string;
                            180: string;
                            240: string;
                            360: string;
                            370: string;
                            500: string;
                            740: string;
                            1000: string;
                            1500: string;
                          };
                        };
                        /** A file asset */
                        portraitImage?: {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          url: string;
                          originalUrl?: string;
                          tags: string[];
                          alt?: string;
                          altDe?: string;
                          altEn?: string;
                          /** Object of sizes mapping to urls */
                          thumbs?: {
                            10: string;
                            75: string;
                            100: string;
                            150: string;
                            180: string;
                            240: string;
                            360: string;
                            370: string;
                            500: string;
                            740: string;
                            1000: string;
                            1500: string;
                          };
                        };
                        /** A file asset */
                        landscapeImage?: {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          url: string;
                          originalUrl?: string;
                          tags: string[];
                          alt?: string;
                          altDe?: string;
                          altEn?: string;
                          /** Object of sizes mapping to urls */
                          thumbs?: {
                            10: string;
                            75: string;
                            100: string;
                            150: string;
                            180: string;
                            240: string;
                            360: string;
                            370: string;
                            500: string;
                            740: string;
                            1000: string;
                            1500: string;
                          };
                        };
                      } & {
                        /** Date at which the tour starts (date of its first event). */
                        startDate?: Date;
                        /** Date at which the tour stops (date of its last event). */
                        endDate?: Date;
                        sortOrderHomepage?: number;
                        sortOrderTicketTopLeft?: number;
                        sortOrderTicketTopRight?: number;
                        sortOrderTicketBottom?: number;
                        /** A file asset */
                        ticketImage?: {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          url: string;
                          originalUrl?: string;
                          tags: string[];
                          alt?: string;
                          altDe?: string;
                          altEn?: string;
                          /** Object of sizes mapping to urls */
                          thumbs?: {
                            10: string;
                            75: string;
                            100: string;
                            150: string;
                            180: string;
                            240: string;
                            360: string;
                            370: string;
                            500: string;
                            740: string;
                            1000: string;
                            1500: string;
                          };
                        };
                      }
                    >;
                  }
                >;
                /** The total price for the cheapest variant of this event. */
                price?: number;
                /** A hint to display a map to facilitate the selection of different variants from a map. */
                displayMap?: boolean;
                /** At which time the doors to the venue open (in the time zone of the venue) */
                doorTime?: string;
                /** At which time the show begins (in the time zone of the venue) */
                stageTime?: string;
                /** Date at which the event starts */
                happeningFrom?: Date;
                /** Date at which the event stops */
                happeningUntil?: Date;
                /** List the ids of categories where this event is assigned for the event's shop */
                categoryIds?: string[];
                meta?: {};
                presaleAt?: Date;
                /** Only set when event is sold out */
                soldOutMessage?: string;
                /** A file asset */
                image?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** The maximum count of items of this product that can be added to a cart. */
                cartLimit?: number;
                description?: string;
                publicShippableFrom?: Date;
              }
            >;
          })
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/categories`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'types'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexCategories', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /categories */
  public async createCategory(params: {
    name?: string | null;
    nameEn?: string | null;
    nameDe?: string | null;
    slug?: string | null;
    publishedFrom?: Date | null;
    publishedUntil?: Date | null;
    order?: number | null;
    alternativeNames?: string[] | null;
    abstract?: string | null;
    description?: string | null;
    comment?: string | null;
    mainCategory?: boolean | null;
    sortOrderHomepage?: number | null;
    sortOrderTicketTopLeft?: number | null;
    sortOrderTicketTopRight?: number | null;
    sortOrderTicketBottom?: number | null;
    meta?: {} | null;
    squareImage?: FileRepresenting | string | null;
    portraitImage?: FileRepresenting | string | null;
    landscapeImage?: FileRepresenting | string | null;
    subtitle?: string | null;
    subtitleInShop?: boolean | null;
    parentCategoryId?: string | null;
    shopId?: string | null;
    type?: string | null;
  }): Promise<
    EntityResponse<
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** A file asset */
          headerImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          sliderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          backgroundImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          tours?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** Date at which the tour starts (date of its first event). */
              startDate?: Date;
              /** Date at which the tour stops (date of its last event). */
              endDate?: Date;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          shop?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The actual name of this shop, displayable to the customer. */
            name?: string;
            /** Default locale (language & formats) of this shop. */
            defaultLocale?: string;
            /** Is the 'waiting queue' feature enabled for this shop? */
            waitingQueue?: boolean;
            /** Is this shop listed in the sitemap? */
            sitemap?: boolean;
            /** Extra information for this shop. */
            meta?: {};
            domain?: string;
            orderMergingAllowed?: boolean;
            paymentBlacklist?: string[];
            /** A Base64 reprensentation of the image. */
            logo?: FileRepresenting | string;
            /** Minimum amount to get free shipping */
            freeShippingThreshold?: number;
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** Date at which the tour starts (date of its first event). */
          startDate?: Date;
          /** Date at which the tour stops (date of its last event). */
          endDate?: Date;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          artists?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** A file asset */
              headerImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              sliderImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              backgroundImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          events?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The name of this event. Usually this is some title or the festivals name. */
              name?: string;
              /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
              artist?: string;
              /** Array of assigned artists */
              artists?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                } & {
                  /** Main categories that should be shown in the menu. */
                  mainCategory?: boolean;
                  parentCategoryId?: string;
                  parentCategory?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A unique identifier in the UUID format. */
                    shopId?: string;
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  };
                  /** Subcategories of this category. */
                  subcategories?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    }
                  >;
                  /** The order of the category relative to it's sibling categories */
                  order?: number;
                  /** Alternative names for this category */
                  alternativeNames?: string[];
                  /** Short abstract describing the category */
                  abstract?: string;
                  /** Longer description of the category */
                  description?: string;
                  createdAt?: Date;
                  updatedAt?: Date;
                  featuredAt?: string;
                  subtitle?: string;
                  /** If false, the subtitle will not be included when the Shop API is reading the category. */
                  subtitleInShop?: boolean;
                  sortOrderHomepage?: number;
                  sortOrderTicketTopLeft?: number;
                  sortOrderTicketTopRight?: number;
                  sortOrderTicketBottom?: number;
                  comment?: string;
                  /** Extra information for this category. */
                  meta?: {};
                  /** A file asset */
                  squareImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  portraitImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  landscapeImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  /** A file asset */
                  headerImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  sliderImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  ticketImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  backgroundImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  tours?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                      slug: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    } & {
                      /** Main categories that should be shown in the menu. */
                      mainCategory?: boolean;
                      parentCategoryId?: string;
                      parentCategory?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        /** A unique identifier in the UUID format. */
                        shopId?: string;
                        name?: string;
                        /** The type of this category */
                        type?: string;
                      };
                      /** Subcategories of this category. */
                      subcategories?: Array<
                        {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          /** The category's name. */
                          name?: string;
                          /** The type of this category */
                          type?: string;
                        }
                      >;
                      /** The order of the category relative to it's sibling categories */
                      order?: number;
                      /** Alternative names for this category */
                      alternativeNames?: string[];
                      /** Short abstract describing the category */
                      abstract?: string;
                      /** Longer description of the category */
                      description?: string;
                      createdAt?: Date;
                      updatedAt?: Date;
                      featuredAt?: string;
                      subtitle?: string;
                      /** If false, the subtitle will not be included when the Shop API is reading the category. */
                      subtitleInShop?: boolean;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      comment?: string;
                      /** Extra information for this category. */
                      meta?: {};
                      /** A file asset */
                      squareImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      portraitImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      landscapeImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    } & {
                      /** Date at which the tour starts (date of its first event). */
                      startDate?: Date;
                      /** Date at which the tour stops (date of its last event). */
                      endDate?: Date;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      /** A file asset */
                      ticketImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                }
              >;
              /** The total price for the cheapest variant of this event. */
              price?: number;
              /** A hint to display a map to facilitate the selection of different variants from a map. */
              displayMap?: boolean;
              /** At which time the doors to the venue open (in the time zone of the venue) */
              doorTime?: string;
              /** At which time the show begins (in the time zone of the venue) */
              stageTime?: string;
              /** Date at which the event starts */
              happeningFrom?: Date;
              /** Date at which the event stops */
              happeningUntil?: Date;
              /** List the ids of categories where this event is assigned for the event's shop */
              categoryIds?: string[];
              meta?: {};
              presaleAt?: Date;
              /** Only set when event is sold out */
              soldOutMessage?: string;
              /** A file asset */
              image?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              description?: string;
              publicShippableFrom?: Date;
            }
          >;
        })
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/categories`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'nameEn',
          'nameDe',
          'slug',
          'publishedFrom',
          'publishedUntil',
          'order',
          'alternativeNames',
          'abstract',
          'description',
          'comment',
          'mainCategory',
          'sortOrderHomepage',
          'sortOrderTicketTopLeft',
          'sortOrderTicketTopRight',
          'sortOrderTicketBottom',
          'meta',
          'squareImage',
          'portraitImage',
          'landscapeImage',
          'subtitle',
          'subtitleInShop',
          'parentCategoryId',
          'shopId',
          'type',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /categories/{category-id} */
  public async showCategory(params: {
    /** Identifier (UUID). */
    categoryId: string;
  }): Promise<
    EntityResponse<
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** A file asset */
          headerImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          sliderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          backgroundImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          tours?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** Date at which the tour starts (date of its first event). */
              startDate?: Date;
              /** Date at which the tour stops (date of its last event). */
              endDate?: Date;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          shop?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The actual name of this shop, displayable to the customer. */
            name?: string;
            /** Default locale (language & formats) of this shop. */
            defaultLocale?: string;
            /** Is the 'waiting queue' feature enabled for this shop? */
            waitingQueue?: boolean;
            /** Is this shop listed in the sitemap? */
            sitemap?: boolean;
            /** Extra information for this shop. */
            meta?: {};
            domain?: string;
            orderMergingAllowed?: boolean;
            paymentBlacklist?: string[];
            /** A Base64 reprensentation of the image. */
            logo?: FileRepresenting | string;
            /** Minimum amount to get free shipping */
            freeShippingThreshold?: number;
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** Date at which the tour starts (date of its first event). */
          startDate?: Date;
          /** Date at which the tour stops (date of its last event). */
          endDate?: Date;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          artists?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** A file asset */
              headerImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              sliderImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              backgroundImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          events?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The name of this event. Usually this is some title or the festivals name. */
              name?: string;
              /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
              artist?: string;
              /** Array of assigned artists */
              artists?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                } & {
                  /** Main categories that should be shown in the menu. */
                  mainCategory?: boolean;
                  parentCategoryId?: string;
                  parentCategory?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A unique identifier in the UUID format. */
                    shopId?: string;
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  };
                  /** Subcategories of this category. */
                  subcategories?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    }
                  >;
                  /** The order of the category relative to it's sibling categories */
                  order?: number;
                  /** Alternative names for this category */
                  alternativeNames?: string[];
                  /** Short abstract describing the category */
                  abstract?: string;
                  /** Longer description of the category */
                  description?: string;
                  createdAt?: Date;
                  updatedAt?: Date;
                  featuredAt?: string;
                  subtitle?: string;
                  /** If false, the subtitle will not be included when the Shop API is reading the category. */
                  subtitleInShop?: boolean;
                  sortOrderHomepage?: number;
                  sortOrderTicketTopLeft?: number;
                  sortOrderTicketTopRight?: number;
                  sortOrderTicketBottom?: number;
                  comment?: string;
                  /** Extra information for this category. */
                  meta?: {};
                  /** A file asset */
                  squareImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  portraitImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  landscapeImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  /** A file asset */
                  headerImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  sliderImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  ticketImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  backgroundImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  tours?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                      slug: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    } & {
                      /** Main categories that should be shown in the menu. */
                      mainCategory?: boolean;
                      parentCategoryId?: string;
                      parentCategory?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        /** A unique identifier in the UUID format. */
                        shopId?: string;
                        name?: string;
                        /** The type of this category */
                        type?: string;
                      };
                      /** Subcategories of this category. */
                      subcategories?: Array<
                        {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          /** The category's name. */
                          name?: string;
                          /** The type of this category */
                          type?: string;
                        }
                      >;
                      /** The order of the category relative to it's sibling categories */
                      order?: number;
                      /** Alternative names for this category */
                      alternativeNames?: string[];
                      /** Short abstract describing the category */
                      abstract?: string;
                      /** Longer description of the category */
                      description?: string;
                      createdAt?: Date;
                      updatedAt?: Date;
                      featuredAt?: string;
                      subtitle?: string;
                      /** If false, the subtitle will not be included when the Shop API is reading the category. */
                      subtitleInShop?: boolean;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      comment?: string;
                      /** Extra information for this category. */
                      meta?: {};
                      /** A file asset */
                      squareImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      portraitImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      landscapeImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    } & {
                      /** Date at which the tour starts (date of its first event). */
                      startDate?: Date;
                      /** Date at which the tour stops (date of its last event). */
                      endDate?: Date;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      /** A file asset */
                      ticketImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                }
              >;
              /** The total price for the cheapest variant of this event. */
              price?: number;
              /** A hint to display a map to facilitate the selection of different variants from a map. */
              displayMap?: boolean;
              /** At which time the doors to the venue open (in the time zone of the venue) */
              doorTime?: string;
              /** At which time the show begins (in the time zone of the venue) */
              stageTime?: string;
              /** Date at which the event starts */
              happeningFrom?: Date;
              /** Date at which the event stops */
              happeningUntil?: Date;
              /** List the ids of categories where this event is assigned for the event's shop */
              categoryIds?: string[];
              meta?: {};
              presaleAt?: Date;
              /** Only set when event is sold out */
              soldOutMessage?: string;
              /** A file asset */
              image?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              description?: string;
              publicShippableFrom?: Date;
            }
          >;
        })
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/categories/${params.categoryId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /categories/{category-id} */
  public async updateCategory(params: {
    /** Identifier (UUID). */
    categoryId: string;
    name?: string | null;
    nameEn?: string | null;
    nameDe?: string | null;
    slug?: string | null;
    publishedFrom?: Date | null;
    publishedUntil?: Date | null;
    order?: number | null;
    alternativeNames?: string[] | null;
    abstract?: string | null;
    description?: string | null;
    comment?: string | null;
    mainCategory?: boolean | null;
    sortOrderHomepage?: number | null;
    sortOrderTicketTopLeft?: number | null;
    sortOrderTicketTopRight?: number | null;
    sortOrderTicketBottom?: number | null;
    meta?: {} | null;
    squareImage?: FileRepresenting | string | null;
    portraitImage?: FileRepresenting | string | null;
    landscapeImage?: FileRepresenting | string | null;
    subtitle?: string | null;
    subtitleInShop?: boolean | null;
  }): Promise<
    EntityResponse<
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** A file asset */
          headerImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          sliderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          backgroundImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          tours?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** Date at which the tour starts (date of its first event). */
              startDate?: Date;
              /** Date at which the tour stops (date of its last event). */
              endDate?: Date;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          shop?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The actual name of this shop, displayable to the customer. */
            name?: string;
            /** Default locale (language & formats) of this shop. */
            defaultLocale?: string;
            /** Is the 'waiting queue' feature enabled for this shop? */
            waitingQueue?: boolean;
            /** Is this shop listed in the sitemap? */
            sitemap?: boolean;
            /** Extra information for this shop. */
            meta?: {};
            domain?: string;
            orderMergingAllowed?: boolean;
            paymentBlacklist?: string[];
            /** A Base64 reprensentation of the image. */
            logo?: FileRepresenting | string;
            /** Minimum amount to get free shipping */
            freeShippingThreshold?: number;
          };
        })
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** Date at which the tour starts (date of its first event). */
          startDate?: Date;
          /** Date at which the tour stops (date of its last event). */
          endDate?: Date;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          /** A file asset */
          ticketImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          artists?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** A file asset */
              headerImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              sliderImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              backgroundImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          events?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The name of this event. Usually this is some title or the festivals name. */
              name?: string;
              /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
              artist?: string;
              /** Array of assigned artists */
              artists?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                } & {
                  /** Main categories that should be shown in the menu. */
                  mainCategory?: boolean;
                  parentCategoryId?: string;
                  parentCategory?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A unique identifier in the UUID format. */
                    shopId?: string;
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  };
                  /** Subcategories of this category. */
                  subcategories?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    }
                  >;
                  /** The order of the category relative to it's sibling categories */
                  order?: number;
                  /** Alternative names for this category */
                  alternativeNames?: string[];
                  /** Short abstract describing the category */
                  abstract?: string;
                  /** Longer description of the category */
                  description?: string;
                  createdAt?: Date;
                  updatedAt?: Date;
                  featuredAt?: string;
                  subtitle?: string;
                  /** If false, the subtitle will not be included when the Shop API is reading the category. */
                  subtitleInShop?: boolean;
                  sortOrderHomepage?: number;
                  sortOrderTicketTopLeft?: number;
                  sortOrderTicketTopRight?: number;
                  sortOrderTicketBottom?: number;
                  comment?: string;
                  /** Extra information for this category. */
                  meta?: {};
                  /** A file asset */
                  squareImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  portraitImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  landscapeImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  /** A file asset */
                  headerImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  sliderImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  ticketImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  backgroundImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  tours?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                      slug: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    } & {
                      /** Main categories that should be shown in the menu. */
                      mainCategory?: boolean;
                      parentCategoryId?: string;
                      parentCategory?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        /** A unique identifier in the UUID format. */
                        shopId?: string;
                        name?: string;
                        /** The type of this category */
                        type?: string;
                      };
                      /** Subcategories of this category. */
                      subcategories?: Array<
                        {
                          /** A unique identifier in the UUID format. */
                          id: string;
                        } & {
                          /** The category's name. */
                          name?: string;
                          /** The type of this category */
                          type?: string;
                        }
                      >;
                      /** The order of the category relative to it's sibling categories */
                      order?: number;
                      /** Alternative names for this category */
                      alternativeNames?: string[];
                      /** Short abstract describing the category */
                      abstract?: string;
                      /** Longer description of the category */
                      description?: string;
                      createdAt?: Date;
                      updatedAt?: Date;
                      featuredAt?: string;
                      subtitle?: string;
                      /** If false, the subtitle will not be included when the Shop API is reading the category. */
                      subtitleInShop?: boolean;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      comment?: string;
                      /** Extra information for this category. */
                      meta?: {};
                      /** A file asset */
                      squareImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      portraitImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      landscapeImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    } & {
                      /** Date at which the tour starts (date of its first event). */
                      startDate?: Date;
                      /** Date at which the tour stops (date of its last event). */
                      endDate?: Date;
                      sortOrderHomepage?: number;
                      sortOrderTicketTopLeft?: number;
                      sortOrderTicketTopRight?: number;
                      sortOrderTicketBottom?: number;
                      /** A file asset */
                      ticketImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                }
              >;
              /** The total price for the cheapest variant of this event. */
              price?: number;
              /** A hint to display a map to facilitate the selection of different variants from a map. */
              displayMap?: boolean;
              /** At which time the doors to the venue open (in the time zone of the venue) */
              doorTime?: string;
              /** At which time the show begins (in the time zone of the venue) */
              stageTime?: string;
              /** Date at which the event starts */
              happeningFrom?: Date;
              /** Date at which the event stops */
              happeningUntil?: Date;
              /** List the ids of categories where this event is assigned for the event's shop */
              categoryIds?: string[];
              meta?: {};
              presaleAt?: Date;
              /** Only set when event is sold out */
              soldOutMessage?: string;
              /** A file asset */
              image?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              description?: string;
              publicShippableFrom?: Date;
            }
          >;
        })
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/categories/${params.categoryId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'nameEn',
          'nameDe',
          'slug',
          'publishedFrom',
          'publishedUntil',
          'order',
          'alternativeNames',
          'abstract',
          'description',
          'comment',
          'mainCategory',
          'sortOrderHomepage',
          'sortOrderTicketTopLeft',
          'sortOrderTicketTopRight',
          'sortOrderTicketBottom',
          'meta',
          'squareImage',
          'portraitImage',
          'landscapeImage',
          'subtitle',
          'subtitleInShop',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /categories/{category-id} */
  public async destroyCategory(params: {
    /** Identifier (UUID). */
    categoryId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/categories/${params.categoryId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /companies */
  public async indexCompanies(params: {
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the contracts by. */
    sortBy?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/companies`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['term', 'page', 'perPage', 'sortDir', 'sortBy'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexCompanies', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /companies */
  public async createCompany(params: {
    slug?: string | null;
    name?: string | null;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    bankAccount?: {
      name?: string | null;
      iban?: string | null;
      bic?: string | null;
      bankName?: string | null;
    } | null;
    vatId?: string | null;
    vatNumber?: string | null;
    eventCreditorReference?: string | null;
    merchCreditorReference?: string | null;
    merchClearingOrderId?: string | null;
    features?: string[] | null;
    invoicingCycle?: string | null;
    billingEmails?: string[] | null;
    defaultEventPayoutPercentage?: number | null;
    eventPayoutCycle?: string | null;
    icon?: string | null;
  }): Promise<
    EntityResponse<
      {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        bankAccount?: {
          name?: string;
          iban?: string;
          bic?: string;
          bankName?: string;
        };
        vatId?: string;
        vatNumber?: string;
        eventCreditorReference?: string;
        merchCreditorReference?: string;
        merchClearingOrderId?: string;
        features?: string[];
        invoicingCycle?: string;
        billingEmails?: string[];
        assignableRoles?: string[];
        defaultEventPayoutPercentage?: number;
        eventPayoutCycle?: string;
        lastEventPayout?: Date;
      } & {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        memberships?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            indexPermissions?: string[];
          }
        >;
        /** A file asset */
        icon?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        lastBookedClearingInvoice?: {
          /** A unique identifier in the UUID format. */
          id?: string;
          reference?: string;
          bookedAt?: Date;
          /** A unique identifier in the UUID format. */
          orderId?: string;
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/companies`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'slug',
          'name',
          'address',
          'bankAccount',
          'vatId',
          'vatNumber',
          'eventCreditorReference',
          'merchCreditorReference',
          'merchClearingOrderId',
          'features',
          'invoicingCycle',
          'billingEmails',
          'defaultEventPayoutPercentage',
          'eventPayoutCycle',
          'icon',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /companies/{company-id} */
  public async showCompany(params: {
    /** Slug or Identifier (UUID). */
    companyId: string;
  }): Promise<
    EntityResponse<
      {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        bankAccount?: {
          name?: string;
          iban?: string;
          bic?: string;
          bankName?: string;
        };
        vatId?: string;
        vatNumber?: string;
        eventCreditorReference?: string;
        merchCreditorReference?: string;
        merchClearingOrderId?: string;
        features?: string[];
        invoicingCycle?: string;
        billingEmails?: string[];
        assignableRoles?: string[];
        defaultEventPayoutPercentage?: number;
        eventPayoutCycle?: string;
        lastEventPayout?: Date;
      } & {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        memberships?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            indexPermissions?: string[];
          }
        >;
        /** A file asset */
        icon?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        lastBookedClearingInvoice?: {
          /** A unique identifier in the UUID format. */
          id?: string;
          reference?: string;
          bookedAt?: Date;
          /** A unique identifier in the UUID format. */
          orderId?: string;
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/companies/${params.companyId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /companies/{company-id} */
  public async updateCompany(params: {
    /** Slug or Identifier (UUID). */
    companyId: string;
    name?: string | null;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    bankAccount?: {
      name?: string | null;
      iban?: string | null;
      bic?: string | null;
      bankName?: string | null;
    } | null;
    vatId?: string | null;
    vatNumber?: string | null;
    eventCreditorReference?: string | null;
    merchCreditorReference?: string | null;
    merchClearingOrderId?: string | null;
    features?: string[] | null;
    invoicingCycle?: string | null;
    billingEmails?: string[] | null;
    defaultEventPayoutPercentage?: number | null;
    eventPayoutCycle?: string | null;
    icon?: string | null;
  }): Promise<
    EntityResponse<
      {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        bankAccount?: {
          name?: string;
          iban?: string;
          bic?: string;
          bankName?: string;
        };
        vatId?: string;
        vatNumber?: string;
        eventCreditorReference?: string;
        merchCreditorReference?: string;
        merchClearingOrderId?: string;
        features?: string[];
        invoicingCycle?: string;
        billingEmails?: string[];
        assignableRoles?: string[];
        defaultEventPayoutPercentage?: number;
        eventPayoutCycle?: string;
        lastEventPayout?: Date;
      } & {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        memberships?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            indexPermissions?: string[];
          }
        >;
        /** A file asset */
        icon?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        lastBookedClearingInvoice?: {
          /** A unique identifier in the UUID format. */
          id?: string;
          reference?: string;
          bookedAt?: Date;
          /** A unique identifier in the UUID format. */
          orderId?: string;
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/companies/${params.companyId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'address',
          'bankAccount',
          'vatId',
          'vatNumber',
          'eventCreditorReference',
          'merchCreditorReference',
          'merchClearingOrderId',
          'features',
          'invoicingCycle',
          'billingEmails',
          'defaultEventPayoutPercentage',
          'eventPayoutCycle',
          'icon',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /companies/{company-id} */
  public async destroyCompany(params: {
    /** Slug or Identifier (UUID). */
    companyId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/companies/${params.companyId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /contracts */
  public async indexContracts(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the contracts by. */
    sortBy?: string | null;
    /** `false` to list only non archived contracts (default), `true` to list only archived contracts, or `all` to ignore this filter. */
    archived?: boolean | string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name: string;
          createdAt?: Date;
          status?: string;
          reference?: string;
          archived?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          products?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              legacyId?: string;
              artist?: string;
              slug?: string;
              artistDe?: string;
              artistEn?: string;
              color?: string;
              colorDe?: string;
              colorEn?: string;
              material?: string;
              materialDe?: string;
              materialEn?: string;
              name?: string;
              nameDe?: string;
              nameEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              costAccountOne?: string;
              costAccountTwo?: string;
              owningCompany?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              };
              owningCompanyId?: string;
              contractId?: string;
              productSafetyDescription?: string;
              productSafetyDescriptionDe?: string;
              productSafetyDescriptionEn?: string;
              productType?: string;
              productTypeDe?: string;
              productTypeEn?: string;
              publishedFrom?: Date;
              publishedUntil?: Date;
              shippableFrom?: Date;
              publicShippableFrom?: Date;
              vendorIds?: string[];
              vendors?: Array<
                {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  memberships?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                      $permissions: string[];
                    } & {
                      role?: string;
                      comment?: string;
                      permissions?: string[];
                      /** On invites, the email of the invitee is displayed here (and the user is then null). */
                      invitedEmail?: string;
                      createdAt?: Date;
                      /** On invites, indicates when it expires and becomes invalid. */
                      expiresAt?: Date;
                      /** The .ics URL to the membership's calendar. */
                      calendarFeedUrl?: string;
                    } & {
                      indexPermissions?: string[];
                    }
                  >;
                  /** A file asset */
                  icon?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  lastBookedClearingInvoice?: {
                    /** A unique identifier in the UUID format. */
                    id?: string;
                    reference?: string;
                    bookedAt?: Date;
                    /** A unique identifier in the UUID format. */
                    orderId?: string;
                  };
                }
              >;
              status?: string;
              /** This represents a box of content with a title and a description. */
              details?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              /** This represents a box of content with a title and a description. */
              additionalInformation?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              productVariants?: Array<
                {
                  id?: string;
                  legacyId?: string;
                  description?: string;
                  reservedCount?: number;
                  cartCount?: number;
                  availableCount?: number;
                  fulfillmentCount?: number;
                  incompleteCount?: number;
                  pendingDeliveryCount?: number;
                  activeFrom?: Date;
                  activeUntil?: Date;
                  stockCount?: number;
                  minimumStockCount?: number;
                  weight?: number;
                  ean?: string;
                  private?: boolean;
                  order?: number;
                  priceInFuture?: boolean;
                  active?: boolean;
                  sku?: string;
                  prices?: Array<
                    {
                      id?: string;
                      shopId?: string;
                      activeFrom?: Date;
                      activeUntil?: Date;
                      amount?: number;
                      countryCode?: string;
                      comment?: string;
                      /** Price this one is replacing (ex: temporary sales, special holidays price). */
                      originalPrice?: number;
                      active?: boolean;
                    } & {
                      /** A file asset */
                      overlayListingImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      overlayDetailImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                  taxRate?: number;
                  taxes?: Array<{
                    id?: string;
                    countryCode?: string;
                    amount?: number;
                  }>;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  /** A file asset */
                  overlayListingImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  overlayDetailImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                }
              >;
              images?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                }
              >;
              recordType?: string;
              recordDistributor?: string;
              recordLabel?: string;
              recordReleasedAt?: Date;
              /** An override for the price of a product only used in the charts export. */
              recordChartsPrice?: number;
              dhlShippingFrom?: Date;
              customsTariffNumber?: string;
              combinedProductIds?: string[][];
              meta?: {};
              /** Exclude the product from feeds (ex: Facebook, Google). */
              excludeFromFeeds?: boolean;
              /** The Google Shopping category for this product. */
              googleFeedCategory?: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              ownerships?: Array<
                {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  role?: string;
                  company?: {
                    name?: string;
                    address?: {
                      company?: string;
                      firstName?: string;
                      lastName?: string;
                      address?: string;
                      address2?: string;
                      postCode?: string;
                      city?: string;
                      countryCode?: string;
                      email?: string;
                      phone?: string;
                    };
                    bankAccount?: {
                      name?: string;
                      iban?: string;
                      bic?: string;
                      bankName?: string;
                    };
                    vatId?: string;
                    vatNumber?: string;
                    eventCreditorReference?: string;
                    merchCreditorReference?: string;
                    merchClearingOrderId?: string;
                    features?: string[];
                    invoicingCycle?: string;
                    billingEmails?: string[];
                    assignableRoles?: string[];
                    defaultEventPayoutPercentage?: number;
                    eventPayoutCycle?: string;
                    lastEventPayout?: Date;
                  } & {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                    slug: string;
                  };
                }
              >;
              /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
              taxesPerCountry?: {};
              shippingRequirements?: string[];
              prohibitedShippingTags?: string[];
              freeShippingMode?: string;
            }
          >;
          companies?: Array<
            {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              memberships?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  role?: string;
                  comment?: string;
                  permissions?: string[];
                  /** On invites, the email of the invitee is displayed here (and the user is then null). */
                  invitedEmail?: string;
                  createdAt?: Date;
                  /** On invites, indicates when it expires and becomes invalid. */
                  expiresAt?: Date;
                  /** The .ics URL to the membership's calendar. */
                  calendarFeedUrl?: string;
                } & {
                  indexPermissions?: string[];
                }
              >;
              /** A file asset */
              icon?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              lastBookedClearingInvoice?: {
                /** A unique identifier in the UUID format. */
                id?: string;
                reference?: string;
                bookedAt?: Date;
                /** A unique identifier in the UUID format. */
                orderId?: string;
              };
            }
          >;
          contractAgreements?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
              priority?: number;
              createdAt?: Date;
              invalidatedAt?: Date;
              status?: string;
            } & {
              shops?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** The actual name of this shop, displayable to the customer. */
                  name?: string;
                  /** Default locale (language & formats) of this shop. */
                  defaultLocale?: string;
                  /** Is the 'waiting queue' feature enabled for this shop? */
                  waitingQueue?: boolean;
                  /** Is this shop listed in the sitemap? */
                  sitemap?: boolean;
                  /** Extra information for this shop. */
                  meta?: {};
                  domain?: string;
                  orderMergingAllowed?: boolean;
                  paymentBlacklist?: string[];
                  /** A Base64 reprensentation of the image. */
                  logo?: FileRepresenting | string;
                  /** Minimum amount to get free shipping */
                  freeShippingThreshold?: number;
                }
              >;
              contractAgreementLines?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  name?: string;
                  priority?: number;
                  createdAt?: Date;
                  invalidatedAt?: Date;
                  status?: string;
                }
              >;
            }
          >;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/contracts`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'archived'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexContracts', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /contracts */
  public async createContract(params: { name?: string | null; archived?: boolean | null }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name: string;
        createdAt?: Date;
        status?: string;
        reference?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        products?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            legacyId?: string;
            artist?: string;
            slug?: string;
            artistDe?: string;
            artistEn?: string;
            color?: string;
            colorDe?: string;
            colorEn?: string;
            material?: string;
            materialDe?: string;
            materialEn?: string;
            name?: string;
            nameDe?: string;
            nameEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
            /** The maximum count of items of this product that can be added to a cart. */
            cartLimit?: number;
            costAccountOne?: string;
            costAccountTwo?: string;
            owningCompany?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              memberships?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  role?: string;
                  comment?: string;
                  permissions?: string[];
                  /** On invites, the email of the invitee is displayed here (and the user is then null). */
                  invitedEmail?: string;
                  createdAt?: Date;
                  /** On invites, indicates when it expires and becomes invalid. */
                  expiresAt?: Date;
                  /** The .ics URL to the membership's calendar. */
                  calendarFeedUrl?: string;
                } & {
                  indexPermissions?: string[];
                }
              >;
              /** A file asset */
              icon?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              lastBookedClearingInvoice?: {
                /** A unique identifier in the UUID format. */
                id?: string;
                reference?: string;
                bookedAt?: Date;
                /** A unique identifier in the UUID format. */
                orderId?: string;
              };
            };
            owningCompanyId?: string;
            contractId?: string;
            productSafetyDescription?: string;
            productSafetyDescriptionDe?: string;
            productSafetyDescriptionEn?: string;
            productType?: string;
            productTypeDe?: string;
            productTypeEn?: string;
            publishedFrom?: Date;
            publishedUntil?: Date;
            shippableFrom?: Date;
            publicShippableFrom?: Date;
            vendorIds?: string[];
            vendors?: Array<
              {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              }
            >;
            status?: string;
            /** This represents a box of content with a title and a description. */
            details?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            /** This represents a box of content with a title and a description. */
            additionalInformation?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            productVariants?: Array<
              {
                id?: string;
                legacyId?: string;
                description?: string;
                reservedCount?: number;
                cartCount?: number;
                availableCount?: number;
                fulfillmentCount?: number;
                incompleteCount?: number;
                pendingDeliveryCount?: number;
                activeFrom?: Date;
                activeUntil?: Date;
                stockCount?: number;
                minimumStockCount?: number;
                weight?: number;
                ean?: string;
                private?: boolean;
                order?: number;
                priceInFuture?: boolean;
                active?: boolean;
                sku?: string;
                prices?: Array<
                  {
                    id?: string;
                    shopId?: string;
                    activeFrom?: Date;
                    activeUntil?: Date;
                    amount?: number;
                    countryCode?: string;
                    comment?: string;
                    /** Price this one is replacing (ex: temporary sales, special holidays price). */
                    originalPrice?: number;
                    active?: boolean;
                  } & {
                    /** A file asset */
                    overlayListingImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    overlayDetailImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                  }
                >;
                taxRate?: number;
                taxes?: Array<{
                  id?: string;
                  countryCode?: string;
                  amount?: number;
                }>;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            images?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              }
            >;
            recordType?: string;
            recordDistributor?: string;
            recordLabel?: string;
            recordReleasedAt?: Date;
            /** An override for the price of a product only used in the charts export. */
            recordChartsPrice?: number;
            dhlShippingFrom?: Date;
            customsTariffNumber?: string;
            combinedProductIds?: string[][];
            meta?: {};
            /** Exclude the product from feeds (ex: Facebook, Google). */
            excludeFromFeeds?: boolean;
            /** The Google Shopping category for this product. */
            googleFeedCategory?: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            ownerships?: Array<
              {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                role?: string;
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
            /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
            taxesPerCountry?: {};
            shippingRequirements?: string[];
            prohibitedShippingTags?: string[];
            freeShippingMode?: string;
          }
        >;
        companies?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        contractAgreements?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            priority?: number;
            createdAt?: Date;
            invalidatedAt?: Date;
            status?: string;
          } & {
            shops?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The actual name of this shop, displayable to the customer. */
                name?: string;
                /** Default locale (language & formats) of this shop. */
                defaultLocale?: string;
                /** Is the 'waiting queue' feature enabled for this shop? */
                waitingQueue?: boolean;
                /** Is this shop listed in the sitemap? */
                sitemap?: boolean;
                /** Extra information for this shop. */
                meta?: {};
                domain?: string;
                orderMergingAllowed?: boolean;
                paymentBlacklist?: string[];
                /** A Base64 reprensentation of the image. */
                logo?: FileRepresenting | string;
                /** Minimum amount to get free shipping */
                freeShippingThreshold?: number;
              }
            >;
            contractAgreementLines?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                priority?: number;
                createdAt?: Date;
                invalidatedAt?: Date;
                status?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/contracts`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['name', 'archived'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /contracts/{contract-id} */
  public async showContract(params: {
    /** Identifier (UUID). */
    contractId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name: string;
        createdAt?: Date;
        status?: string;
        reference?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        products?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            legacyId?: string;
            artist?: string;
            slug?: string;
            artistDe?: string;
            artistEn?: string;
            color?: string;
            colorDe?: string;
            colorEn?: string;
            material?: string;
            materialDe?: string;
            materialEn?: string;
            name?: string;
            nameDe?: string;
            nameEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
            /** The maximum count of items of this product that can be added to a cart. */
            cartLimit?: number;
            costAccountOne?: string;
            costAccountTwo?: string;
            owningCompany?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              memberships?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  role?: string;
                  comment?: string;
                  permissions?: string[];
                  /** On invites, the email of the invitee is displayed here (and the user is then null). */
                  invitedEmail?: string;
                  createdAt?: Date;
                  /** On invites, indicates when it expires and becomes invalid. */
                  expiresAt?: Date;
                  /** The .ics URL to the membership's calendar. */
                  calendarFeedUrl?: string;
                } & {
                  indexPermissions?: string[];
                }
              >;
              /** A file asset */
              icon?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              lastBookedClearingInvoice?: {
                /** A unique identifier in the UUID format. */
                id?: string;
                reference?: string;
                bookedAt?: Date;
                /** A unique identifier in the UUID format. */
                orderId?: string;
              };
            };
            owningCompanyId?: string;
            contractId?: string;
            productSafetyDescription?: string;
            productSafetyDescriptionDe?: string;
            productSafetyDescriptionEn?: string;
            productType?: string;
            productTypeDe?: string;
            productTypeEn?: string;
            publishedFrom?: Date;
            publishedUntil?: Date;
            shippableFrom?: Date;
            publicShippableFrom?: Date;
            vendorIds?: string[];
            vendors?: Array<
              {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              }
            >;
            status?: string;
            /** This represents a box of content with a title and a description. */
            details?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            /** This represents a box of content with a title and a description. */
            additionalInformation?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            productVariants?: Array<
              {
                id?: string;
                legacyId?: string;
                description?: string;
                reservedCount?: number;
                cartCount?: number;
                availableCount?: number;
                fulfillmentCount?: number;
                incompleteCount?: number;
                pendingDeliveryCount?: number;
                activeFrom?: Date;
                activeUntil?: Date;
                stockCount?: number;
                minimumStockCount?: number;
                weight?: number;
                ean?: string;
                private?: boolean;
                order?: number;
                priceInFuture?: boolean;
                active?: boolean;
                sku?: string;
                prices?: Array<
                  {
                    id?: string;
                    shopId?: string;
                    activeFrom?: Date;
                    activeUntil?: Date;
                    amount?: number;
                    countryCode?: string;
                    comment?: string;
                    /** Price this one is replacing (ex: temporary sales, special holidays price). */
                    originalPrice?: number;
                    active?: boolean;
                  } & {
                    /** A file asset */
                    overlayListingImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    overlayDetailImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                  }
                >;
                taxRate?: number;
                taxes?: Array<{
                  id?: string;
                  countryCode?: string;
                  amount?: number;
                }>;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            images?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              }
            >;
            recordType?: string;
            recordDistributor?: string;
            recordLabel?: string;
            recordReleasedAt?: Date;
            /** An override for the price of a product only used in the charts export. */
            recordChartsPrice?: number;
            dhlShippingFrom?: Date;
            customsTariffNumber?: string;
            combinedProductIds?: string[][];
            meta?: {};
            /** Exclude the product from feeds (ex: Facebook, Google). */
            excludeFromFeeds?: boolean;
            /** The Google Shopping category for this product. */
            googleFeedCategory?: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            ownerships?: Array<
              {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                role?: string;
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
            /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
            taxesPerCountry?: {};
            shippingRequirements?: string[];
            prohibitedShippingTags?: string[];
            freeShippingMode?: string;
          }
        >;
        companies?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        contractAgreements?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            priority?: number;
            createdAt?: Date;
            invalidatedAt?: Date;
            status?: string;
          } & {
            shops?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The actual name of this shop, displayable to the customer. */
                name?: string;
                /** Default locale (language & formats) of this shop. */
                defaultLocale?: string;
                /** Is the 'waiting queue' feature enabled for this shop? */
                waitingQueue?: boolean;
                /** Is this shop listed in the sitemap? */
                sitemap?: boolean;
                /** Extra information for this shop. */
                meta?: {};
                domain?: string;
                orderMergingAllowed?: boolean;
                paymentBlacklist?: string[];
                /** A Base64 reprensentation of the image. */
                logo?: FileRepresenting | string;
                /** Minimum amount to get free shipping */
                freeShippingThreshold?: number;
              }
            >;
            contractAgreementLines?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                priority?: number;
                createdAt?: Date;
                invalidatedAt?: Date;
                status?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/contracts/${params.contractId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /contracts/{contract-id} */
  public async updateContract(params: {
    /** Identifier (UUID). */
    contractId: string;
    name?: string | null;
    archived?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name: string;
        createdAt?: Date;
        status?: string;
        reference?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        products?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            legacyId?: string;
            artist?: string;
            slug?: string;
            artistDe?: string;
            artistEn?: string;
            color?: string;
            colorDe?: string;
            colorEn?: string;
            material?: string;
            materialDe?: string;
            materialEn?: string;
            name?: string;
            nameDe?: string;
            nameEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
            /** The maximum count of items of this product that can be added to a cart. */
            cartLimit?: number;
            costAccountOne?: string;
            costAccountTwo?: string;
            owningCompany?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              memberships?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  role?: string;
                  comment?: string;
                  permissions?: string[];
                  /** On invites, the email of the invitee is displayed here (and the user is then null). */
                  invitedEmail?: string;
                  createdAt?: Date;
                  /** On invites, indicates when it expires and becomes invalid. */
                  expiresAt?: Date;
                  /** The .ics URL to the membership's calendar. */
                  calendarFeedUrl?: string;
                } & {
                  indexPermissions?: string[];
                }
              >;
              /** A file asset */
              icon?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              lastBookedClearingInvoice?: {
                /** A unique identifier in the UUID format. */
                id?: string;
                reference?: string;
                bookedAt?: Date;
                /** A unique identifier in the UUID format. */
                orderId?: string;
              };
            };
            owningCompanyId?: string;
            contractId?: string;
            productSafetyDescription?: string;
            productSafetyDescriptionDe?: string;
            productSafetyDescriptionEn?: string;
            productType?: string;
            productTypeDe?: string;
            productTypeEn?: string;
            publishedFrom?: Date;
            publishedUntil?: Date;
            shippableFrom?: Date;
            publicShippableFrom?: Date;
            vendorIds?: string[];
            vendors?: Array<
              {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              }
            >;
            status?: string;
            /** This represents a box of content with a title and a description. */
            details?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            /** This represents a box of content with a title and a description. */
            additionalInformation?: {
              title?: string;
              titleDe?: string;
              titleEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
            };
            productVariants?: Array<
              {
                id?: string;
                legacyId?: string;
                description?: string;
                reservedCount?: number;
                cartCount?: number;
                availableCount?: number;
                fulfillmentCount?: number;
                incompleteCount?: number;
                pendingDeliveryCount?: number;
                activeFrom?: Date;
                activeUntil?: Date;
                stockCount?: number;
                minimumStockCount?: number;
                weight?: number;
                ean?: string;
                private?: boolean;
                order?: number;
                priceInFuture?: boolean;
                active?: boolean;
                sku?: string;
                prices?: Array<
                  {
                    id?: string;
                    shopId?: string;
                    activeFrom?: Date;
                    activeUntil?: Date;
                    amount?: number;
                    countryCode?: string;
                    comment?: string;
                    /** Price this one is replacing (ex: temporary sales, special holidays price). */
                    originalPrice?: number;
                    active?: boolean;
                  } & {
                    /** A file asset */
                    overlayListingImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                    /** A file asset */
                    overlayDetailImage?: {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      url: string;
                      originalUrl?: string;
                      tags: string[];
                      alt?: string;
                      altDe?: string;
                      altEn?: string;
                      /** Object of sizes mapping to urls */
                      thumbs?: {
                        10: string;
                        75: string;
                        100: string;
                        150: string;
                        180: string;
                        240: string;
                        360: string;
                        370: string;
                        500: string;
                        740: string;
                        1000: string;
                        1500: string;
                      };
                    };
                  }
                >;
                taxRate?: number;
                taxes?: Array<{
                  id?: string;
                  countryCode?: string;
                  amount?: number;
                }>;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            images?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              }
            >;
            recordType?: string;
            recordDistributor?: string;
            recordLabel?: string;
            recordReleasedAt?: Date;
            /** An override for the price of a product only used in the charts export. */
            recordChartsPrice?: number;
            dhlShippingFrom?: Date;
            customsTariffNumber?: string;
            combinedProductIds?: string[][];
            meta?: {};
            /** Exclude the product from feeds (ex: Facebook, Google). */
            excludeFromFeeds?: boolean;
            /** The Google Shopping category for this product. */
            googleFeedCategory?: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            ownerships?: Array<
              {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                role?: string;
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
            /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
            taxesPerCountry?: {};
            shippingRequirements?: string[];
            prohibitedShippingTags?: string[];
            freeShippingMode?: string;
          }
        >;
        companies?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        contractAgreements?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            priority?: number;
            createdAt?: Date;
            invalidatedAt?: Date;
            status?: string;
          } & {
            shops?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The actual name of this shop, displayable to the customer. */
                name?: string;
                /** Default locale (language & formats) of this shop. */
                defaultLocale?: string;
                /** Is the 'waiting queue' feature enabled for this shop? */
                waitingQueue?: boolean;
                /** Is this shop listed in the sitemap? */
                sitemap?: boolean;
                /** Extra information for this shop. */
                meta?: {};
                domain?: string;
                orderMergingAllowed?: boolean;
                paymentBlacklist?: string[];
                /** A Base64 reprensentation of the image. */
                logo?: FileRepresenting | string;
                /** Minimum amount to get free shipping */
                freeShippingThreshold?: number;
              }
            >;
            contractAgreementLines?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                priority?: number;
                createdAt?: Date;
                invalidatedAt?: Date;
                status?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/contracts/${params.contractId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['name', 'archived'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /contracts/{contract-id} */
  public async destroyContract(params: {
    /** Identifier (UUID). */
    contractId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/contracts/${params.contractId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /contracts/{contract-id}/contract_agreements */
  public async createContractAgreement(params: {
    /** Identifier (UUID). */
    contractId: string;
    name?: string | null;
    priority?: number | null;
    shopIds?: string[] | null;
    contractAgreementLines?: Array<{
      companyId?: string | null;
      contractType?: string | null;
      amount?: number | null;
      postTax?: boolean | null;
      fee?: boolean | null;
      vatOverride?: number | null;
      description?: string | null;
    }> | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name?: string;
        priority?: number;
        createdAt?: Date;
        invalidatedAt?: Date;
        status?: string;
      } & {
        shops?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The actual name of this shop, displayable to the customer. */
            name?: string;
            /** Default locale (language & formats) of this shop. */
            defaultLocale?: string;
            /** Is the 'waiting queue' feature enabled for this shop? */
            waitingQueue?: boolean;
            /** Is this shop listed in the sitemap? */
            sitemap?: boolean;
            /** Extra information for this shop. */
            meta?: {};
            domain?: string;
            orderMergingAllowed?: boolean;
            paymentBlacklist?: string[];
            /** A Base64 reprensentation of the image. */
            logo?: FileRepresenting | string;
            /** Minimum amount to get free shipping */
            freeShippingThreshold?: number;
          }
        >;
        contractAgreementLines?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            priority?: number;
            createdAt?: Date;
            invalidatedAt?: Date;
            status?: string;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/contracts/${params.contractId}/contract_agreements`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['name', 'priority', 'shopIds', 'contractAgreementLines'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /countries */
  public async indexCountries(): Promise<
    EntityResponse<
      Array<{
        /** Localized name of the country. */
        name: string;
        /** The ISO-3166 "2 letters" code for the country. */
        countryCode: string;
        /** Is the country a part of the EU? */
        eu: boolean;
      }>
    >
  > {
    const headers = {};
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/countries`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /deliveries/{delivery-id} */
  public async showDelivery(params: {
    /** The identifier of the delivery. */
    deliveryId: string;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      /** A unique identifier in the UUID format. */
      picklistId?: string;
      reference?: string;
      status?: string;
      deliveryNoteType?: string;
      sentAt?: Date;
      fulfillmentAt?: Date;
      trackingCode?: string;
      /** Is generated from the shipping product and the tracking_code */
      trackingUrl?: string;
      outgoing?: boolean;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      documentUrl?: string;
      dhlLabelsUrl?: string;
      shippingMethodId?: string;
      requiresInvoiceDownload?: boolean;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/deliveries/${params.deliveryId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /deliveries/{delivery-id} */
  public async updateDelivery(params: {
    /** The identifier of the delivery. */
    deliveryId: string;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    trackingCode?: string | null;
    deliveryNoteType?: string | null;
    shippingMethodId?: string | null;
    status?: string | null;
    skipEmailDelivery?: boolean | null;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      /** A unique identifier in the UUID format. */
      picklistId?: string;
      reference?: string;
      status?: string;
      deliveryNoteType?: string;
      sentAt?: Date;
      fulfillmentAt?: Date;
      trackingCode?: string;
      /** Is generated from the shipping product and the tracking_code */
      trackingUrl?: string;
      outgoing?: boolean;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      documentUrl?: string;
      dhlLabelsUrl?: string;
      shippingMethodId?: string;
      requiresInvoiceDownload?: boolean;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/deliveries/${params.deliveryId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'address',
          'trackingCode',
          'deliveryNoteType',
          'shippingMethodId',
          'status',
          'skipEmailDelivery',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /deliveries/{delivery-id} */
  public async destroyDelivery(params: {
    /** The identifier of the delivery. */
    deliveryId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/deliveries/${params.deliveryId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /events */
  public async indexEvents(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the events by. */
    sortBy?: string | null;
    /** List only orders with these statuses. */
    status?: string[] | null;
    /** List only events starting on or after this date. Only considered when `happening_until` is also provided. */
    happeningFrom?: Date | null;
    /** List only events starting on or after this date. Only considered when `happening_from` is also provided. */
    happeningUntil?: Date | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** Representation in the old database. */
          legacyId?: string;
          /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
          name?: string;
          /** Ticket override for the name attribute */
          nameOverride?: string;
          /** aka ticket line 1 */
          artist?: string;
          /** Ticket override for the artist attribute */
          artistOverride?: string;
          /** aka ticket line 3 */
          ticketSupportLine?: string;
          /** Ticket override for the ticket_support_line attribute */
          ticketSupportLineOverride?: string;
          /** aka ticket line 4 */
          ticketHeadline?: string;
          /** Ticket override for the ticket_headline attribute */
          ticketHeadlineOverride?: string;
          ticketLayout?: string;
          onlineTicketLayout?: string;
          localTicketLayout?: string;
          externalReference?: string;
          /** This represents a box of content with a title and a description. */
          additionalInformation?: {
            title?: string;
            titleDe?: string;
            titleEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
          };
          /** Ticket override for the additional_information attribute */
          additionalInformationOverride?: string;
          /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
          immediatePaymentAtStockQuota?: number;
          /** The status of this event */
          status?: string;
          /** Date at which the event starts */
          happeningFrom?: Date;
          /** Date at which the event stops */
          happeningUntil?: Date;
          sku?: string;
          tourOrganizer?: string;
          localOrganizer?: string;
          /** Indicates when this event should be published */
          publishedFrom?: Date;
          /** Indicates when this event should be unpublished */
          publishedUntil?: Date;
          ageLimit?: number;
          personalizationRequirement?: string;
          soldOutMessage?: string;
          /** At which time the doors to the venue open (in the time zone of the venue) */
          doorTime?: string;
          /** At which time the show begins (in the time zone of the venue) */
          stageTime?: string;
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
          /** Link to download a pdf report of the venue layout with the current seat status applied. */
          allocationPdfDownloadUrl?: string;
          /** Link to download a pdf report of the event sales so far. */
          salesReportPdfDownloadUrl?: string;
          description?: string;
          descriptionEn?: string;
          descriptionDe?: string;
          shippableFrom?: Date;
          dhlShippingFrom?: Date;
          publicShippableFrom?: Date;
          customsTariffNumber?: string;
          combinedProductIds?: string[][];
          meta?: {};
          /** Exclude the product from feeds (ex: Facebook, Google). */
          excludeFromFeeds?: boolean;
          publicTransportDescription?: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          owningCompany?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
          ownerships?: Array<
            {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              role?: string;
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
          venue?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            venueLayouts?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                capacity?: number;
                /** A link to a pdf containing a detailed seatmap. */
                detailPdfDownloadUrl?: string;
                /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
                readyForEvents?: boolean;
                /** Indicates the latest status of the import. If null, it was not imported. */
                importStatus?: string;
                archived?: boolean;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                pricingCategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    name?: string;
                  }
                >;
              }
            >;
          };
          logos: Array<
            {
              tags?: string[];
            } & {
              /** A file asset */
              image: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          venueLayout?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          };
          categories?: Array<{
            order?: number;
            category: {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            };
            /** The URL of the product in the shop of the described category. */
            urlInShop: string;
          }>;
          /** Counts of different grouping of sales. */
          salesStats: {
            available: number;
            returned?: number;
            blocked: number;
            reserved: number;
            cart: number;
            /** Number of sold tickets. */
            sold: number;
            /** Number of sold tickets that are hard tickets (paper). */
            soldHard: number;
            /** Number of sold ticket that are online ticket. */
            soldOnline: number;
            /** Number of tickets that have a personalization set. */
            personalized: number;
          };
          /** A file asset */
          image?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          tourImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketHeaderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          productVariants: Array<{
            id?: string;
            sku?: string;
            description: string;
            basePrice: number;
            presaleFee?: number;
            presaleFeePercentage?: number;
            systemFee: number;
            refundDeduction?: number;
            physical: boolean;
            taxRate: number;
            activeFrom?: Date;
            activeUntil?: Date;
            /** Indicates if this variant should be excluded from public facing endpoints */
            private?: boolean;
            /** Indicates if this variant should be for local pricing instead of system pricing */
            local?: boolean;
            priceParts?: Array<{
              description: string;
              price: number;
            }>;
          }>;
          clearingOrderId?: string;
          checkInStartAt?: Date;
          checkInEndAt?: Date;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'status', 'happeningFrom', 'happeningUntil'])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexEvents', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /events */
  public async createEvent(params: {
    legacyId?: string | null;
    name?: string | null;
    nameOverride?: string | null;
    artist?: string | null;
    artistOverride?: string | null;
    ticketSupportLine?: string | null;
    ticketSupportLineOverride?: string | null;
    ticketHeadline?: string | null;
    ticketHeadlineOverride?: string | null;
    ticketLayout?: string | null;
    onlineTicketLayout?: string | null;
    localTicketLayout?: string | null;
    externalReference?: string | null;
    additionalInformation?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    additionalInformationOverride?: string | null;
    immediatePaymentAtStockQuota?: number | null;
    status?: string | null;
    happeningFrom?: Date | null;
    happeningUntil?: Date | null;
    sku?: string | null;
    tourOrganizer?: string | null;
    localOrganizer?: string | null;
    publishedFrom?: Date;
    publishedUntil?: Date | null;
    ageLimit?: number | null;
    personalizationRequirement?: string | null;
    soldOutMessage?: string | null;
    doorTime?: string | null;
    stageTime?: string | null;
    cartLimit?: number | null;
    allocationPdfDownloadUrl?: string | null;
    salesReportPdfDownloadUrl?: string | null;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionDe?: string | null;
    shippableFrom?: Date | null;
    dhlShippingFrom?: Date | null;
    publicShippableFrom?: Date | null;
    customsTariffNumber?: string | null;
    combinedProductIds?: string[][] | null;
    meta?: {} | null;
    excludeFromFeeds?: boolean | null;
    publicTransportDescription?: string | null;
    slug?: string | null;
    venueLayoutId?: string | null;
    launchSaleAt?: Date | null;
    logoIds?: string[] | null;
    blockAllSeats?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** Representation in the old database. */
        legacyId?: string;
        /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
        name?: string;
        /** Ticket override for the name attribute */
        nameOverride?: string;
        /** aka ticket line 1 */
        artist?: string;
        /** Ticket override for the artist attribute */
        artistOverride?: string;
        /** aka ticket line 3 */
        ticketSupportLine?: string;
        /** Ticket override for the ticket_support_line attribute */
        ticketSupportLineOverride?: string;
        /** aka ticket line 4 */
        ticketHeadline?: string;
        /** Ticket override for the ticket_headline attribute */
        ticketHeadlineOverride?: string;
        ticketLayout?: string;
        onlineTicketLayout?: string;
        localTicketLayout?: string;
        externalReference?: string;
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** Ticket override for the additional_information attribute */
        additionalInformationOverride?: string;
        /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
        immediatePaymentAtStockQuota?: number;
        /** The status of this event */
        status?: string;
        /** Date at which the event starts */
        happeningFrom?: Date;
        /** Date at which the event stops */
        happeningUntil?: Date;
        sku?: string;
        tourOrganizer?: string;
        localOrganizer?: string;
        /** Indicates when this event should be published */
        publishedFrom?: Date;
        /** Indicates when this event should be unpublished */
        publishedUntil?: Date;
        ageLimit?: number;
        personalizationRequirement?: string;
        soldOutMessage?: string;
        /** At which time the doors to the venue open (in the time zone of the venue) */
        doorTime?: string;
        /** At which time the show begins (in the time zone of the venue) */
        stageTime?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        /** Link to download a pdf report of the venue layout with the current seat status applied. */
        allocationPdfDownloadUrl?: string;
        /** Link to download a pdf report of the event sales so far. */
        salesReportPdfDownloadUrl?: string;
        description?: string;
        descriptionEn?: string;
        descriptionDe?: string;
        shippableFrom?: Date;
        dhlShippingFrom?: Date;
        publicShippableFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        publicTransportDescription?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        venue?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          venueLayouts?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
              capacity?: number;
              /** A link to a pdf containing a detailed seatmap. */
              detailPdfDownloadUrl?: string;
              /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
              readyForEvents?: boolean;
              /** Indicates the latest status of the import. If null, it was not imported. */
              importStatus?: string;
              archived?: boolean;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              pricingCategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  name?: string;
                }
              >;
            }
          >;
        };
        logos: Array<
          {
            tags?: string[];
          } & {
            /** A file asset */
            image: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        venueLayout?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
          capacity?: number;
          /** A link to a pdf containing a detailed seatmap. */
          detailPdfDownloadUrl?: string;
          /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
          readyForEvents?: boolean;
          /** Indicates the latest status of the import. If null, it was not imported. */
          importStatus?: string;
          archived?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          pricingCategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
            }
          >;
        };
        categories?: Array<{
          order?: number;
          category: {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
          /** The URL of the product in the shop of the described category. */
          urlInShop: string;
        }>;
        /** Counts of different grouping of sales. */
        salesStats: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        tourImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        ticketHeaderImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        productVariants: Array<{
          id?: string;
          sku?: string;
          description: string;
          basePrice: number;
          presaleFee?: number;
          presaleFeePercentage?: number;
          systemFee: number;
          refundDeduction?: number;
          physical: boolean;
          taxRate: number;
          activeFrom?: Date;
          activeUntil?: Date;
          /** Indicates if this variant should be excluded from public facing endpoints */
          private?: boolean;
          /** Indicates if this variant should be for local pricing instead of system pricing */
          local?: boolean;
          priceParts?: Array<{
            description: string;
            price: number;
          }>;
        }>;
        clearingOrderId?: string;
        checkInStartAt?: Date;
        checkInEndAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/events`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'name',
          'nameOverride',
          'artist',
          'artistOverride',
          'ticketSupportLine',
          'ticketSupportLineOverride',
          'ticketHeadline',
          'ticketHeadlineOverride',
          'ticketLayout',
          'onlineTicketLayout',
          'localTicketLayout',
          'externalReference',
          'additionalInformation',
          'additionalInformationOverride',
          'immediatePaymentAtStockQuota',
          'status',
          'happeningFrom',
          'happeningUntil',
          'sku',
          'tourOrganizer',
          'localOrganizer',
          'publishedFrom',
          'publishedUntil',
          'ageLimit',
          'personalizationRequirement',
          'soldOutMessage',
          'doorTime',
          'stageTime',
          'cartLimit',
          'allocationPdfDownloadUrl',
          'salesReportPdfDownloadUrl',
          'description',
          'descriptionEn',
          'descriptionDe',
          'shippableFrom',
          'dhlShippingFrom',
          'publicShippableFrom',
          'customsTariffNumber',
          'combinedProductIds',
          'meta',
          'excludeFromFeeds',
          'publicTransportDescription',
          'slug',
          'venueLayoutId',
          'launchSaleAt',
          'logoIds',
          'blockAllSeats',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id} */
  public async showEvent(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** Representation in the old database. */
        legacyId?: string;
        /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
        name?: string;
        /** Ticket override for the name attribute */
        nameOverride?: string;
        /** aka ticket line 1 */
        artist?: string;
        /** Ticket override for the artist attribute */
        artistOverride?: string;
        /** aka ticket line 3 */
        ticketSupportLine?: string;
        /** Ticket override for the ticket_support_line attribute */
        ticketSupportLineOverride?: string;
        /** aka ticket line 4 */
        ticketHeadline?: string;
        /** Ticket override for the ticket_headline attribute */
        ticketHeadlineOverride?: string;
        ticketLayout?: string;
        onlineTicketLayout?: string;
        localTicketLayout?: string;
        externalReference?: string;
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** Ticket override for the additional_information attribute */
        additionalInformationOverride?: string;
        /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
        immediatePaymentAtStockQuota?: number;
        /** The status of this event */
        status?: string;
        /** Date at which the event starts */
        happeningFrom?: Date;
        /** Date at which the event stops */
        happeningUntil?: Date;
        sku?: string;
        tourOrganizer?: string;
        localOrganizer?: string;
        /** Indicates when this event should be published */
        publishedFrom?: Date;
        /** Indicates when this event should be unpublished */
        publishedUntil?: Date;
        ageLimit?: number;
        personalizationRequirement?: string;
        soldOutMessage?: string;
        /** At which time the doors to the venue open (in the time zone of the venue) */
        doorTime?: string;
        /** At which time the show begins (in the time zone of the venue) */
        stageTime?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        /** Link to download a pdf report of the venue layout with the current seat status applied. */
        allocationPdfDownloadUrl?: string;
        /** Link to download a pdf report of the event sales so far. */
        salesReportPdfDownloadUrl?: string;
        description?: string;
        descriptionEn?: string;
        descriptionDe?: string;
        shippableFrom?: Date;
        dhlShippingFrom?: Date;
        publicShippableFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        publicTransportDescription?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        venue?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          venueLayouts?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
              capacity?: number;
              /** A link to a pdf containing a detailed seatmap. */
              detailPdfDownloadUrl?: string;
              /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
              readyForEvents?: boolean;
              /** Indicates the latest status of the import. If null, it was not imported. */
              importStatus?: string;
              archived?: boolean;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              pricingCategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  name?: string;
                }
              >;
            }
          >;
        };
        logos: Array<
          {
            tags?: string[];
          } & {
            /** A file asset */
            image: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        venueLayout?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
          capacity?: number;
          /** A link to a pdf containing a detailed seatmap. */
          detailPdfDownloadUrl?: string;
          /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
          readyForEvents?: boolean;
          /** Indicates the latest status of the import. If null, it was not imported. */
          importStatus?: string;
          archived?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          pricingCategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
            }
          >;
        };
        categories?: Array<{
          order?: number;
          category: {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
          /** The URL of the product in the shop of the described category. */
          urlInShop: string;
        }>;
        /** Counts of different grouping of sales. */
        salesStats: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        tourImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        ticketHeaderImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        productVariants: Array<{
          id?: string;
          sku?: string;
          description: string;
          basePrice: number;
          presaleFee?: number;
          presaleFeePercentage?: number;
          systemFee: number;
          refundDeduction?: number;
          physical: boolean;
          taxRate: number;
          activeFrom?: Date;
          activeUntil?: Date;
          /** Indicates if this variant should be excluded from public facing endpoints */
          private?: boolean;
          /** Indicates if this variant should be for local pricing instead of system pricing */
          local?: boolean;
          priceParts?: Array<{
            description: string;
            price: number;
          }>;
        }>;
        clearingOrderId?: string;
        checkInStartAt?: Date;
        checkInEndAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /events/{event-id} */
  public async updateEvent(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    legacyId?: string | null;
    name?: string | null;
    nameOverride?: string | null;
    artist?: string | null;
    artistOverride?: string | null;
    ticketSupportLine?: string | null;
    ticketSupportLineOverride?: string | null;
    ticketHeadline?: string | null;
    ticketHeadlineOverride?: string | null;
    ticketLayout?: string | null;
    onlineTicketLayout?: string | null;
    localTicketLayout?: string | null;
    externalReference?: string | null;
    additionalInformation?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    additionalInformationOverride?: string | null;
    immediatePaymentAtStockQuota?: number | null;
    status?: string | null;
    happeningFrom?: Date | null;
    happeningUntil?: Date | null;
    sku?: string | null;
    tourOrganizer?: string | null;
    localOrganizer?: string | null;
    publishedFrom?: Date;
    publishedUntil?: Date | null;
    ageLimit?: number | null;
    personalizationRequirement?: string | null;
    soldOutMessage?: string | null;
    doorTime?: string | null;
    stageTime?: string | null;
    cartLimit?: number | null;
    allocationPdfDownloadUrl?: string | null;
    salesReportPdfDownloadUrl?: string | null;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionDe?: string | null;
    shippableFrom?: Date | null;
    dhlShippingFrom?: Date | null;
    publicShippableFrom?: Date | null;
    customsTariffNumber?: string | null;
    combinedProductIds?: string[][] | null;
    meta?: {} | null;
    excludeFromFeeds?: boolean | null;
    publicTransportDescription?: string | null;
    slug?: string | null;
    venueLayoutId?: string | null;
    owningCompanyId?: string | null;
    logoIds?: string[] | null;
    image?: FileRepresenting | string | null;
    ticketHeaderImage?: FileRepresenting | string | null;
    productVariantsActiveFrom?: Date | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** Representation in the old database. */
        legacyId?: string;
        /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
        name?: string;
        /** Ticket override for the name attribute */
        nameOverride?: string;
        /** aka ticket line 1 */
        artist?: string;
        /** Ticket override for the artist attribute */
        artistOverride?: string;
        /** aka ticket line 3 */
        ticketSupportLine?: string;
        /** Ticket override for the ticket_support_line attribute */
        ticketSupportLineOverride?: string;
        /** aka ticket line 4 */
        ticketHeadline?: string;
        /** Ticket override for the ticket_headline attribute */
        ticketHeadlineOverride?: string;
        ticketLayout?: string;
        onlineTicketLayout?: string;
        localTicketLayout?: string;
        externalReference?: string;
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** Ticket override for the additional_information attribute */
        additionalInformationOverride?: string;
        /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
        immediatePaymentAtStockQuota?: number;
        /** The status of this event */
        status?: string;
        /** Date at which the event starts */
        happeningFrom?: Date;
        /** Date at which the event stops */
        happeningUntil?: Date;
        sku?: string;
        tourOrganizer?: string;
        localOrganizer?: string;
        /** Indicates when this event should be published */
        publishedFrom?: Date;
        /** Indicates when this event should be unpublished */
        publishedUntil?: Date;
        ageLimit?: number;
        personalizationRequirement?: string;
        soldOutMessage?: string;
        /** At which time the doors to the venue open (in the time zone of the venue) */
        doorTime?: string;
        /** At which time the show begins (in the time zone of the venue) */
        stageTime?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        /** Link to download a pdf report of the venue layout with the current seat status applied. */
        allocationPdfDownloadUrl?: string;
        /** Link to download a pdf report of the event sales so far. */
        salesReportPdfDownloadUrl?: string;
        description?: string;
        descriptionEn?: string;
        descriptionDe?: string;
        shippableFrom?: Date;
        dhlShippingFrom?: Date;
        publicShippableFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        publicTransportDescription?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        venue?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          venueLayouts?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
              capacity?: number;
              /** A link to a pdf containing a detailed seatmap. */
              detailPdfDownloadUrl?: string;
              /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
              readyForEvents?: boolean;
              /** Indicates the latest status of the import. If null, it was not imported. */
              importStatus?: string;
              archived?: boolean;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              pricingCategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  name?: string;
                }
              >;
            }
          >;
        };
        logos: Array<
          {
            tags?: string[];
          } & {
            /** A file asset */
            image: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        venueLayout?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
          capacity?: number;
          /** A link to a pdf containing a detailed seatmap. */
          detailPdfDownloadUrl?: string;
          /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
          readyForEvents?: boolean;
          /** Indicates the latest status of the import. If null, it was not imported. */
          importStatus?: string;
          archived?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          pricingCategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
            }
          >;
        };
        categories?: Array<{
          order?: number;
          category: {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          };
          /** The URL of the product in the shop of the described category. */
          urlInShop: string;
        }>;
        /** Counts of different grouping of sales. */
        salesStats: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        tourImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        ticketHeaderImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        productVariants: Array<{
          id?: string;
          sku?: string;
          description: string;
          basePrice: number;
          presaleFee?: number;
          presaleFeePercentage?: number;
          systemFee: number;
          refundDeduction?: number;
          physical: boolean;
          taxRate: number;
          activeFrom?: Date;
          activeUntil?: Date;
          /** Indicates if this variant should be excluded from public facing endpoints */
          private?: boolean;
          /** Indicates if this variant should be for local pricing instead of system pricing */
          local?: boolean;
          priceParts?: Array<{
            description: string;
            price: number;
          }>;
        }>;
        clearingOrderId?: string;
        checkInStartAt?: Date;
        checkInEndAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/events/${params.eventId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'name',
          'nameOverride',
          'artist',
          'artistOverride',
          'ticketSupportLine',
          'ticketSupportLineOverride',
          'ticketHeadline',
          'ticketHeadlineOverride',
          'ticketLayout',
          'onlineTicketLayout',
          'localTicketLayout',
          'externalReference',
          'additionalInformation',
          'additionalInformationOverride',
          'immediatePaymentAtStockQuota',
          'status',
          'happeningFrom',
          'happeningUntil',
          'sku',
          'tourOrganizer',
          'localOrganizer',
          'publishedFrom',
          'publishedUntil',
          'ageLimit',
          'personalizationRequirement',
          'soldOutMessage',
          'doorTime',
          'stageTime',
          'cartLimit',
          'allocationPdfDownloadUrl',
          'salesReportPdfDownloadUrl',
          'description',
          'descriptionEn',
          'descriptionDe',
          'shippableFrom',
          'dhlShippingFrom',
          'publicShippableFrom',
          'customsTariffNumber',
          'combinedProductIds',
          'meta',
          'excludeFromFeeds',
          'publicTransportDescription',
          'slug',
          'venueLayoutId',
          'owningCompanyId',
          'logoIds',
          'image',
          'ticketHeaderImage',
          'productVariantsActiveFrom',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /events/{event-id} */
  public async destroyEvent(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/events/${params.eventId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /events/{event-id}/mass_refund */
  public async triggerMassRefund(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
  }): Promise<
    EntityResponse<{
      /** Number of messages enqueued for mail delivery */
      emailsSent?: number;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/events/${params.eventId}/mass_refund`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id}/pricing_categories */
  public async indexPricingCategories(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    /** retrieve local or system categories */
    local?: boolean | null;
  }): Promise<
    EntityResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
        } & {
          productVariants: Array<
            {
              id?: string;
              sku?: string;
              description: string;
              basePrice: number;
              presaleFee?: number;
              presaleFeePercentage?: number;
              systemFee: number;
              refundDeduction?: number;
              physical: boolean;
              taxRate: number;
              activeFrom?: Date;
              activeUntil?: Date;
              /** Indicates if this variant should be excluded from public facing endpoints */
              private?: boolean;
              /** Indicates if this variant should be for local pricing instead of system pricing */
              local?: boolean;
              priceParts?: Array<{
                description: string;
                price: number;
              }>;
            } & {
              /** If this is set to true. This variant will be deleted. */
              delete?: boolean;
            }
          >;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}/pricing_categories`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['local'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /events/{event-id}/pricing_categories/{pricing-category-id} */
  public async updatePricingCategory(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    /** Identifier (UUID). */
    pricingCategoryId: string;
    name?: string | null;
    productVariants?: Array<
      {
        id?: string;
        sku?: string;
        description: string;
        basePrice: number;
        presaleFee?: number;
        presaleFeePercentage?: number;
        systemFee: number;
        refundDeduction?: number;
        physical: boolean;
        taxRate: number;
        activeFrom?: Date;
        activeUntil?: Date;
        /** Indicates if this variant should be excluded from public facing endpoints */
        private?: boolean;
        /** Indicates if this variant should be for local pricing instead of system pricing */
        local?: boolean;
        priceParts?: Array<{
          description: string;
          price: number;
        }>;
      } & {
        /** If this is set to true. This variant will be deleted. */
        delete?: boolean;
      }
    > | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name?: string;
      } & {
        productVariants: Array<
          {
            id?: string;
            sku?: string;
            description: string;
            basePrice: number;
            presaleFee?: number;
            presaleFeePercentage?: number;
            systemFee: number;
            refundDeduction?: number;
            physical: boolean;
            taxRate: number;
            activeFrom?: Date;
            activeUntil?: Date;
            /** Indicates if this variant should be excluded from public facing endpoints */
            private?: boolean;
            /** Indicates if this variant should be for local pricing instead of system pricing */
            local?: boolean;
            priceParts?: Array<{
              description: string;
              price: number;
            }>;
          } & {
            /** If this is set to true. This variant will be deleted. */
            delete?: boolean;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/events/${params.eventId}/pricing_categories/${params.pricingCategoryId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['name', 'productVariants'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id}/sales_stats */
  public async showEventSalesStats(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
  }): Promise<
    EntityResponse<{
      available: number;
      returned?: number;
      blocked: number;
      reserved: number;
      cart: number;
      /** Number of sold tickets. */
      sold: number;
      /** Number of sold tickets that are hard tickets (paper). */
      soldHard: number;
      /** Number of sold ticket that are online ticket. */
      soldOnline: number;
      /** Number of tickets that have a personalization set. */
      personalized: number;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}/sales_stats`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id}/seat_statuses */
  public async indexSeatStatuses(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
  }): Promise<
    EntityResponse<
      Array<{
        /** The ID of the item representing this seat for this event. */
        id: string;
        /** The ID of the seat (seatmap). */
        seatId: string;
        status: string;
        /** If the seat is blocked, why is that? */
        blockReason?: string;
      }>
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}/seat_statuses`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id}/seat_statuses/{item-id} */
  public async showSeatStatus(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    /** The identifier of the shop item. */
    itemId: string;
  }): Promise<
    EntityResponse<
      {
        /** The ID of the item representing this seat for this event. */
        id: string;
        /** The ID of the seat (seatmap). */
        seatId: string;
        status: string;
        /** If the seat is blocked, why is that? */
        blockReason?: string;
      } & {
        orderId?: string;
        orderReference?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}/seat_statuses/${params.itemId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /events/{event-id}/seat_statuses/{item-id} */
  public async updateSeatStatus(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    /** The identifier of the shop item. */
    itemId: string;
    blockReason?: string | null;
  }): Promise<
    EntityResponse<
      {
        /** The ID of the item representing this seat for this event. */
        id: string;
        /** The ID of the seat (seatmap). */
        seatId: string;
        status: string;
        /** If the seat is blocked, why is that? */
        blockReason?: string;
      } & {
        orderId?: string;
        orderReference?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/events/${params.eventId}/seat_statuses/${params.itemId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['blockReason'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /events/{event-id}/ticket_preview */
  public async showTicketPreview(params: {
    /** Slug or Identifier (UUID). */
    eventId: string;
    /** The ID of the product variant */
    productVariantId?: string | null;
    /** The ID of the venue section */
    venueSectionId?: string | null;
  }): Promise<
    EntityResponse<{
      /** base64 encoded PDF content of the preview ticket */
      preview: string;
      /** The venue section ID */
      venueSectionID?: string;
      /** The product variant ID */
      productVariantID?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/events/${params.eventId}/ticket_preview`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['productVariantId', 'venueSectionId'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /general_products/{product-id} */
  public async showGeneralProduct(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** Base type of a product. Not to be confused with a product's `product_type`, which is free text.  Note: In this case, `product` means Merchandise Product. */
        type?: string;
        name?: string;
        nameDe?: string;
        nameEn?: string;
        sku?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/general_products/${params.productId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /images/{image-id} */
  public async updateImage(params: {
    /** The identifier of the product variant. */
    imageId: string;
    tags?: string[] | null;
    order?: number | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        url: string;
        originalUrl?: string;
        tags: string[];
        alt?: string;
        altDe?: string;
        altEn?: string;
        /** Object of sizes mapping to urls */
        thumbs?: {
          10: string;
          75: string;
          100: string;
          150: string;
          180: string;
          240: string;
          360: string;
          370: string;
          500: string;
          740: string;
          1000: string;
          1500: string;
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/images/${params.imageId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['tags', 'order'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /images/{image-id} */
  public async destroyImage(params: {
    /** The identifier of the product variant. */
    imageId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/images/${params.imageId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /invoices/{invoice-id} */
  public async showInvoice(params: {
    /** The identifier of the invoice. */
    invoiceId: string;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      status?: string;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      total?: number;
      reference?: string;
      bookingNumber?: number;
      formattedBookingNumber?: string;
      documentUrl?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/invoices/${params.invoiceId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /invoices/{invoice-id} */
  public async updateInvoice(params: {
    /** The identifier of the invoice. */
    invoiceId: string;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    status?: string | null;
    skipEmailDelivery?: boolean | null;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      status?: string;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      total?: number;
      reference?: string;
      bookingNumber?: number;
      formattedBookingNumber?: string;
      documentUrl?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/invoices/${params.invoiceId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['address', 'status', 'skipEmailDelivery'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /invoices/{invoice-id} */
  public async destroyInvoice(params: {
    /** The identifier of the invoice. */
    invoiceId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/invoices/${params.invoiceId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /logos */
  public async indexLogos(): Promise<
    EntityResponse<
      Array<
        {
          tags?: string[];
        } & {
          /** A file asset */
          image: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/logos`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /logos */
  public async createLogo(params: { image?: FileRepresenting | string | null; tags?: string[] | null }): Promise<
    EntityResponse<
      Array<
        {
          tags?: string[];
        } & {
          /** A file asset */
          image: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/logos`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['image', 'tags'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /memberships */
  public async createMembership(params: {
    role?: string | null;
    comment?: string | null;
    permissions?: string[] | null;
    invitedEmail?: string | null;
    userId?: string | null;
    companyId?: string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        role?: string;
        comment?: string;
        permissions?: string[];
        /** On invites, the email of the invitee is displayed here (and the user is then null). */
        invitedEmail?: string;
        createdAt?: Date;
        /** On invites, indicates when it expires and becomes invalid. */
        expiresAt?: Date;
        /** The .ics URL to the membership's calendar. */
        calendarFeedUrl?: string;
      } & {
        indexPermissions?: string[];
      } & {
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/memberships`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['role', 'comment', 'permissions', 'invitedEmail', 'userId', 'companyId'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /memberships/{membership-id} */
  public async updateMembership(params: {
    /** Identifier (UUID). */
    membershipId: string;
    role?: string | null;
    comment?: string | null;
    permissions?: string[] | null;
    invitedEmail?: string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        role?: string;
        comment?: string;
        permissions?: string[];
        /** On invites, the email of the invitee is displayed here (and the user is then null). */
        invitedEmail?: string;
        createdAt?: Date;
        /** On invites, indicates when it expires and becomes invalid. */
        expiresAt?: Date;
        /** The .ics URL to the membership's calendar. */
        calendarFeedUrl?: string;
      } & {
        indexPermissions?: string[];
      } & {
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/memberships/${params.membershipId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['role', 'comment', 'permissions', 'invitedEmail'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /memberships/{membership-id} */
  public async destroyMembership(params: {
    /** Identifier (UUID). */
    membershipId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/memberships/${params.membershipId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /news */
  public async indexNews(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Identifier (UUID). */
    shopId?: string | null;
    /** Slug or Identifier (UUID). */
    categorySlug?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** The ID of the shop under which the news is published. */
          shopId?: string;
          /** The news title (e.g. for a button/link). */
          title?: string;
          titleEn?: string;
          titleDe?: string;
          /** A long text representing the content of the news. */
          text?: string;
          textEn?: string;
          textDe?: string;
          /** The category of the news. */
          category?: string;
          /** A file asset */
          image?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** The order of the news relative to it's sibling news. */
          order?: number;
          /** News can have a URL potentially pointing to a location with further content. */
          url?: string;
          inverse?: boolean;
          /** The time of the creation of the news. */
          createdAt?: Date;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/news`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'shopId', 'categorySlug'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexNews', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /news */
  public async createNews(params: {
    shopId?: string | null;
    titleEn?: string | null;
    titleDe?: string | null;
    textEn?: string | null;
    textDe?: string | null;
    category?: string | null;
    image?: FileRepresenting | string | null;
    order?: number | null;
    url?: string | null;
    inverse?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** The ID of the shop under which the news is published. */
        shopId?: string;
        /** The news title (e.g. for a button/link). */
        title?: string;
        titleEn?: string;
        titleDe?: string;
        /** A long text representing the content of the news. */
        text?: string;
        textEn?: string;
        textDe?: string;
        /** The category of the news. */
        category?: string;
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** The order of the news relative to it's sibling news. */
        order?: number;
        /** News can have a URL potentially pointing to a location with further content. */
        url?: string;
        inverse?: boolean;
        /** The time of the creation of the news. */
        createdAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/news`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'shopId',
          'titleEn',
          'titleDe',
          'textEn',
          'textDe',
          'category',
          'image',
          'order',
          'url',
          'inverse',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /news/{news-id} */
  public async showNews(params: {
    /** The identifier of the news. */
    newsId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** The ID of the shop under which the news is published. */
        shopId?: string;
        /** The news title (e.g. for a button/link). */
        title?: string;
        titleEn?: string;
        titleDe?: string;
        /** A long text representing the content of the news. */
        text?: string;
        textEn?: string;
        textDe?: string;
        /** The category of the news. */
        category?: string;
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** The order of the news relative to it's sibling news. */
        order?: number;
        /** News can have a URL potentially pointing to a location with further content. */
        url?: string;
        inverse?: boolean;
        /** The time of the creation of the news. */
        createdAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/news/${params.newsId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /news/{news-id} */
  public async updateNews(params: {
    /** The identifier of the news. */
    newsId: string;
    shopId?: string | null;
    titleEn?: string | null;
    titleDe?: string | null;
    textEn?: string | null;
    textDe?: string | null;
    category?: string | null;
    image?: FileRepresenting | string | null;
    order?: number | null;
    url?: string | null;
    inverse?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** The ID of the shop under which the news is published. */
        shopId?: string;
        /** The news title (e.g. for a button/link). */
        title?: string;
        titleEn?: string;
        titleDe?: string;
        /** A long text representing the content of the news. */
        text?: string;
        textEn?: string;
        textDe?: string;
        /** The category of the news. */
        category?: string;
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** The order of the news relative to it's sibling news. */
        order?: number;
        /** News can have a URL potentially pointing to a location with further content. */
        url?: string;
        inverse?: boolean;
        /** The time of the creation of the news. */
        createdAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/news/${params.newsId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'shopId',
          'titleEn',
          'titleDe',
          'textEn',
          'textDe',
          'category',
          'image',
          'order',
          'url',
          'inverse',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /news/{news-id} */
  public async destroyNews(params: {
    /** The identifier of the news. */
    newsId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/news/${params.newsId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /order_auto_cancellation */
  public async showOrderAutoCancellation(): Promise<
    EntityResponse<{
      /** Indicates wheather auto cancellation for orders is disabled */
      disabled?: boolean;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/order_auto_cancellation`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /order_auto_cancellation */
  public async updateOrderAutoCancellation(params: { disabled?: boolean | null }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'patch',
      url: `/order_auto_cancellation`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['disabled'])),
    });
  }

  /** GET /orders */
  public async indexOrders(params: {
    /** List of sub-resources path to include. */
    include?: string[] | null;
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the orders by. */
    sortBy?: string | null;
    /** List only orders with these statuses. */
    status?: string[] | null;
    /** Only return clearing orders for this company */
    billingCompanyId?: string | null;
    /** List only orders that have one of their delivery with these statuses. */
    deliveryStatus?: string[] | null;
    /** List only orders that have one of those as its preferred payment method or has one payment using that method. */
    paymentMethod?: string[] | null;
    /** List only orders with items in those storages. */
    storage?: string[] | null;
    /** List only orders created on or after this date. Only considered when `end_date` is also provided. */
    startDate?: Date | null;
    /** List only orders created on or after this date. Only considered when `start_date` is also provided. */
    endDate?: Date | null;
    /** If true, returns only orders with products that are shippable */
    withShippableProductsOnly?: boolean | null;
    /** If true, archived orders are included. They are excluded by default, unless `status` explicitly contains `archived`. */
    archived?: boolean | null;
    /** List only the orders containing this product. Can be its ID or its SKU. */
    productId?: string | null;
    /** List only the orders containing this product variant. Can be its ID or its SKU. */
    productVariantId?: string | null;
    /** List only the orders containing tickets in this venue section. Use `product_id` to be specific to one event. */
    venueSectionId?: string | null;
    /** List only the orders containing items in this status. */
    itemStatus?: string | null;
    /** List only the orders containing items with specified ticket type. */
    containingOnly?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          type?: string;
          status: string;
          problem?: boolean;
          deliveries: Array<{
            /** A unique identifier in the UUID format. */
            id?: string;
            /** A unique identifier in the UUID format. */
            orderId?: string;
            /** A unique identifier in the UUID format. */
            picklistId?: string;
            reference?: string;
            status?: string;
            deliveryNoteType?: string;
            sentAt?: Date;
            fulfillmentAt?: Date;
            trackingCode?: string;
            /** Is generated from the shipping product and the tracking_code */
            trackingUrl?: string;
            outgoing?: boolean;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            documentUrl?: string;
            dhlLabelsUrl?: string;
            shippingMethodId?: string;
            requiresInvoiceDownload?: boolean;
          }>;
          payments: Array<
            {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              /** A unique identifier in the UUID format. */
              id?: string;
              status?: string;
              amount?: number;
              createdAt?: Date;
              updatedAt?: Date;
              paidAt?: Date;
              reference?: string;
              paymentMethod?: string;
              expiresAt?: Date;
              authorizationProcessable?: boolean;
            }
          >;
          balance?: number;
          reference?: number;
          createdAt?: Date;
          updatedAt?: Date;
          orderedAt?: Date;
          cancelAt?: Date;
          invoices: Array<{
            /** A unique identifier in the UUID format. */
            id?: string;
            /** A unique identifier in the UUID format. */
            orderId?: string;
            status?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            total?: number;
            reference?: string;
            bookingNumber?: number;
            formattedBookingNumber?: string;
            documentUrl?: string;
          }>;
          tags?: string[];
          company?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
          user?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            createdAt?: Date;
            locale?: string;
          } & {
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            memberships?: Array<
              {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
          };
          voucherCode?: {
            id?: string;
            code?: string;
          };
          preferredBillingAddress?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          preferredShippingAddress?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          paymentLink?: string;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/orders`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, [
          'include',
          'page',
          'perPage',
          'term',
          'sortDir',
          'sortBy',
          'status',
          'billingCompanyId',
          'deliveryStatus',
          'paymentMethod',
          'storage',
          'startDate',
          'endDate',
          'withShippableProductsOnly',
          'archived',
          'productId',
          'productVariantId',
          'venueSectionId',
          'itemStatus',
          'containingOnly',
        ])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexOrders', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /orders */
  public async createOrder(params: { userId?: string | null; companyId?: string | null }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['userId', 'companyId'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /orders/{order-id} */
  public async showOrder(params: {
    /** The identifier of the order. */
    orderId: string;
    /** List of sub-resources path to include. */
    include?: string[] | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/orders/${params.orderId}`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['include'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /orders/{order-id} */
  public async updateOrder(params: {
    /** The identifier of the order. */
    orderId: string;
    userId?: string | null;
    comment?: string | null;
    tags?: string[] | null;
    problem?: boolean | null;
    cancelAt?: Date | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/orders/${params.orderId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['userId', 'comment', 'tags', 'problem', 'cancelAt'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /orders/{order-id} */
  public async destroyOrder(params: {
    /** The identifier of the order. */
    orderId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/orders/${params.orderId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /orders/{order-id}/clearings */
  public async createClearing(params: {
    /** The identifier of the order. */
    orderId: string;
    from?: Date | null;
    to?: Date | null;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/clearings`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['from', 'to'])),
    });
  }

  /** POST /orders/{order-id}/deliveries */
  public async createDelivery(params: {
    /** The identifier of the order. */
    orderId: string;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    trackingCode?: string | null;
    deliveryNoteType?: string | null;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      /** A unique identifier in the UUID format. */
      picklistId?: string;
      reference?: string;
      status?: string;
      deliveryNoteType?: string;
      sentAt?: Date;
      fulfillmentAt?: Date;
      trackingCode?: string;
      /** Is generated from the shipping product and the tracking_code */
      trackingUrl?: string;
      outgoing?: boolean;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      documentUrl?: string;
      dhlLabelsUrl?: string;
      shippingMethodId?: string;
      requiresInvoiceDownload?: boolean;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/deliveries`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['address', 'trackingCode', 'deliveryNoteType'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /orders/{order-id}/invoices */
  public async createInvoice(params: {
    /** The identifier of the order. */
    orderId: string;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  }): Promise<
    EntityResponse<{
      /** A unique identifier in the UUID format. */
      id?: string;
      /** A unique identifier in the UUID format. */
      orderId?: string;
      status?: string;
      address?: {
        company?: string;
        firstName?: string;
        lastName?: string;
        address?: string;
        address2?: string;
        postCode?: string;
        city?: string;
        countryCode?: string;
        email?: string;
        phone?: string;
      };
      total?: number;
      reference?: string;
      bookingNumber?: number;
      formattedBookingNumber?: string;
      documentUrl?: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/invoices`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['address'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /orders/{order-id}/items */
  public async createOrderItem(params: {
    /** The identifier of the order. */
    orderId: string;
    requests?: Array<{
      invoiceId?: string | null;
      deliveryId?: string | null;
      incomingDeliveryId?: string | null;
      storageId?: string | null;
      productVariantId: string;
      seatIds?: string[] | null;
      sectionId?: string | null;
      count?: number | null;
      price?: number | null;
      vatPercentage?: number | null;
      personalIdentifier?: string | null;
      pool?: string | null;
    }> | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/items`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['requests'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /orders/{order-id}/items */
  public async destroyManyOrderItems(params: {
    /** The identifier of the order. */
    orderId: string;
    itemIds?: string[] | null;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/orders/${params.orderId}/items`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['itemIds'])),
    });
  }

  /** PATCH /orders/{order-id}/items/{item-id} */
  public async updateOrderItem(params: {
    /** The identifier of the order. */
    orderId: string;
    /** The identifier of the shop item. */
    itemId: string;
    personalIdentifier?: string | null;
    deliveryId?: string | null;
    invoiceId?: string | null;
    price?: number | null;
    vatPercentage?: number | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/orders/${params.orderId}/items/${params.itemId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['personalIdentifier', 'deliveryId', 'invoiceId', 'price', 'vatPercentage'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /orders/{order-id}/items/{item-id} */
  public async destroyOrderItem(params: {
    /** The identifier of the order. */
    orderId: string;
    /** The identifier of the shop item. */
    itemId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/orders/${params.orderId}/items/${params.itemId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /orders/{order-id}/payments */
  public async createPayment(params: {
    /** The identifier of the order. */
    orderId: string;
    amount?: number | null;
    provider?: string | null;
    bankAccount?: {
      name?: string | null;
      iban?: string | null;
      bic?: string | null;
      bankName?: string | null;
    } | null;
    status?: string | null;
    skipEmailDelivery?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A unique identifier in the UUID format. */
        id?: string;
        status?: string;
        amount?: number;
        createdAt?: Date;
        updatedAt?: Date;
        paidAt?: Date;
        reference?: string;
        paymentMethod?: string;
        expiresAt?: Date;
        authorizationProcessable?: boolean;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/payments`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['amount', 'provider', 'bankAccount', 'status', 'skipEmailDelivery'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /orders/{order-id}/recalculate_status */
  public async recalculateStatusOrder(params: {
    /** The identifier of the order. */
    orderId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/recalculate_status`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /orders/{order-id}/refunds */
  public async createRefund(params: {
    /** The identifier of the order. */
    orderId: string;
    itemIds?: string[] | null;
    strategy?: string | null;
    blockReason?: string | null;
    markDeliveryAsSent?: boolean;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        type?: string;
        status: string;
        problem?: boolean;
        deliveries: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          /** A unique identifier in the UUID format. */
          picklistId?: string;
          reference?: string;
          status?: string;
          deliveryNoteType?: string;
          sentAt?: Date;
          fulfillmentAt?: Date;
          trackingCode?: string;
          /** Is generated from the shipping product and the tracking_code */
          trackingUrl?: string;
          outgoing?: boolean;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          documentUrl?: string;
          dhlLabelsUrl?: string;
          shippingMethodId?: string;
          requiresInvoiceDownload?: boolean;
        }>;
        payments: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id?: string;
            status?: string;
            amount?: number;
            createdAt?: Date;
            updatedAt?: Date;
            paidAt?: Date;
            reference?: string;
            paymentMethod?: string;
            expiresAt?: Date;
            authorizationProcessable?: boolean;
          }
        >;
        balance?: number;
        reference?: number;
        createdAt?: Date;
        updatedAt?: Date;
        orderedAt?: Date;
        cancelAt?: Date;
        invoices: Array<{
          /** A unique identifier in the UUID format. */
          id?: string;
          /** A unique identifier in the UUID format. */
          orderId?: string;
          status?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          total?: number;
          reference?: string;
          bookingNumber?: number;
          formattedBookingNumber?: string;
          documentUrl?: string;
        }>;
        tags?: string[];
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        user?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        };
        voucherCode?: {
          id?: string;
          code?: string;
        };
        preferredBillingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        preferredShippingAddress?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
        paymentLink?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/refunds`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['itemIds', 'strategy', 'blockReason', 'markDeliveryAsSent'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /orders/{order-id}/send_refund_email */
  public async sendRefundEmail(params: {
    /** The identifier of the order. */
    orderId: string;
    productId?: string | null;
    email?: string | null;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'post',
      url: `/orders/${params.orderId}/send_refund_email`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['productId', 'email'])),
    });
  }

  /** PATCH /ownerships/{ownership-id} */
  public async updateOwnership(params: {
    /** Identifier (UUID). */
    ownershipId: string;
    role?: string | null;
    companyId?: string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        role?: string;
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        };
        product?:
          | {
              legacyId?: string;
              artist?: string;
              slug?: string;
              artistDe?: string;
              artistEn?: string;
              color?: string;
              colorDe?: string;
              colorEn?: string;
              material?: string;
              materialDe?: string;
              materialEn?: string;
              name?: string;
              nameDe?: string;
              nameEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              costAccountOne?: string;
              costAccountTwo?: string;
              owningCompany?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              };
              owningCompanyId?: string;
              contractId?: string;
              productSafetyDescription?: string;
              productSafetyDescriptionDe?: string;
              productSafetyDescriptionEn?: string;
              productType?: string;
              productTypeDe?: string;
              productTypeEn?: string;
              publishedFrom?: Date;
              publishedUntil?: Date;
              shippableFrom?: Date;
              publicShippableFrom?: Date;
              vendorIds?: string[];
              vendors?: Array<
                {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  memberships?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                      $permissions: string[];
                    } & {
                      role?: string;
                      comment?: string;
                      permissions?: string[];
                      /** On invites, the email of the invitee is displayed here (and the user is then null). */
                      invitedEmail?: string;
                      createdAt?: Date;
                      /** On invites, indicates when it expires and becomes invalid. */
                      expiresAt?: Date;
                      /** The .ics URL to the membership's calendar. */
                      calendarFeedUrl?: string;
                    } & {
                      indexPermissions?: string[];
                    }
                  >;
                  /** A file asset */
                  icon?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  lastBookedClearingInvoice?: {
                    /** A unique identifier in the UUID format. */
                    id?: string;
                    reference?: string;
                    bookedAt?: Date;
                    /** A unique identifier in the UUID format. */
                    orderId?: string;
                  };
                }
              >;
              status?: string;
              /** This represents a box of content with a title and a description. */
              details?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              /** This represents a box of content with a title and a description. */
              additionalInformation?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              productVariants?: Array<
                {
                  id?: string;
                  legacyId?: string;
                  description?: string;
                  reservedCount?: number;
                  cartCount?: number;
                  availableCount?: number;
                  fulfillmentCount?: number;
                  incompleteCount?: number;
                  pendingDeliveryCount?: number;
                  activeFrom?: Date;
                  activeUntil?: Date;
                  stockCount?: number;
                  minimumStockCount?: number;
                  weight?: number;
                  ean?: string;
                  private?: boolean;
                  order?: number;
                  priceInFuture?: boolean;
                  active?: boolean;
                  sku?: string;
                  prices?: Array<
                    {
                      id?: string;
                      shopId?: string;
                      activeFrom?: Date;
                      activeUntil?: Date;
                      amount?: number;
                      countryCode?: string;
                      comment?: string;
                      /** Price this one is replacing (ex: temporary sales, special holidays price). */
                      originalPrice?: number;
                      active?: boolean;
                    } & {
                      /** A file asset */
                      overlayListingImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      overlayDetailImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                  taxRate?: number;
                  taxes?: Array<{
                    id?: string;
                    countryCode?: string;
                    amount?: number;
                  }>;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  /** A file asset */
                  overlayListingImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  overlayDetailImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                }
              >;
              images?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                }
              >;
              recordType?: string;
              recordDistributor?: string;
              recordLabel?: string;
              recordReleasedAt?: Date;
              /** An override for the price of a product only used in the charts export. */
              recordChartsPrice?: number;
              dhlShippingFrom?: Date;
              customsTariffNumber?: string;
              combinedProductIds?: string[][];
              meta?: {};
              /** Exclude the product from feeds (ex: Facebook, Google). */
              excludeFromFeeds?: boolean;
              /** The Google Shopping category for this product. */
              googleFeedCategory?: string;
            }
          | {
              /** Representation in the old database. */
              legacyId?: string;
              /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
              name?: string;
              /** Ticket override for the name attribute */
              nameOverride?: string;
              /** aka ticket line 1 */
              artist?: string;
              /** Ticket override for the artist attribute */
              artistOverride?: string;
              /** aka ticket line 3 */
              ticketSupportLine?: string;
              /** Ticket override for the ticket_support_line attribute */
              ticketSupportLineOverride?: string;
              /** aka ticket line 4 */
              ticketHeadline?: string;
              /** Ticket override for the ticket_headline attribute */
              ticketHeadlineOverride?: string;
              ticketLayout?: string;
              onlineTicketLayout?: string;
              localTicketLayout?: string;
              externalReference?: string;
              /** This represents a box of content with a title and a description. */
              additionalInformation?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              /** Ticket override for the additional_information attribute */
              additionalInformationOverride?: string;
              /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
              immediatePaymentAtStockQuota?: number;
              /** The status of this event */
              status?: string;
              /** Date at which the event starts */
              happeningFrom?: Date;
              /** Date at which the event stops */
              happeningUntil?: Date;
              sku?: string;
              tourOrganizer?: string;
              localOrganizer?: string;
              /** Indicates when this event should be published */
              publishedFrom?: Date;
              /** Indicates when this event should be unpublished */
              publishedUntil?: Date;
              ageLimit?: number;
              personalizationRequirement?: string;
              soldOutMessage?: string;
              /** At which time the doors to the venue open (in the time zone of the venue) */
              doorTime?: string;
              /** At which time the show begins (in the time zone of the venue) */
              stageTime?: string;
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              /** Link to download a pdf report of the venue layout with the current seat status applied. */
              allocationPdfDownloadUrl?: string;
              /** Link to download a pdf report of the event sales so far. */
              salesReportPdfDownloadUrl?: string;
              description?: string;
              descriptionEn?: string;
              descriptionDe?: string;
              shippableFrom?: Date;
              dhlShippingFrom?: Date;
              publicShippableFrom?: Date;
              customsTariffNumber?: string;
              combinedProductIds?: string[][];
              meta?: {};
              /** Exclude the product from feeds (ex: Facebook, Google). */
              excludeFromFeeds?: boolean;
              publicTransportDescription?: string;
            };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/ownerships/${params.ownershipId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['role', 'companyId'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /ownerships/{ownership-id} */
  public async destroyOwnership(params: {
    /** Identifier (UUID). */
    ownershipId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/ownerships/${params.ownershipId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /password_resets */
  public async createPasswordReset(params: { email?: string | null }): Promise<
    EntityResponse<{
      name?: string;
    }>
  > {
    const headers = {};
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/password_resets`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['email'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /password_resets/{reset-token} */
  public async updatePasswordReset(params: {
    /** The reset token obtained from the link in the email. */
    resetToken: string;
    password?: string | null;
  }): Promise<
    EntityResponse<{
      email: string;
    }>
  > {
    const headers = {};
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/password_resets/${params.resetToken}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['password'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /payments/{payment-id} */
  public async updatePayment(params: {
    /** Slug or Identifier (UUID). */
    paymentId: string;
    amount?: number | null;
    paymentMethodId?: string | null;
    status?: string | null;
    bankAccount?: {
      name?: string | null;
      iban?: string | null;
      bic?: string | null;
      bankName?: string | null;
    } | null;
  }): Promise<
    EntityResponse<
      {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A unique identifier in the UUID format. */
        id?: string;
        status?: string;
        amount?: number;
        createdAt?: Date;
        updatedAt?: Date;
        paidAt?: Date;
        reference?: string;
        paymentMethod?: string;
        expiresAt?: Date;
        authorizationProcessable?: boolean;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/payments/${params.paymentId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['amount', 'paymentMethodId', 'status', 'bankAccount'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /payments/{payment-id} */
  public async destroyPayment(params: {
    /** Slug or Identifier (UUID). */
    paymentId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/payments/${params.paymentId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /picklists */
  public async indexPicklists(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the orders by. */
    sortBy?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          id?: string;
          batchSize?: number;
          reference?: string;
          containsPicklist: boolean;
          containsTicketlist: boolean;
          containsDeliveries: boolean;
          createdAt?: Date;
          updatedAt?: Date;
          deliveryIds?: string[];
          pdfUrl?: string;
          ticketlistPdfUrl?: string;
          addressesCsvDownloadUrl?: string;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/picklists`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexPicklists', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /picklists */
  public async createPicklist(params: {
    batchSize?: number | null;
    containsPicklist?: boolean | null;
    containsTicketlist?: boolean | null;
    containsDeliveries?: boolean | null;
    deliveryIds?: string[] | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        id?: string;
        batchSize?: number;
        reference?: string;
        containsPicklist: boolean;
        containsTicketlist: boolean;
        containsDeliveries: boolean;
        createdAt?: Date;
        updatedAt?: Date;
        deliveryIds?: string[];
        pdfUrl?: string;
        ticketlistPdfUrl?: string;
        addressesCsvDownloadUrl?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/picklists`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'batchSize',
          'containsPicklist',
          'containsTicketlist',
          'containsDeliveries',
          'deliveryIds',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /picklists/{picklist-id} */
  public async showPicklist(params: {
    /** Identifier (UUID). */
    picklistId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        id?: string;
        batchSize?: number;
        reference?: string;
        containsPicklist: boolean;
        containsTicketlist: boolean;
        containsDeliveries: boolean;
        createdAt?: Date;
        updatedAt?: Date;
        deliveryIds?: string[];
        pdfUrl?: string;
        ticketlistPdfUrl?: string;
        addressesCsvDownloadUrl?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/picklists/${params.picklistId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /picklists/{picklist-id} */
  public async updatePicklist(params: {
    /** Identifier (UUID). */
    picklistId: string;
    batchSize?: number | null;
    containsPicklist?: boolean | null;
    containsTicketlist?: boolean | null;
    containsDeliveries?: boolean | null;
    deliveryIds?: string[] | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        id?: string;
        batchSize?: number;
        reference?: string;
        containsPicklist: boolean;
        containsTicketlist: boolean;
        containsDeliveries: boolean;
        createdAt?: Date;
        updatedAt?: Date;
        deliveryIds?: string[];
        pdfUrl?: string;
        ticketlistPdfUrl?: string;
        addressesCsvDownloadUrl?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/picklists/${params.picklistId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'batchSize',
          'containsPicklist',
          'containsTicketlist',
          'containsDeliveries',
          'deliveryIds',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /picklists/{picklist-id} */
  public async destroyPicklist(params: {
    /** Identifier (UUID). */
    picklistId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/picklists/${params.picklistId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** PATCH /prices/{price-id} */
  public async updatePrice(params: {
    /** The identifier of the price. */
    priceId: string;
    shopId?: string | null;
    activeFrom?: Date | null;
    activeUntil?: Date | null;
    amount?: number | null;
    countryCode?: string | null;
    comment?: string | null;
    originalPrice?: number | null;
    overlayListingImage?: FileRepresenting | string | null;
    overlayDetailImage?: FileRepresenting | string | null;
    productTaxesPerCountry?: {} | null;
  }): Promise<
    EntityResponse<
      {
        id?: string;
        shopId?: string;
        activeFrom?: Date;
        activeUntil?: Date;
        amount?: number;
        countryCode?: string;
        comment?: string;
        /** Price this one is replacing (ex: temporary sales, special holidays price). */
        originalPrice?: number;
        active?: boolean;
      } & {
        /** A file asset */
        overlayListingImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        overlayDetailImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/prices/${params.priceId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'shopId',
          'activeFrom',
          'activeUntil',
          'amount',
          'countryCode',
          'comment',
          'originalPrice',
          'overlayListingImage',
          'overlayDetailImage',
          'productTaxesPerCountry',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /prices/{price-id} */
  public async destroyPrice(params: {
    /** The identifier of the price. */
    priceId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/prices/${params.priceId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** PATCH /product_variants/{product-variant-id} */
  public async updateProductVariant(params: {
    /** The identifier of the product variant. */
    productVariantId: string;
    legacyId?: string | null;
    description?: string | null;
    activeFrom?: Date | null;
    activeUntil?: Date | null;
    minimumStockCount?: number | null;
    weight?: number | null;
    ean?: string | null;
    private?: boolean | null;
    order?: number | null;
    sku?: string | null;
    taxRate?: number | null;
  }): Promise<
    EntityResponse<
      {
        id?: string;
        legacyId?: string;
        description?: string;
        reservedCount?: number;
        cartCount?: number;
        availableCount?: number;
        fulfillmentCount?: number;
        incompleteCount?: number;
        pendingDeliveryCount?: number;
        activeFrom?: Date;
        activeUntil?: Date;
        stockCount?: number;
        minimumStockCount?: number;
        weight?: number;
        ean?: string;
        private?: boolean;
        order?: number;
        priceInFuture?: boolean;
        active?: boolean;
        sku?: string;
        prices?: Array<
          {
            id?: string;
            shopId?: string;
            activeFrom?: Date;
            activeUntil?: Date;
            amount?: number;
            countryCode?: string;
            comment?: string;
            /** Price this one is replacing (ex: temporary sales, special holidays price). */
            originalPrice?: number;
            active?: boolean;
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        taxRate?: number;
        taxes?: Array<{
          id?: string;
          countryCode?: string;
          amount?: number;
        }>;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A file asset */
        overlayListingImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        overlayDetailImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/product_variants/${params.productVariantId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'description',
          'activeFrom',
          'activeUntil',
          'minimumStockCount',
          'weight',
          'ean',
          'private',
          'order',
          'sku',
          'taxRate',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /product_variants/{product-variant-id} */
  public async destroyProductVariant(params: {
    /** The identifier of the product variant. */
    productVariantId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/product_variants/${params.productVariantId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /products */
  public async indexProducts(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the products by. */
    sortBy?: string | null;
    /** List only products with these statuses. */
    status?: string[] | null;
    /** List only products with low availability. */
    lowAvailability?: boolean | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          legacyId?: string;
          artist?: string;
          slug?: string;
          artistDe?: string;
          artistEn?: string;
          color?: string;
          colorDe?: string;
          colorEn?: string;
          material?: string;
          materialDe?: string;
          materialEn?: string;
          name?: string;
          nameDe?: string;
          nameEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
          costAccountOne?: string;
          costAccountTwo?: string;
          owningCompany?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
          owningCompanyId?: string;
          contractId?: string;
          productSafetyDescription?: string;
          productSafetyDescriptionDe?: string;
          productSafetyDescriptionEn?: string;
          productType?: string;
          productTypeDe?: string;
          productTypeEn?: string;
          publishedFrom?: Date;
          publishedUntil?: Date;
          shippableFrom?: Date;
          publicShippableFrom?: Date;
          vendorIds?: string[];
          vendors?: Array<
            {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              memberships?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  role?: string;
                  comment?: string;
                  permissions?: string[];
                  /** On invites, the email of the invitee is displayed here (and the user is then null). */
                  invitedEmail?: string;
                  createdAt?: Date;
                  /** On invites, indicates when it expires and becomes invalid. */
                  expiresAt?: Date;
                  /** The .ics URL to the membership's calendar. */
                  calendarFeedUrl?: string;
                } & {
                  indexPermissions?: string[];
                }
              >;
              /** A file asset */
              icon?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              lastBookedClearingInvoice?: {
                /** A unique identifier in the UUID format. */
                id?: string;
                reference?: string;
                bookedAt?: Date;
                /** A unique identifier in the UUID format. */
                orderId?: string;
              };
            }
          >;
          status?: string;
          /** This represents a box of content with a title and a description. */
          details?: {
            title?: string;
            titleDe?: string;
            titleEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
          };
          /** This represents a box of content with a title and a description. */
          additionalInformation?: {
            title?: string;
            titleDe?: string;
            titleEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
          };
          productVariants?: Array<
            {
              id?: string;
              legacyId?: string;
              description?: string;
              reservedCount?: number;
              cartCount?: number;
              availableCount?: number;
              fulfillmentCount?: number;
              incompleteCount?: number;
              pendingDeliveryCount?: number;
              activeFrom?: Date;
              activeUntil?: Date;
              stockCount?: number;
              minimumStockCount?: number;
              weight?: number;
              ean?: string;
              private?: boolean;
              order?: number;
              priceInFuture?: boolean;
              active?: boolean;
              sku?: string;
              prices?: Array<
                {
                  id?: string;
                  shopId?: string;
                  activeFrom?: Date;
                  activeUntil?: Date;
                  amount?: number;
                  countryCode?: string;
                  comment?: string;
                  /** Price this one is replacing (ex: temporary sales, special holidays price). */
                  originalPrice?: number;
                  active?: boolean;
                } & {
                  /** A file asset */
                  overlayListingImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  overlayDetailImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                }
              >;
              taxRate?: number;
              taxes?: Array<{
                id?: string;
                countryCode?: string;
                amount?: number;
              }>;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              /** A file asset */
              overlayListingImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              overlayDetailImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          images?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            }
          >;
          recordType?: string;
          recordDistributor?: string;
          recordLabel?: string;
          recordReleasedAt?: Date;
          /** An override for the price of a product only used in the charts export. */
          recordChartsPrice?: number;
          dhlShippingFrom?: Date;
          customsTariffNumber?: string;
          combinedProductIds?: string[][];
          meta?: {};
          /** Exclude the product from feeds (ex: Facebook, Google). */
          excludeFromFeeds?: boolean;
          /** The Google Shopping category for this product. */
          googleFeedCategory?: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
          taxesPerCountry?: {};
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/products`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'status', 'lowAvailability'])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexProducts', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /products */
  public async createProduct(params: {
    legacyId?: string | null;
    slug?: string | null;
    artistDe?: string | null;
    artistEn?: string | null;
    colorDe?: string | null;
    colorEn?: string | null;
    materialDe?: string | null;
    materialEn?: string | null;
    nameDe?: string | null;
    nameEn?: string | null;
    descriptionDe?: string | null;
    descriptionEn?: string | null;
    cartLimit?: number | null;
    costAccountOne?: string | null;
    costAccountTwo?: string | null;
    owningCompanyId?: string | null;
    contractId?: string | null;
    productSafetyDescription?: string | null;
    productSafetyDescriptionDe?: string | null;
    productSafetyDescriptionEn?: string | null;
    productTypeDe?: string | null;
    productTypeEn?: string | null;
    publishedFrom?: Date | null;
    publishedUntil?: Date | null;
    shippableFrom?: Date | null;
    publicShippableFrom?: Date | null;
    vendorIds?: string[] | null;
    status?: string | null;
    details?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    additionalInformation?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    recordType?: string | null;
    recordDistributor?: string | null;
    recordLabel?: string | null;
    recordReleasedAt?: Date | null;
    recordChartsPrice?: number | null;
    dhlShippingFrom?: Date | null;
    customsTariffNumber?: string | null;
    combinedProductIds?: string[][] | null;
    meta?: {} | null;
    excludeFromFeeds?: boolean | null;
    googleFeedCategory?: string | null;
    taxesPerCountry?: {} | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        legacyId?: string;
        artist?: string;
        slug?: string;
        artistDe?: string;
        artistEn?: string;
        color?: string;
        colorDe?: string;
        colorEn?: string;
        material?: string;
        materialDe?: string;
        materialEn?: string;
        name?: string;
        nameDe?: string;
        nameEn?: string;
        description?: string;
        descriptionDe?: string;
        descriptionEn?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        costAccountOne?: string;
        costAccountTwo?: string;
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        owningCompanyId?: string;
        contractId?: string;
        productSafetyDescription?: string;
        productSafetyDescriptionDe?: string;
        productSafetyDescriptionEn?: string;
        productType?: string;
        productTypeDe?: string;
        productTypeEn?: string;
        publishedFrom?: Date;
        publishedUntil?: Date;
        shippableFrom?: Date;
        publicShippableFrom?: Date;
        vendorIds?: string[];
        vendors?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        status?: string;
        /** This represents a box of content with a title and a description. */
        details?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        productVariants?: Array<
          {
            id?: string;
            legacyId?: string;
            description?: string;
            reservedCount?: number;
            cartCount?: number;
            availableCount?: number;
            fulfillmentCount?: number;
            incompleteCount?: number;
            pendingDeliveryCount?: number;
            activeFrom?: Date;
            activeUntil?: Date;
            stockCount?: number;
            minimumStockCount?: number;
            weight?: number;
            ean?: string;
            private?: boolean;
            order?: number;
            priceInFuture?: boolean;
            active?: boolean;
            sku?: string;
            prices?: Array<
              {
                id?: string;
                shopId?: string;
                activeFrom?: Date;
                activeUntil?: Date;
                amount?: number;
                countryCode?: string;
                comment?: string;
                /** Price this one is replacing (ex: temporary sales, special holidays price). */
                originalPrice?: number;
                active?: boolean;
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            taxRate?: number;
            taxes?: Array<{
              id?: string;
              countryCode?: string;
              amount?: number;
            }>;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        images?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          }
        >;
        recordType?: string;
        recordDistributor?: string;
        recordLabel?: string;
        recordReleasedAt?: Date;
        /** An override for the price of a product only used in the charts export. */
        recordChartsPrice?: number;
        dhlShippingFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        /** The Google Shopping category for this product. */
        googleFeedCategory?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
        taxesPerCountry?: {};
        shippingRequirements?: string[];
        prohibitedShippingTags?: string[];
        freeShippingMode?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'slug',
          'artistDe',
          'artistEn',
          'colorDe',
          'colorEn',
          'materialDe',
          'materialEn',
          'nameDe',
          'nameEn',
          'descriptionDe',
          'descriptionEn',
          'cartLimit',
          'costAccountOne',
          'costAccountTwo',
          'owningCompanyId',
          'contractId',
          'productSafetyDescription',
          'productSafetyDescriptionDe',
          'productSafetyDescriptionEn',
          'productTypeDe',
          'productTypeEn',
          'publishedFrom',
          'publishedUntil',
          'shippableFrom',
          'publicShippableFrom',
          'vendorIds',
          'status',
          'details',
          'additionalInformation',
          'recordType',
          'recordDistributor',
          'recordLabel',
          'recordReleasedAt',
          'recordChartsPrice',
          'dhlShippingFrom',
          'customsTariffNumber',
          'combinedProductIds',
          'meta',
          'excludeFromFeeds',
          'googleFeedCategory',
          'taxesPerCountry',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /products/{product-id} */
  public async showProduct(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        legacyId?: string;
        artist?: string;
        slug?: string;
        artistDe?: string;
        artistEn?: string;
        color?: string;
        colorDe?: string;
        colorEn?: string;
        material?: string;
        materialDe?: string;
        materialEn?: string;
        name?: string;
        nameDe?: string;
        nameEn?: string;
        description?: string;
        descriptionDe?: string;
        descriptionEn?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        costAccountOne?: string;
        costAccountTwo?: string;
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        owningCompanyId?: string;
        contractId?: string;
        productSafetyDescription?: string;
        productSafetyDescriptionDe?: string;
        productSafetyDescriptionEn?: string;
        productType?: string;
        productTypeDe?: string;
        productTypeEn?: string;
        publishedFrom?: Date;
        publishedUntil?: Date;
        shippableFrom?: Date;
        publicShippableFrom?: Date;
        vendorIds?: string[];
        vendors?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        status?: string;
        /** This represents a box of content with a title and a description. */
        details?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        productVariants?: Array<
          {
            id?: string;
            legacyId?: string;
            description?: string;
            reservedCount?: number;
            cartCount?: number;
            availableCount?: number;
            fulfillmentCount?: number;
            incompleteCount?: number;
            pendingDeliveryCount?: number;
            activeFrom?: Date;
            activeUntil?: Date;
            stockCount?: number;
            minimumStockCount?: number;
            weight?: number;
            ean?: string;
            private?: boolean;
            order?: number;
            priceInFuture?: boolean;
            active?: boolean;
            sku?: string;
            prices?: Array<
              {
                id?: string;
                shopId?: string;
                activeFrom?: Date;
                activeUntil?: Date;
                amount?: number;
                countryCode?: string;
                comment?: string;
                /** Price this one is replacing (ex: temporary sales, special holidays price). */
                originalPrice?: number;
                active?: boolean;
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            taxRate?: number;
            taxes?: Array<{
              id?: string;
              countryCode?: string;
              amount?: number;
            }>;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        images?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          }
        >;
        recordType?: string;
        recordDistributor?: string;
        recordLabel?: string;
        recordReleasedAt?: Date;
        /** An override for the price of a product only used in the charts export. */
        recordChartsPrice?: number;
        dhlShippingFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        /** The Google Shopping category for this product. */
        googleFeedCategory?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
        taxesPerCountry?: {};
        shippingRequirements?: string[];
        prohibitedShippingTags?: string[];
        freeShippingMode?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/products/${params.productId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /products/{product-id} */
  public async updateProduct(params: {
    /** The slug or identifier of the product. */
    productId: string;
    legacyId?: string | null;
    slug?: string | null;
    artistDe?: string | null;
    artistEn?: string | null;
    colorDe?: string | null;
    colorEn?: string | null;
    materialDe?: string | null;
    materialEn?: string | null;
    nameDe?: string | null;
    nameEn?: string | null;
    descriptionDe?: string | null;
    descriptionEn?: string | null;
    cartLimit?: number | null;
    costAccountOne?: string | null;
    costAccountTwo?: string | null;
    owningCompanyId?: string | null;
    contractId?: string | null;
    productSafetyDescription?: string | null;
    productSafetyDescriptionDe?: string | null;
    productSafetyDescriptionEn?: string | null;
    productTypeDe?: string | null;
    productTypeEn?: string | null;
    publishedFrom?: Date | null;
    publishedUntil?: Date | null;
    shippableFrom?: Date | null;
    publicShippableFrom?: Date | null;
    vendorIds?: string[] | null;
    status?: string | null;
    details?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    additionalInformation?: {
      title?: string | null;
      titleDe?: string | null;
      titleEn?: string | null;
      description?: string | null;
      descriptionDe?: string | null;
      descriptionEn?: string | null;
    } | null;
    recordType?: string | null;
    recordDistributor?: string | null;
    recordLabel?: string | null;
    recordReleasedAt?: Date | null;
    recordChartsPrice?: number | null;
    dhlShippingFrom?: Date | null;
    customsTariffNumber?: string | null;
    combinedProductIds?: string[][] | null;
    meta?: {} | null;
    excludeFromFeeds?: boolean | null;
    googleFeedCategory?: string | null;
    taxesPerCountry?: {} | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        legacyId?: string;
        artist?: string;
        slug?: string;
        artistDe?: string;
        artistEn?: string;
        color?: string;
        colorDe?: string;
        colorEn?: string;
        material?: string;
        materialDe?: string;
        materialEn?: string;
        name?: string;
        nameDe?: string;
        nameEn?: string;
        description?: string;
        descriptionDe?: string;
        descriptionEn?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        costAccountOne?: string;
        costAccountTwo?: string;
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        owningCompanyId?: string;
        contractId?: string;
        productSafetyDescription?: string;
        productSafetyDescriptionDe?: string;
        productSafetyDescriptionEn?: string;
        productType?: string;
        productTypeDe?: string;
        productTypeEn?: string;
        publishedFrom?: Date;
        publishedUntil?: Date;
        shippableFrom?: Date;
        publicShippableFrom?: Date;
        vendorIds?: string[];
        vendors?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        status?: string;
        /** This represents a box of content with a title and a description. */
        details?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        productVariants?: Array<
          {
            id?: string;
            legacyId?: string;
            description?: string;
            reservedCount?: number;
            cartCount?: number;
            availableCount?: number;
            fulfillmentCount?: number;
            incompleteCount?: number;
            pendingDeliveryCount?: number;
            activeFrom?: Date;
            activeUntil?: Date;
            stockCount?: number;
            minimumStockCount?: number;
            weight?: number;
            ean?: string;
            private?: boolean;
            order?: number;
            priceInFuture?: boolean;
            active?: boolean;
            sku?: string;
            prices?: Array<
              {
                id?: string;
                shopId?: string;
                activeFrom?: Date;
                activeUntil?: Date;
                amount?: number;
                countryCode?: string;
                comment?: string;
                /** Price this one is replacing (ex: temporary sales, special holidays price). */
                originalPrice?: number;
                active?: boolean;
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            taxRate?: number;
            taxes?: Array<{
              id?: string;
              countryCode?: string;
              amount?: number;
            }>;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        images?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          }
        >;
        recordType?: string;
        recordDistributor?: string;
        recordLabel?: string;
        recordReleasedAt?: Date;
        /** An override for the price of a product only used in the charts export. */
        recordChartsPrice?: number;
        dhlShippingFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        /** The Google Shopping category for this product. */
        googleFeedCategory?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
        taxesPerCountry?: {};
        shippingRequirements?: string[];
        prohibitedShippingTags?: string[];
        freeShippingMode?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/products/${params.productId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'slug',
          'artistDe',
          'artistEn',
          'colorDe',
          'colorEn',
          'materialDe',
          'materialEn',
          'nameDe',
          'nameEn',
          'descriptionDe',
          'descriptionEn',
          'cartLimit',
          'costAccountOne',
          'costAccountTwo',
          'owningCompanyId',
          'contractId',
          'productSafetyDescription',
          'productSafetyDescriptionDe',
          'productSafetyDescriptionEn',
          'productTypeDe',
          'productTypeEn',
          'publishedFrom',
          'publishedUntil',
          'shippableFrom',
          'publicShippableFrom',
          'vendorIds',
          'status',
          'details',
          'additionalInformation',
          'recordType',
          'recordDistributor',
          'recordLabel',
          'recordReleasedAt',
          'recordChartsPrice',
          'dhlShippingFrom',
          'customsTariffNumber',
          'combinedProductIds',
          'meta',
          'excludeFromFeeds',
          'googleFeedCategory',
          'taxesPerCountry',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /products/{product-id} */
  public async destroyProduct(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/products/${params.productId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /products/{product-id}/category_assignments */
  public async indexCategoryAssignments(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<
      Array<{
        order?: number;
        category: {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        };
        /** The URL of the product in the shop of the described category. */
        urlInShop: string;
      }>
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/products/${params.productId}/category_assignments`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/category_assignments */
  public async createCategoryAssignment(params: {
    /** The slug or identifier of the product. */
    productId: string;
    order?: number | null;
    categoryId?: string | null;
  }): Promise<
    EntityResponse<{
      order?: number;
      category: {
        /** The category's name. */
        name?: string;
        /** The type of this category */
        type?: string;
      } & {
        /** Main categories that should be shown in the menu. */
        mainCategory?: boolean;
        parentCategoryId?: string;
        parentCategory?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        };
        /** Subcategories of this category. */
        subcategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          }
        >;
        /** The order of the category relative to it's sibling categories */
        order?: number;
        /** Alternative names for this category */
        alternativeNames?: string[];
        /** Short abstract describing the category */
        abstract?: string;
        /** Longer description of the category */
        description?: string;
        createdAt?: Date;
        updatedAt?: Date;
        featuredAt?: string;
        subtitle?: string;
        /** If false, the subtitle will not be included when the Shop API is reading the category. */
        subtitleInShop?: boolean;
        sortOrderHomepage?: number;
        sortOrderTicketTopLeft?: number;
        sortOrderTicketTopRight?: number;
        sortOrderTicketBottom?: number;
        comment?: string;
        /** Extra information for this category. */
        meta?: {};
        /** A file asset */
        squareImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        portraitImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        landscapeImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      };
      /** The URL of the product in the shop of the described category. */
      urlInShop: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/category_assignments`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['order', 'categoryId'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /products/{product-id}/category_assignments/{category-id} */
  public async updateCategoryAssignment(params: {
    /** The slug or identifier of the product. */
    productId: string;
    /** Identifier (UUID). */
    categoryId: string;
    order?: number | null;
  }): Promise<
    EntityResponse<{
      order?: number;
      category: {
        /** The category's name. */
        name?: string;
        /** The type of this category */
        type?: string;
      } & {
        /** Main categories that should be shown in the menu. */
        mainCategory?: boolean;
        parentCategoryId?: string;
        parentCategory?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        };
        /** Subcategories of this category. */
        subcategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          }
        >;
        /** The order of the category relative to it's sibling categories */
        order?: number;
        /** Alternative names for this category */
        alternativeNames?: string[];
        /** Short abstract describing the category */
        abstract?: string;
        /** Longer description of the category */
        description?: string;
        createdAt?: Date;
        updatedAt?: Date;
        featuredAt?: string;
        subtitle?: string;
        /** If false, the subtitle will not be included when the Shop API is reading the category. */
        subtitleInShop?: boolean;
        sortOrderHomepage?: number;
        sortOrderTicketTopLeft?: number;
        sortOrderTicketTopRight?: number;
        sortOrderTicketBottom?: number;
        comment?: string;
        /** Extra information for this category. */
        meta?: {};
        /** A file asset */
        squareImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        portraitImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        landscapeImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      };
      /** The URL of the product in the shop of the described category. */
      urlInShop: string;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/products/${params.productId}/category_assignments/${params.categoryId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['order'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /products/{product-id}/category_assignments/{category-id} */
  public async destroyCategoryAssignment(params: {
    /** The slug or identifier of the product. */
    productId: string;
    /** Identifier (UUID). */
    categoryId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/products/${params.productId}/category_assignments/${params.categoryId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** POST /products/{product-id}/duplicates */
  public async duplicateProduct(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        legacyId?: string;
        artist?: string;
        slug?: string;
        artistDe?: string;
        artistEn?: string;
        color?: string;
        colorDe?: string;
        colorEn?: string;
        material?: string;
        materialDe?: string;
        materialEn?: string;
        name?: string;
        nameDe?: string;
        nameEn?: string;
        description?: string;
        descriptionDe?: string;
        descriptionEn?: string;
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        costAccountOne?: string;
        costAccountTwo?: string;
        owningCompany?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        };
        owningCompanyId?: string;
        contractId?: string;
        productSafetyDescription?: string;
        productSafetyDescriptionDe?: string;
        productSafetyDescriptionEn?: string;
        productType?: string;
        productTypeDe?: string;
        productTypeEn?: string;
        publishedFrom?: Date;
        publishedUntil?: Date;
        shippableFrom?: Date;
        publicShippableFrom?: Date;
        vendorIds?: string[];
        vendors?: Array<
          {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          }
        >;
        status?: string;
        /** This represents a box of content with a title and a description. */
        details?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        /** This represents a box of content with a title and a description. */
        additionalInformation?: {
          title?: string;
          titleDe?: string;
          titleEn?: string;
          description?: string;
          descriptionDe?: string;
          descriptionEn?: string;
        };
        productVariants?: Array<
          {
            id?: string;
            legacyId?: string;
            description?: string;
            reservedCount?: number;
            cartCount?: number;
            availableCount?: number;
            fulfillmentCount?: number;
            incompleteCount?: number;
            pendingDeliveryCount?: number;
            activeFrom?: Date;
            activeUntil?: Date;
            stockCount?: number;
            minimumStockCount?: number;
            weight?: number;
            ean?: string;
            private?: boolean;
            order?: number;
            priceInFuture?: boolean;
            active?: boolean;
            sku?: string;
            prices?: Array<
              {
                id?: string;
                shopId?: string;
                activeFrom?: Date;
                activeUntil?: Date;
                amount?: number;
                countryCode?: string;
                comment?: string;
                /** Price this one is replacing (ex: temporary sales, special holidays price). */
                originalPrice?: number;
                active?: boolean;
              } & {
                /** A file asset */
                overlayListingImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                overlayDetailImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
            taxRate?: number;
            taxes?: Array<{
              id?: string;
              countryCode?: string;
              amount?: number;
            }>;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        images?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          }
        >;
        recordType?: string;
        recordDistributor?: string;
        recordLabel?: string;
        recordReleasedAt?: Date;
        /** An override for the price of a product only used in the charts export. */
        recordChartsPrice?: number;
        dhlShippingFrom?: Date;
        customsTariffNumber?: string;
        combinedProductIds?: string[][];
        meta?: {};
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
        /** The Google Shopping category for this product. */
        googleFeedCategory?: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        ownerships?: Array<
          {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            role?: string;
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
        /** A hash of country-code to tax rate.  Country codes have to be ISO-3166 "2 letters" codes. A special "world" country code is supported for "all countries".  The tax rate is given in decimal (ex: 0.19 for 19%). */
        taxesPerCountry?: {};
        shippingRequirements?: string[];
        prohibitedShippingTags?: string[];
        freeShippingMode?: string;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/duplicates`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/images */
  public async createImage(params: {
    /** The slug or identifier of the product. */
    productId: string;
    file?: string | null;
    order?: number | null;
    tags?: string[] | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        url: string;
        originalUrl?: string;
        tags: string[];
        alt?: string;
        altDe?: string;
        altEn?: string;
        /** Object of sizes mapping to urls */
        thumbs?: {
          10: string;
          75: string;
          100: string;
          150: string;
          180: string;
          240: string;
          360: string;
          370: string;
          500: string;
          740: string;
          1000: string;
          1500: string;
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/images`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['file', 'order', 'tags'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/ownerships */
  public async createOwnership(params: {
    /** The slug or identifier of the product. */
    productId: string;
    role?: string | null;
    companyId?: string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        role?: string;
        company?: {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        };
        product?:
          | {
              legacyId?: string;
              artist?: string;
              slug?: string;
              artistDe?: string;
              artistEn?: string;
              color?: string;
              colorDe?: string;
              colorEn?: string;
              material?: string;
              materialDe?: string;
              materialEn?: string;
              name?: string;
              nameDe?: string;
              nameEn?: string;
              description?: string;
              descriptionDe?: string;
              descriptionEn?: string;
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              costAccountOne?: string;
              costAccountTwo?: string;
              owningCompany?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                memberships?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                    $permissions: string[];
                  } & {
                    role?: string;
                    comment?: string;
                    permissions?: string[];
                    /** On invites, the email of the invitee is displayed here (and the user is then null). */
                    invitedEmail?: string;
                    createdAt?: Date;
                    /** On invites, indicates when it expires and becomes invalid. */
                    expiresAt?: Date;
                    /** The .ics URL to the membership's calendar. */
                    calendarFeedUrl?: string;
                  } & {
                    indexPermissions?: string[];
                  }
                >;
                /** A file asset */
                icon?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                lastBookedClearingInvoice?: {
                  /** A unique identifier in the UUID format. */
                  id?: string;
                  reference?: string;
                  bookedAt?: Date;
                  /** A unique identifier in the UUID format. */
                  orderId?: string;
                };
              };
              owningCompanyId?: string;
              contractId?: string;
              productSafetyDescription?: string;
              productSafetyDescriptionDe?: string;
              productSafetyDescriptionEn?: string;
              productType?: string;
              productTypeDe?: string;
              productTypeEn?: string;
              publishedFrom?: Date;
              publishedUntil?: Date;
              shippableFrom?: Date;
              publicShippableFrom?: Date;
              vendorIds?: string[];
              vendors?: Array<
                {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  memberships?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                      $permissions: string[];
                    } & {
                      role?: string;
                      comment?: string;
                      permissions?: string[];
                      /** On invites, the email of the invitee is displayed here (and the user is then null). */
                      invitedEmail?: string;
                      createdAt?: Date;
                      /** On invites, indicates when it expires and becomes invalid. */
                      expiresAt?: Date;
                      /** The .ics URL to the membership's calendar. */
                      calendarFeedUrl?: string;
                    } & {
                      indexPermissions?: string[];
                    }
                  >;
                  /** A file asset */
                  icon?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  lastBookedClearingInvoice?: {
                    /** A unique identifier in the UUID format. */
                    id?: string;
                    reference?: string;
                    bookedAt?: Date;
                    /** A unique identifier in the UUID format. */
                    orderId?: string;
                  };
                }
              >;
              status?: string;
              /** This represents a box of content with a title and a description. */
              details?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              /** This represents a box of content with a title and a description. */
              additionalInformation?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              productVariants?: Array<
                {
                  id?: string;
                  legacyId?: string;
                  description?: string;
                  reservedCount?: number;
                  cartCount?: number;
                  availableCount?: number;
                  fulfillmentCount?: number;
                  incompleteCount?: number;
                  pendingDeliveryCount?: number;
                  activeFrom?: Date;
                  activeUntil?: Date;
                  stockCount?: number;
                  minimumStockCount?: number;
                  weight?: number;
                  ean?: string;
                  private?: boolean;
                  order?: number;
                  priceInFuture?: boolean;
                  active?: boolean;
                  sku?: string;
                  prices?: Array<
                    {
                      id?: string;
                      shopId?: string;
                      activeFrom?: Date;
                      activeUntil?: Date;
                      amount?: number;
                      countryCode?: string;
                      comment?: string;
                      /** Price this one is replacing (ex: temporary sales, special holidays price). */
                      originalPrice?: number;
                      active?: boolean;
                    } & {
                      /** A file asset */
                      overlayListingImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                      /** A file asset */
                      overlayDetailImage?: {
                        /** A unique identifier in the UUID format. */
                        id: string;
                      } & {
                        url: string;
                        originalUrl?: string;
                        tags: string[];
                        alt?: string;
                        altDe?: string;
                        altEn?: string;
                        /** Object of sizes mapping to urls */
                        thumbs?: {
                          10: string;
                          75: string;
                          100: string;
                          150: string;
                          180: string;
                          240: string;
                          360: string;
                          370: string;
                          500: string;
                          740: string;
                          1000: string;
                          1500: string;
                        };
                      };
                    }
                  >;
                  taxRate?: number;
                  taxes?: Array<{
                    id?: string;
                    countryCode?: string;
                    amount?: number;
                  }>;
                } & {
                  /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                  $permissions: string[];
                } & {
                  /** A file asset */
                  overlayListingImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  overlayDetailImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                }
              >;
              images?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                }
              >;
              recordType?: string;
              recordDistributor?: string;
              recordLabel?: string;
              recordReleasedAt?: Date;
              /** An override for the price of a product only used in the charts export. */
              recordChartsPrice?: number;
              dhlShippingFrom?: Date;
              customsTariffNumber?: string;
              combinedProductIds?: string[][];
              meta?: {};
              /** Exclude the product from feeds (ex: Facebook, Google). */
              excludeFromFeeds?: boolean;
              /** The Google Shopping category for this product. */
              googleFeedCategory?: string;
            }
          | {
              /** Representation in the old database. */
              legacyId?: string;
              /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
              name?: string;
              /** Ticket override for the name attribute */
              nameOverride?: string;
              /** aka ticket line 1 */
              artist?: string;
              /** Ticket override for the artist attribute */
              artistOverride?: string;
              /** aka ticket line 3 */
              ticketSupportLine?: string;
              /** Ticket override for the ticket_support_line attribute */
              ticketSupportLineOverride?: string;
              /** aka ticket line 4 */
              ticketHeadline?: string;
              /** Ticket override for the ticket_headline attribute */
              ticketHeadlineOverride?: string;
              ticketLayout?: string;
              onlineTicketLayout?: string;
              localTicketLayout?: string;
              externalReference?: string;
              /** This represents a box of content with a title and a description. */
              additionalInformation?: {
                title?: string;
                titleDe?: string;
                titleEn?: string;
                description?: string;
                descriptionDe?: string;
                descriptionEn?: string;
              };
              /** Ticket override for the additional_information attribute */
              additionalInformationOverride?: string;
              /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
              immediatePaymentAtStockQuota?: number;
              /** The status of this event */
              status?: string;
              /** Date at which the event starts */
              happeningFrom?: Date;
              /** Date at which the event stops */
              happeningUntil?: Date;
              sku?: string;
              tourOrganizer?: string;
              localOrganizer?: string;
              /** Indicates when this event should be published */
              publishedFrom?: Date;
              /** Indicates when this event should be unpublished */
              publishedUntil?: Date;
              ageLimit?: number;
              personalizationRequirement?: string;
              soldOutMessage?: string;
              /** At which time the doors to the venue open (in the time zone of the venue) */
              doorTime?: string;
              /** At which time the show begins (in the time zone of the venue) */
              stageTime?: string;
              /** The maximum count of items of this product that can be added to a cart. */
              cartLimit?: number;
              /** Link to download a pdf report of the venue layout with the current seat status applied. */
              allocationPdfDownloadUrl?: string;
              /** Link to download a pdf report of the event sales so far. */
              salesReportPdfDownloadUrl?: string;
              description?: string;
              descriptionEn?: string;
              descriptionDe?: string;
              shippableFrom?: Date;
              dhlShippingFrom?: Date;
              publicShippableFrom?: Date;
              customsTariffNumber?: string;
              combinedProductIds?: string[][];
              meta?: {};
              /** Exclude the product from feeds (ex: Facebook, Google). */
              excludeFromFeeds?: boolean;
              publicTransportDescription?: string;
            };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/ownerships`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['role', 'companyId'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/prices */
  public async createPrice(params: {
    /** The slug or identifier of the product. */
    productId: string;
    shopId?: string | null;
    activeFrom?: Date | null;
    activeUntil?: Date | null;
    amount?: number | null;
    countryCode?: string | null;
    comment?: string | null;
    originalPrice?: number | null;
    overlayListingImage?: FileRepresenting | string | null;
    overlayDetailImage?: FileRepresenting | string | null;
    productTaxesPerCountry?: {} | null;
  }): Promise<
    EntityResponse<
      {
        id?: string;
        shopId?: string;
        activeFrom?: Date;
        activeUntil?: Date;
        amount?: number;
        countryCode?: string;
        comment?: string;
        /** Price this one is replacing (ex: temporary sales, special holidays price). */
        originalPrice?: number;
        active?: boolean;
      } & {
        /** A file asset */
        overlayListingImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        overlayDetailImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/prices`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'shopId',
          'activeFrom',
          'activeUntil',
          'amount',
          'countryCode',
          'comment',
          'originalPrice',
          'overlayListingImage',
          'overlayDetailImage',
          'productTaxesPerCountry',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/product_variants */
  public async createProductVariant(params: {
    /** The slug or identifier of the product. */
    productId: string;
    legacyId?: string | null;
    description?: string | null;
    activeFrom?: Date | null;
    activeUntil?: Date | null;
    minimumStockCount?: number | null;
    weight?: number | null;
    ean?: string | null;
    private?: boolean | null;
    order?: number | null;
    sku?: string | null;
    taxRate?: number | null;
  }): Promise<
    EntityResponse<
      {
        id?: string;
        legacyId?: string;
        description?: string;
        reservedCount?: number;
        cartCount?: number;
        availableCount?: number;
        fulfillmentCount?: number;
        incompleteCount?: number;
        pendingDeliveryCount?: number;
        activeFrom?: Date;
        activeUntil?: Date;
        stockCount?: number;
        minimumStockCount?: number;
        weight?: number;
        ean?: string;
        private?: boolean;
        order?: number;
        priceInFuture?: boolean;
        active?: boolean;
        sku?: string;
        prices?: Array<
          {
            id?: string;
            shopId?: string;
            activeFrom?: Date;
            activeUntil?: Date;
            amount?: number;
            countryCode?: string;
            comment?: string;
            /** Price this one is replacing (ex: temporary sales, special holidays price). */
            originalPrice?: number;
            active?: boolean;
          } & {
            /** A file asset */
            overlayListingImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            overlayDetailImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          }
        >;
        taxRate?: number;
        taxes?: Array<{
          id?: string;
          countryCode?: string;
          amount?: number;
        }>;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        /** A file asset */
        overlayListingImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        overlayDetailImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/product_variants`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'legacyId',
          'description',
          'activeFrom',
          'activeUntil',
          'minimumStockCount',
          'weight',
          'ean',
          'private',
          'order',
          'sku',
          'taxRate',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /products/{product-id}/recall */
  public async showProductRecall(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        titleEn?: string;
        titleDe?: string;
        textEn?: string;
        textDe?: string;
        emailTemplateEn?: string;
        emailTemplateDe?: string;
        emailSubjectEn?: string;
        emailSubjectDe?: string;
        paymentsMassBookable?: boolean;
        usedTicketsMassRefundable?: boolean;
        paused?: boolean;
      } & {
        triggeredAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/products/${params.productId}/recall`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /products/{product-id}/recall */
  public async createProductRecall(params: {
    /** The slug or identifier of the product. */
    productId: string;
    titleEn?: string | null;
    titleDe?: string | null;
    textEn?: string | null;
    textDe?: string | null;
    emailTemplateEn?: string | null;
    emailTemplateDe?: string | null;
    emailSubjectEn?: string | null;
    emailSubjectDe?: string | null;
    paymentsMassBookable?: boolean | null;
    usedTicketsMassRefundable?: boolean | null;
    paused?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        titleEn?: string;
        titleDe?: string;
        textEn?: string;
        textDe?: string;
        emailTemplateEn?: string;
        emailTemplateDe?: string;
        emailSubjectEn?: string;
        emailSubjectDe?: string;
        paymentsMassBookable?: boolean;
        usedTicketsMassRefundable?: boolean;
        paused?: boolean;
      } & {
        triggeredAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/products/${params.productId}/recall`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'titleEn',
          'titleDe',
          'textEn',
          'textDe',
          'emailTemplateEn',
          'emailTemplateDe',
          'emailSubjectEn',
          'emailSubjectDe',
          'paymentsMassBookable',
          'usedTicketsMassRefundable',
          'paused',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /products/{product-id}/recall */
  public async updateProductRecall(params: {
    /** The slug or identifier of the product. */
    productId: string;
    titleEn?: string | null;
    titleDe?: string | null;
    textEn?: string | null;
    textDe?: string | null;
    emailTemplateEn?: string | null;
    emailTemplateDe?: string | null;
    emailSubjectEn?: string | null;
    emailSubjectDe?: string | null;
    paymentsMassBookable?: boolean | null;
    usedTicketsMassRefundable?: boolean | null;
    paused?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        titleEn?: string;
        titleDe?: string;
        textEn?: string;
        textDe?: string;
        emailTemplateEn?: string;
        emailTemplateDe?: string;
        emailSubjectEn?: string;
        emailSubjectDe?: string;
        paymentsMassBookable?: boolean;
        usedTicketsMassRefundable?: boolean;
        paused?: boolean;
      } & {
        triggeredAt?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/products/${params.productId}/recall`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'titleEn',
          'titleDe',
          'textEn',
          'textDe',
          'emailTemplateEn',
          'emailTemplateDe',
          'emailSubjectEn',
          'emailSubjectDe',
          'paymentsMassBookable',
          'usedTicketsMassRefundable',
          'paused',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /products/{product-id}/recall */
  public async deleteProductRecall(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/products/${params.productId}/recall`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /products/{product-id}/sales_stats */
  public async showProductSalesStats(params: {
    /** The slug or identifier of the product. */
    productId: string;
  }): Promise<
    EntityResponse<{
      stats?: Array<{
        /** Name / description of product variant */
        name?: string;
        /** Product variant's ID */
        id?: string;
        lifetime?: {
          total?: number;
          online?: number;
        };
        lastYear?: {
          total?: number;
          online?: number;
        };
        lastThirtyDays?: {
          total?: number;
          online?: number;
        };
        today?: {
          total?: number;
          online?: number;
        };
        hour?: {
          total?: number;
          online?: number;
        };
        months?: Array<{
          /** First day of that month */
          date?: Date;
          total?: number;
          online?: number;
        }>;
      }>;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/products/${params.productId}/sales_stats`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /search */
  public async search(params: {
    /** The term to search for. */
    term: string;
  }): Promise<
    EntityResponse<{
      events?: Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The name of this event. Usually this is some title or the festivals name. */
          name?: string;
          /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
          artist?: string;
          /** Array of assigned artists */
          artists?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              /** A file asset */
              headerImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              sliderImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              ticketImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              backgroundImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              tours?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                } & {
                  /** Main categories that should be shown in the menu. */
                  mainCategory?: boolean;
                  parentCategoryId?: string;
                  parentCategory?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** A unique identifier in the UUID format. */
                    shopId?: string;
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  };
                  /** Subcategories of this category. */
                  subcategories?: Array<
                    {
                      /** A unique identifier in the UUID format. */
                      id: string;
                    } & {
                      /** The category's name. */
                      name?: string;
                      /** The type of this category */
                      type?: string;
                    }
                  >;
                  /** The order of the category relative to it's sibling categories */
                  order?: number;
                  /** Alternative names for this category */
                  alternativeNames?: string[];
                  /** Short abstract describing the category */
                  abstract?: string;
                  /** Longer description of the category */
                  description?: string;
                  createdAt?: Date;
                  updatedAt?: Date;
                  featuredAt?: string;
                  subtitle?: string;
                  /** If false, the subtitle will not be included when the Shop API is reading the category. */
                  subtitleInShop?: boolean;
                  sortOrderHomepage?: number;
                  sortOrderTicketTopLeft?: number;
                  sortOrderTicketTopRight?: number;
                  sortOrderTicketBottom?: number;
                  comment?: string;
                  /** Extra information for this category. */
                  meta?: {};
                  /** A file asset */
                  squareImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  portraitImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                  /** A file asset */
                  landscapeImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                } & {
                  /** Date at which the tour starts (date of its first event). */
                  startDate?: Date;
                  /** Date at which the tour stops (date of its last event). */
                  endDate?: Date;
                  sortOrderHomepage?: number;
                  sortOrderTicketTopLeft?: number;
                  sortOrderTicketTopRight?: number;
                  sortOrderTicketBottom?: number;
                  /** A file asset */
                  ticketImage?: {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    url: string;
                    originalUrl?: string;
                    tags: string[];
                    alt?: string;
                    altDe?: string;
                    altEn?: string;
                    /** Object of sizes mapping to urls */
                    thumbs?: {
                      10: string;
                      75: string;
                      100: string;
                      150: string;
                      180: string;
                      240: string;
                      360: string;
                      370: string;
                      500: string;
                      740: string;
                      1000: string;
                      1500: string;
                    };
                  };
                }
              >;
            }
          >;
          /** The total price for the cheapest variant of this event. */
          price?: number;
          /** A hint to display a map to facilitate the selection of different variants from a map. */
          displayMap?: boolean;
          /** At which time the doors to the venue open (in the time zone of the venue) */
          doorTime?: string;
          /** At which time the show begins (in the time zone of the venue) */
          stageTime?: string;
          /** Date at which the event starts */
          happeningFrom?: Date;
          /** Date at which the event stops */
          happeningUntil?: Date;
          /** List the ids of categories where this event is assigned for the event's shop */
          categoryIds?: string[];
          meta?: {};
          presaleAt?: Date;
          /** Only set when event is sold out */
          soldOutMessage?: string;
          /** A file asset */
          image?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
          description?: string;
          publicShippableFrom?: Date;
        }
      >;
      merchProducts?: Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
          type?: string;
          originalPrice?: number;
          price?: number;
          /** True if at least one of the product's variants is available */
          available?: boolean;
          /** True if there is only a small amount of this product left */
          lowStock?: boolean;
          /** The artist title of this product */
          artistName?: string;
          meta?: {};
          presaleAt?: Date;
          /** Details about the product. Can contain HTML. */
          details?: {
            title?: string;
            description?: string;
          };
          combinedProductIds?: string[][];
          immediatePaymentAtStockQuota?: number;
        } & {
          /** A list of the product's variants */
          productVariants?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              price?: number;
              tax?: number;
              taxRate?: number;
              description?: string;
              available?: boolean;
              /** Dimensions of this specific variant. Given in a generic key-value pairs. Typically representing axis from a pictogram presented to the user. */
              sizes?: {};
            }
          >;
          artist?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          };
          categories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            } & {
              shop?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The actual name of this shop, displayable to the customer. */
                name?: string;
                /** Default locale (language & formats) of this shop. */
                defaultLocale?: string;
                /** Is the 'waiting queue' feature enabled for this shop? */
                waitingQueue?: boolean;
                /** Is this shop listed in the sitemap? */
                sitemap?: boolean;
                /** Extra information for this shop. */
                meta?: {};
                domain?: string;
                orderMergingAllowed?: boolean;
                paymentBlacklist?: string[];
                /** A Base64 reprensentation of the image. */
                logo?: FileRepresenting | string;
                /** Minimum amount to get free shipping */
                freeShippingThreshold?: number;
              };
            }
          >;
          images?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            }
          >;
          /** A file asset */
          pictogram?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
        }
      >;
      companies?: Array<
        {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
          bankAccount?: {
            name?: string;
            iban?: string;
            bic?: string;
            bankName?: string;
          };
          vatId?: string;
          vatNumber?: string;
          eventCreditorReference?: string;
          merchCreditorReference?: string;
          merchClearingOrderId?: string;
          features?: string[];
          invoicingCycle?: string;
          billingEmails?: string[];
          assignableRoles?: string[];
          defaultEventPayoutPercentage?: number;
          eventPayoutCycle?: string;
          lastEventPayout?: Date;
        } & {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          memberships?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              indexPermissions?: string[];
            }
          >;
          /** A file asset */
          icon?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          lastBookedClearingInvoice?: {
            /** A unique identifier in the UUID format. */
            id?: string;
            reference?: string;
            bookedAt?: Date;
            /** A unique identifier in the UUID format. */
            orderId?: string;
          };
        }
      >;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/search`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['term'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /session */
  public async showSession(): Promise<
    EntityResponse<{
      /** A unique identifier that should be send with every request that requires a session */
      token: string;
      /** ISO code of the current user's language */
      language: string;
      /** Indicates until when session is valid and will be purged */
      validUntil: Date;
      /** The user's ID (or null) */
      userId?: string;
      /** List of the membership this user has, with minimal information on the companies she/he has access to. */
      memberships: Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          role?: string;
          comment?: string;
          permissions?: string[];
          /** On invites, the email of the invitee is displayed here (and the user is then null). */
          invitedEmail?: string;
          createdAt?: Date;
          /** On invites, indicates when it expires and becomes invalid. */
          expiresAt?: Date;
          /** The .ics URL to the membership's calendar. */
          calendarFeedUrl?: string;
        } & {
          indexPermissions?: string[];
        } & {
          user?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            createdAt?: Date;
            locale?: string;
          } & {
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            memberships?: Array<
              {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
          };
          company?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
        }
      >;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/session`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** POST /session */
  public async createSession(params: {
    email?: string | null;
    password?: string | null;
    language?: string | null;
    rememberLogin?: boolean | null;
  }): Promise<
    EntityResponse<{
      /** A unique identifier that should be send with every request that requires a session */
      token: string;
      /** ISO code of the current user's language */
      language: string;
      /** Indicates until when session is valid and will be purged */
      validUntil: Date;
      /** The user's ID (or null) */
      userId?: string;
      /** List of the membership this user has, with minimal information on the companies she/he has access to. */
      memberships: Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          role?: string;
          comment?: string;
          permissions?: string[];
          /** On invites, the email of the invitee is displayed here (and the user is then null). */
          invitedEmail?: string;
          createdAt?: Date;
          /** On invites, indicates when it expires and becomes invalid. */
          expiresAt?: Date;
          /** The .ics URL to the membership's calendar. */
          calendarFeedUrl?: string;
        } & {
          indexPermissions?: string[];
        } & {
          user?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            createdAt?: Date;
            locale?: string;
          } & {
            /** A file asset */
            avatar?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            memberships?: Array<
              {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                company?: {
                  name?: string;
                  address?: {
                    company?: string;
                    firstName?: string;
                    lastName?: string;
                    address?: string;
                    address2?: string;
                    postCode?: string;
                    city?: string;
                    countryCode?: string;
                    email?: string;
                    phone?: string;
                  };
                  bankAccount?: {
                    name?: string;
                    iban?: string;
                    bic?: string;
                    bankName?: string;
                  };
                  vatId?: string;
                  vatNumber?: string;
                  eventCreditorReference?: string;
                  merchCreditorReference?: string;
                  merchClearingOrderId?: string;
                  features?: string[];
                  invoicingCycle?: string;
                  billingEmails?: string[];
                  assignableRoles?: string[];
                  defaultEventPayoutPercentage?: number;
                  eventPayoutCycle?: string;
                  lastEventPayout?: Date;
                } & {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                  slug: string;
                };
              }
            >;
          };
          company?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
        }
      >;
    }>
  > {
    const headers = {};
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/session`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['email', 'password', 'language', 'rememberLogin'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /session */
  public async updateSession(params: { language?: string | null }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The name of this event. Usually this is some title or the festivals name. */
        name?: string;
        /** The name of the artist, as to be written on the page. Be careful, this doesn't have to match the name of the associated artists. */
        artist?: string;
        /** Array of assigned artists */
        artists?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          }
        >;
        /** The total price for the cheapest variant of this event. */
        price?: number;
        /** A hint to display a map to facilitate the selection of different variants from a map. */
        displayMap?: boolean;
        /** At which time the doors to the venue open (in the time zone of the venue) */
        doorTime?: string;
        /** At which time the show begins (in the time zone of the venue) */
        stageTime?: string;
        /** Date at which the event starts */
        happeningFrom?: Date;
        /** Date at which the event stops */
        happeningUntil?: Date;
        /** List the ids of categories where this event is assigned for the event's shop */
        categoryIds?: string[];
        meta?: {};
        presaleAt?: Date;
        /** Only set when event is sold out */
        soldOutMessage?: string;
        /** A file asset */
        image?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** The maximum count of items of this product that can be added to a cart. */
        cartLimit?: number;
        description?: string;
        publicShippableFrom?: Date;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/session`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['language'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /session */
  public async destroySession(): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/session`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /sessions_stats/age_active_shop */
  public async showSessionsStatsAgeActive(): Promise<EntityResponse<{}>> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/sessions_stats/age_active_shop`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /sessions_stats/age_all_shop */
  public async showSessionsStatsAgeAll(): Promise<EntityResponse<{}>> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/sessions_stats/age_all_shop`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /sessions_stats/waiting_time_shop */
  public async showSessionsStatsWaitingTime(): Promise<EntityResponse<{}>> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/sessions_stats/waiting_time_shop`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /shops */
  public async indexShops(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the categories by. If not specified, they are sorted by `order`. */
    sortBy?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The actual name of this shop, displayable to the customer. */
          name?: string;
          /** Default locale (language & formats) of this shop. */
          defaultLocale?: string;
          /** Is the 'waiting queue' feature enabled for this shop? */
          waitingQueue?: boolean;
          /** Is this shop listed in the sitemap? */
          sitemap?: boolean;
          /** Extra information for this shop. */
          meta?: {};
          domain?: string;
          orderMergingAllowed?: boolean;
          paymentBlacklist?: string[];
          /** A Base64 reprensentation of the image. */
          logo?: FileRepresenting | string;
          /** Minimum amount to get free shipping */
          freeShippingThreshold?: number;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/shops`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexShops', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /shops */
  public async createShop(params: {
    slug?: string | null;
    name?: string | null;
    defaultLocale?: string | null;
    waitingQueue?: boolean | null;
    sitemap?: boolean | null;
    meta?: {} | null;
    domain?: string | null;
    orderMergingAllowed?: boolean | null;
    paymentBlacklist?: string[] | null;
    logo?: FileRepresenting | string | null;
    freeShippingThreshold?: number | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The actual name of this shop, displayable to the customer. */
        name?: string;
        /** Default locale (language & formats) of this shop. */
        defaultLocale?: string;
        /** Is the 'waiting queue' feature enabled for this shop? */
        waitingQueue?: boolean;
        /** Is this shop listed in the sitemap? */
        sitemap?: boolean;
        /** Extra information for this shop. */
        meta?: {};
        domain?: string;
        orderMergingAllowed?: boolean;
        paymentBlacklist?: string[];
        /** A Base64 reprensentation of the image. */
        logo?: FileRepresenting | string;
        /** Minimum amount to get free shipping */
        freeShippingThreshold?: number;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/shops`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'slug',
          'name',
          'defaultLocale',
          'waitingQueue',
          'sitemap',
          'meta',
          'domain',
          'orderMergingAllowed',
          'paymentBlacklist',
          'logo',
          'freeShippingThreshold',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /shops/{shop-id} */
  public async showShop(params: {
    /** Slug or Identifier (UUID). */
    shopId: string;
    /** If set, returns an uncached version. */
    noCache?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The actual name of this shop, displayable to the customer. */
        name?: string;
        /** Default locale (language & formats) of this shop. */
        defaultLocale?: string;
        /** Is the 'waiting queue' feature enabled for this shop? */
        waitingQueue?: boolean;
        /** Is this shop listed in the sitemap? */
        sitemap?: boolean;
        /** Extra information for this shop. */
        meta?: {};
        domain?: string;
        orderMergingAllowed?: boolean;
        paymentBlacklist?: string[];
        /** A Base64 reprensentation of the image. */
        logo?: FileRepresenting | string;
        /** Minimum amount to get free shipping */
        freeShippingThreshold?: number;
      }
    >
  > {
    const headers = {};
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/shops/${params.shopId}`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['noCache'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /shops/{shop-id} */
  public async updateShop(params: {
    /** Slug or Identifier (UUID). */
    shopId: string;
    slug?: string | null;
    name?: string | null;
    defaultLocale?: string | null;
    waitingQueue?: boolean | null;
    sitemap?: boolean | null;
    meta?: {} | null;
    domain?: string | null;
    orderMergingAllowed?: boolean | null;
    paymentBlacklist?: string[] | null;
    logo?: FileRepresenting | string | null;
    freeShippingThreshold?: number | null;
    /** If set, returns an uncached version. */
    noCache?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The actual name of this shop, displayable to the customer. */
        name?: string;
        /** Default locale (language & formats) of this shop. */
        defaultLocale?: string;
        /** Is the 'waiting queue' feature enabled for this shop? */
        waitingQueue?: boolean;
        /** Is this shop listed in the sitemap? */
        sitemap?: boolean;
        /** Extra information for this shop. */
        meta?: {};
        domain?: string;
        orderMergingAllowed?: boolean;
        paymentBlacklist?: string[];
        /** A Base64 reprensentation of the image. */
        logo?: FileRepresenting | string;
        /** Minimum amount to get free shipping */
        freeShippingThreshold?: number;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/shops/${params.shopId}`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['noCache'])),
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'slug',
          'name',
          'defaultLocale',
          'waitingQueue',
          'sitemap',
          'meta',
          'domain',
          'orderMergingAllowed',
          'paymentBlacklist',
          'logo',
          'freeShippingThreshold',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /shops/{shop-id} */
  public async destroyShop(params: {
    /** Slug or Identifier (UUID). */
    shopId: string;
    /** If set, returns an uncached version. */
    noCache?: boolean | null;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/shops/${params.shopId}`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['noCache'])),
      data: {},
    });
  }

  /** GET /storages */
  public async indexStorages(): Promise<
    EntityResponse<
      Array<{
        id?: string;
        slug?: string;
        location?: string;
        row?: string;
        field?: string;
        floor?: string;
        description?: string;
        /** A generated name for this location that is suitable to be displayed to a user */
        name?: string;
        createdAt?: Date;
        updatedAt?: Date;
      }>
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/storages`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /storages/{storage-id} */
  public async showStorage(params: {
    /** Identifier (UUID). */
    storageId: string;
  }): Promise<
    EntityResponse<{
      id?: string;
      slug?: string;
      location?: string;
      row?: string;
      field?: string;
      floor?: string;
      description?: string;
      /** A generated name for this location that is suitable to be displayed to a user */
      name?: string;
      createdAt?: Date;
      updatedAt?: Date;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/storages/${params.storageId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /tours */
  public async indexTours(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the tours by */
    sortBy?: string | null;
    /** If both start_date and end_date are supplied, only tours with overlapping start/end_date are included */
    startDate?: Date | null;
    /** If both start_date and end_date are supplied, only tours with overlapping start/end_date are included */
    endDate?: Date | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The category's name. */
          name?: string;
          /** The type of this category */
          type?: string;
        } & {
          /** Main categories that should be shown in the menu. */
          mainCategory?: boolean;
          parentCategoryId?: string;
          parentCategory?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A unique identifier in the UUID format. */
            shopId?: string;
            name?: string;
            /** The type of this category */
            type?: string;
          };
          /** Subcategories of this category. */
          subcategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            }
          >;
          /** The order of the category relative to it's sibling categories */
          order?: number;
          /** Alternative names for this category */
          alternativeNames?: string[];
          /** Short abstract describing the category */
          abstract?: string;
          /** Longer description of the category */
          description?: string;
          createdAt?: Date;
          updatedAt?: Date;
          featuredAt?: string;
          subtitle?: string;
          /** If false, the subtitle will not be included when the Shop API is reading the category. */
          subtitleInShop?: boolean;
          sortOrderHomepage?: number;
          sortOrderTicketTopLeft?: number;
          sortOrderTicketTopRight?: number;
          sortOrderTicketBottom?: number;
          comment?: string;
          /** Extra information for this category. */
          meta?: {};
          /** A file asset */
          squareImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          portraitImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          landscapeImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
        } & {
          /** Date at which the tour starts (date of its first event). */
          startDate?: Date;
          internalName?: string;
          /** Date at which the tour stops (date of its last event). */
          endDate?: Date;
          /** Counts of different grouping of sales. */
          salesStats?: {
            available: number;
            returned?: number;
            blocked: number;
            reserved: number;
            cart: number;
            /** Number of sold tickets. */
            sold: number;
            /** Number of sold tickets that are hard tickets (paper). */
            soldHard: number;
            /** Number of sold ticket that are online ticket. */
            soldOnline: number;
            /** Number of tickets that have a personalization set. */
            personalized: number;
          };
          publishedFrom?: Date;
          publishedUntil?: Date;
          /** Exclude the product from feeds (ex: Facebook, Google). */
          excludeFromFeeds?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          shop?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** The actual name of this shop, displayable to the customer. */
            name?: string;
            /** Default locale (language & formats) of this shop. */
            defaultLocale?: string;
            /** Is the 'waiting queue' feature enabled for this shop? */
            waitingQueue?: boolean;
            /** Is this shop listed in the sitemap? */
            sitemap?: boolean;
            /** Extra information for this shop. */
            meta?: {};
            domain?: string;
            orderMergingAllowed?: boolean;
            paymentBlacklist?: string[];
            /** A Base64 reprensentation of the image. */
            logo?: FileRepresenting | string;
            /** Minimum amount to get free shipping */
            freeShippingThreshold?: number;
          };
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/tours`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'startDate', 'endDate'])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexTours', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /tours */
  public async createTour(params: {
    name?: string | null;
    type?: string | null;
    mainCategory?: boolean | null;
    parentCategoryId?: string | null;
    parentCategory?:
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        })
      | null;
    order?: number | null;
    alternativeNames?: string[] | null;
    abstract?: string | null;
    description?: string | null;
    createdAt?: Date | null;
    updatedAt?: Date | null;
    featuredAt?: string | null;
    subtitle?: string | null;
    subtitleInShop?: boolean | null;
    sortOrderHomepage?: number | null;
    sortOrderTicketTopLeft?: number | null;
    sortOrderTicketTopRight?: number | null;
    sortOrderTicketBottom?: number | null;
    comment?: string | null;
    meta?: {} | null;
    internalName?: string | null;
    salesStats?: {
      available: number;
      returned?: number | null;
      blocked: number;
      reserved: number;
      cart: number;
      /** Number of sold tickets. */
      sold: number;
      /** Number of sold tickets that are hard tickets (paper). */
      soldHard: number;
      /** Number of sold ticket that are online ticket. */
      soldOnline: number;
      /** Number of tickets that have a personalization set. */
      personalized: number;
    } | null;
    excludeFromFeeds?: boolean | null;
    shopId?: string | null;
    artistIds?: string[] | null;
    ticketImage?: FileRepresenting | string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The category's name. */
        name?: string;
        /** The type of this category */
        type?: string;
      } & {
        /** Main categories that should be shown in the menu. */
        mainCategory?: boolean;
        parentCategoryId?: string;
        parentCategory?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        };
        /** Subcategories of this category. */
        subcategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          }
        >;
        /** The order of the category relative to it's sibling categories */
        order?: number;
        /** Alternative names for this category */
        alternativeNames?: string[];
        /** Short abstract describing the category */
        abstract?: string;
        /** Longer description of the category */
        description?: string;
        createdAt?: Date;
        updatedAt?: Date;
        featuredAt?: string;
        subtitle?: string;
        /** If false, the subtitle will not be included when the Shop API is reading the category. */
        subtitleInShop?: boolean;
        sortOrderHomepage?: number;
        sortOrderTicketTopLeft?: number;
        sortOrderTicketTopRight?: number;
        sortOrderTicketBottom?: number;
        comment?: string;
        /** Extra information for this category. */
        meta?: {};
        /** A file asset */
        squareImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        portraitImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        landscapeImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      } & {
        /** Date at which the tour starts (date of its first event). */
        startDate?: Date;
        internalName?: string;
        /** Date at which the tour stops (date of its last event). */
        endDate?: Date;
        /** Counts of different grouping of sales. */
        salesStats?: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        publishedFrom?: Date;
        publishedUntil?: Date;
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        shop?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The actual name of this shop, displayable to the customer. */
          name?: string;
          /** Default locale (language & formats) of this shop. */
          defaultLocale?: string;
          /** Is the 'waiting queue' feature enabled for this shop? */
          waitingQueue?: boolean;
          /** Is this shop listed in the sitemap? */
          sitemap?: boolean;
          /** Extra information for this shop. */
          meta?: {};
          domain?: string;
          orderMergingAllowed?: boolean;
          paymentBlacklist?: string[];
          /** A Base64 reprensentation of the image. */
          logo?: FileRepresenting | string;
          /** Minimum amount to get free shipping */
          freeShippingThreshold?: number;
        };
      } & {
        artists: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          }
        >;
        /** A file asset */
        ticketImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/tours`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'type',
          'mainCategory',
          'parentCategoryId',
          'parentCategory',
          'order',
          'alternativeNames',
          'abstract',
          'description',
          'createdAt',
          'updatedAt',
          'featuredAt',
          'subtitle',
          'subtitleInShop',
          'sortOrderHomepage',
          'sortOrderTicketTopLeft',
          'sortOrderTicketTopRight',
          'sortOrderTicketBottom',
          'comment',
          'meta',
          'internalName',
          'salesStats',
          'excludeFromFeeds',
          'shopId',
          'artistIds',
          'ticketImage',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /tours/{tour-id} */
  public async showTour(params: {
    /** Identifier (UUID). */
    tourId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The category's name. */
        name?: string;
        /** The type of this category */
        type?: string;
      } & {
        /** Main categories that should be shown in the menu. */
        mainCategory?: boolean;
        parentCategoryId?: string;
        parentCategory?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        };
        /** Subcategories of this category. */
        subcategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          }
        >;
        /** The order of the category relative to it's sibling categories */
        order?: number;
        /** Alternative names for this category */
        alternativeNames?: string[];
        /** Short abstract describing the category */
        abstract?: string;
        /** Longer description of the category */
        description?: string;
        createdAt?: Date;
        updatedAt?: Date;
        featuredAt?: string;
        subtitle?: string;
        /** If false, the subtitle will not be included when the Shop API is reading the category. */
        subtitleInShop?: boolean;
        sortOrderHomepage?: number;
        sortOrderTicketTopLeft?: number;
        sortOrderTicketTopRight?: number;
        sortOrderTicketBottom?: number;
        comment?: string;
        /** Extra information for this category. */
        meta?: {};
        /** A file asset */
        squareImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        portraitImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        landscapeImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      } & {
        /** Date at which the tour starts (date of its first event). */
        startDate?: Date;
        internalName?: string;
        /** Date at which the tour stops (date of its last event). */
        endDate?: Date;
        /** Counts of different grouping of sales. */
        salesStats?: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        publishedFrom?: Date;
        publishedUntil?: Date;
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        shop?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The actual name of this shop, displayable to the customer. */
          name?: string;
          /** Default locale (language & formats) of this shop. */
          defaultLocale?: string;
          /** Is the 'waiting queue' feature enabled for this shop? */
          waitingQueue?: boolean;
          /** Is this shop listed in the sitemap? */
          sitemap?: boolean;
          /** Extra information for this shop. */
          meta?: {};
          domain?: string;
          orderMergingAllowed?: boolean;
          paymentBlacklist?: string[];
          /** A Base64 reprensentation of the image. */
          logo?: FileRepresenting | string;
          /** Minimum amount to get free shipping */
          freeShippingThreshold?: number;
        };
      } & {
        artists: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          }
        >;
        /** A file asset */
        ticketImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/tours/${params.tourId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /tours/{tour-id} */
  public async updateTour(params: {
    /** Identifier (UUID). */
    tourId: string;
    name?: string | null;
    type?: string | null;
    mainCategory?: boolean | null;
    parentCategoryId?: string | null;
    parentCategory?:
      | ({
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        })
      | null;
    order?: number | null;
    alternativeNames?: string[] | null;
    abstract?: string | null;
    description?: string | null;
    createdAt?: Date | null;
    updatedAt?: Date | null;
    featuredAt?: string | null;
    subtitle?: string | null;
    subtitleInShop?: boolean | null;
    sortOrderHomepage?: number | null;
    sortOrderTicketTopLeft?: number | null;
    sortOrderTicketTopRight?: number | null;
    sortOrderTicketBottom?: number | null;
    comment?: string | null;
    meta?: {} | null;
    internalName?: string | null;
    salesStats?: {
      available: number;
      returned?: number | null;
      blocked: number;
      reserved: number;
      cart: number;
      /** Number of sold tickets. */
      sold: number;
      /** Number of sold tickets that are hard tickets (paper). */
      soldHard: number;
      /** Number of sold ticket that are online ticket. */
      soldOnline: number;
      /** Number of tickets that have a personalization set. */
      personalized: number;
    } | null;
    excludeFromFeeds?: boolean | null;
    shopId?: string | null;
    artistIds?: string[] | null;
    ticketImage?: FileRepresenting | string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        /** The category's name. */
        name?: string;
        /** The type of this category */
        type?: string;
      } & {
        /** Main categories that should be shown in the menu. */
        mainCategory?: boolean;
        parentCategoryId?: string;
        parentCategory?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A unique identifier in the UUID format. */
          shopId?: string;
          name?: string;
          /** The type of this category */
          type?: string;
        };
        /** Subcategories of this category. */
        subcategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          }
        >;
        /** The order of the category relative to it's sibling categories */
        order?: number;
        /** Alternative names for this category */
        alternativeNames?: string[];
        /** Short abstract describing the category */
        abstract?: string;
        /** Longer description of the category */
        description?: string;
        createdAt?: Date;
        updatedAt?: Date;
        featuredAt?: string;
        subtitle?: string;
        /** If false, the subtitle will not be included when the Shop API is reading the category. */
        subtitleInShop?: boolean;
        sortOrderHomepage?: number;
        sortOrderTicketTopLeft?: number;
        sortOrderTicketTopRight?: number;
        sortOrderTicketBottom?: number;
        comment?: string;
        /** Extra information for this category. */
        meta?: {};
        /** A file asset */
        squareImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        portraitImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        /** A file asset */
        landscapeImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      } & {
        /** Date at which the tour starts (date of its first event). */
        startDate?: Date;
        internalName?: string;
        /** Date at which the tour stops (date of its last event). */
        endDate?: Date;
        /** Counts of different grouping of sales. */
        salesStats?: {
          available: number;
          returned?: number;
          blocked: number;
          reserved: number;
          cart: number;
          /** Number of sold tickets. */
          sold: number;
          /** Number of sold tickets that are hard tickets (paper). */
          soldHard: number;
          /** Number of sold ticket that are online ticket. */
          soldOnline: number;
          /** Number of tickets that have a personalization set. */
          personalized: number;
        };
        publishedFrom?: Date;
        publishedUntil?: Date;
        /** Exclude the product from feeds (ex: Facebook, Google). */
        excludeFromFeeds?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        shop?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** The actual name of this shop, displayable to the customer. */
          name?: string;
          /** Default locale (language & formats) of this shop. */
          defaultLocale?: string;
          /** Is the 'waiting queue' feature enabled for this shop? */
          waitingQueue?: boolean;
          /** Is this shop listed in the sitemap? */
          sitemap?: boolean;
          /** Extra information for this shop. */
          meta?: {};
          domain?: string;
          orderMergingAllowed?: boolean;
          paymentBlacklist?: string[];
          /** A Base64 reprensentation of the image. */
          logo?: FileRepresenting | string;
          /** Minimum amount to get free shipping */
          freeShippingThreshold?: number;
        };
      } & {
        artists: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** The category's name. */
            name?: string;
            /** The type of this category */
            type?: string;
          } & {
            /** Main categories that should be shown in the menu. */
            mainCategory?: boolean;
            parentCategoryId?: string;
            parentCategory?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A unique identifier in the UUID format. */
              shopId?: string;
              name?: string;
              /** The type of this category */
              type?: string;
            };
            /** Subcategories of this category. */
            subcategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              }
            >;
            /** The order of the category relative to it's sibling categories */
            order?: number;
            /** Alternative names for this category */
            alternativeNames?: string[];
            /** Short abstract describing the category */
            abstract?: string;
            /** Longer description of the category */
            description?: string;
            createdAt?: Date;
            updatedAt?: Date;
            featuredAt?: string;
            subtitle?: string;
            /** If false, the subtitle will not be included when the Shop API is reading the category. */
            subtitleInShop?: boolean;
            sortOrderHomepage?: number;
            sortOrderTicketTopLeft?: number;
            sortOrderTicketTopRight?: number;
            sortOrderTicketBottom?: number;
            comment?: string;
            /** Extra information for this category. */
            meta?: {};
            /** A file asset */
            squareImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            portraitImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            landscapeImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            /** A file asset */
            headerImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            sliderImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            ticketImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            /** A file asset */
            backgroundImage?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
          } & {
            tours?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              } & {
                /** The category's name. */
                name?: string;
                /** The type of this category */
                type?: string;
              } & {
                /** Main categories that should be shown in the menu. */
                mainCategory?: boolean;
                parentCategoryId?: string;
                parentCategory?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** A unique identifier in the UUID format. */
                  shopId?: string;
                  name?: string;
                  /** The type of this category */
                  type?: string;
                };
                /** Subcategories of this category. */
                subcategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    /** The category's name. */
                    name?: string;
                    /** The type of this category */
                    type?: string;
                  }
                >;
                /** The order of the category relative to it's sibling categories */
                order?: number;
                /** Alternative names for this category */
                alternativeNames?: string[];
                /** Short abstract describing the category */
                abstract?: string;
                /** Longer description of the category */
                description?: string;
                createdAt?: Date;
                updatedAt?: Date;
                featuredAt?: string;
                subtitle?: string;
                /** If false, the subtitle will not be included when the Shop API is reading the category. */
                subtitleInShop?: boolean;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                comment?: string;
                /** Extra information for this category. */
                meta?: {};
                /** A file asset */
                squareImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                portraitImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
                /** A file asset */
                landscapeImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              } & {
                /** Date at which the tour starts (date of its first event). */
                startDate?: Date;
                /** Date at which the tour stops (date of its last event). */
                endDate?: Date;
                sortOrderHomepage?: number;
                sortOrderTicketTopLeft?: number;
                sortOrderTicketTopRight?: number;
                sortOrderTicketBottom?: number;
                /** A file asset */
                ticketImage?: {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  url: string;
                  originalUrl?: string;
                  tags: string[];
                  alt?: string;
                  altDe?: string;
                  altEn?: string;
                  /** Object of sizes mapping to urls */
                  thumbs?: {
                    10: string;
                    75: string;
                    100: string;
                    150: string;
                    180: string;
                    240: string;
                    360: string;
                    370: string;
                    500: string;
                    740: string;
                    1000: string;
                    1500: string;
                  };
                };
              }
            >;
          }
        >;
        /** A file asset */
        ticketImage?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/tours/${params.tourId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'type',
          'mainCategory',
          'parentCategoryId',
          'parentCategory',
          'order',
          'alternativeNames',
          'abstract',
          'description',
          'createdAt',
          'updatedAt',
          'featuredAt',
          'subtitle',
          'subtitleInShop',
          'sortOrderHomepage',
          'sortOrderTicketTopLeft',
          'sortOrderTicketTopRight',
          'sortOrderTicketBottom',
          'comment',
          'meta',
          'internalName',
          'salesStats',
          'excludeFromFeeds',
          'shopId',
          'artistIds',
          'ticketImage',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /tours/{tour-id} */
  public async destroyTour(params: {
    /** Identifier (UUID). */
    tourId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/tours/${params.tourId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /tours/{tour-id}/events */
  public async indexTourEvents(params: {
    /** Identifier (UUID). */
    tourId: string;
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the events by. */
    sortBy?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** Representation in the old database. */
          legacyId?: string;
          /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
          name?: string;
          /** Ticket override for the name attribute */
          nameOverride?: string;
          /** aka ticket line 1 */
          artist?: string;
          /** Ticket override for the artist attribute */
          artistOverride?: string;
          /** aka ticket line 3 */
          ticketSupportLine?: string;
          /** Ticket override for the ticket_support_line attribute */
          ticketSupportLineOverride?: string;
          /** aka ticket line 4 */
          ticketHeadline?: string;
          /** Ticket override for the ticket_headline attribute */
          ticketHeadlineOverride?: string;
          ticketLayout?: string;
          onlineTicketLayout?: string;
          localTicketLayout?: string;
          externalReference?: string;
          /** This represents a box of content with a title and a description. */
          additionalInformation?: {
            title?: string;
            titleDe?: string;
            titleEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
          };
          /** Ticket override for the additional_information attribute */
          additionalInformationOverride?: string;
          /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
          immediatePaymentAtStockQuota?: number;
          /** The status of this event */
          status?: string;
          /** Date at which the event starts */
          happeningFrom?: Date;
          /** Date at which the event stops */
          happeningUntil?: Date;
          sku?: string;
          tourOrganizer?: string;
          localOrganizer?: string;
          /** Indicates when this event should be published */
          publishedFrom?: Date;
          /** Indicates when this event should be unpublished */
          publishedUntil?: Date;
          ageLimit?: number;
          personalizationRequirement?: string;
          soldOutMessage?: string;
          /** At which time the doors to the venue open (in the time zone of the venue) */
          doorTime?: string;
          /** At which time the show begins (in the time zone of the venue) */
          stageTime?: string;
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
          /** Link to download a pdf report of the venue layout with the current seat status applied. */
          allocationPdfDownloadUrl?: string;
          /** Link to download a pdf report of the event sales so far. */
          salesReportPdfDownloadUrl?: string;
          description?: string;
          descriptionEn?: string;
          descriptionDe?: string;
          shippableFrom?: Date;
          dhlShippingFrom?: Date;
          publicShippableFrom?: Date;
          customsTariffNumber?: string;
          combinedProductIds?: string[][];
          meta?: {};
          /** Exclude the product from feeds (ex: Facebook, Google). */
          excludeFromFeeds?: boolean;
          publicTransportDescription?: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          owningCompany?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
          ownerships?: Array<
            {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              role?: string;
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
          venue?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            venueLayouts?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                capacity?: number;
                /** A link to a pdf containing a detailed seatmap. */
                detailPdfDownloadUrl?: string;
                /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
                readyForEvents?: boolean;
                /** Indicates the latest status of the import. If null, it was not imported. */
                importStatus?: string;
                archived?: boolean;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                pricingCategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    name?: string;
                  }
                >;
              }
            >;
          };
          logos: Array<
            {
              tags?: string[];
            } & {
              /** A file asset */
              image: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          venueLayout?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          };
          categories?: Array<{
            order?: number;
            category: {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            };
            /** The URL of the product in the shop of the described category. */
            urlInShop: string;
          }>;
          /** Counts of different grouping of sales. */
          salesStats: {
            available: number;
            returned?: number;
            blocked: number;
            reserved: number;
            cart: number;
            /** Number of sold tickets. */
            sold: number;
            /** Number of sold tickets that are hard tickets (paper). */
            soldHard: number;
            /** Number of sold ticket that are online ticket. */
            soldOnline: number;
            /** Number of tickets that have a personalization set. */
            personalized: number;
          };
          /** A file asset */
          image?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          tourImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketHeaderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          productVariants: Array<{
            id?: string;
            sku?: string;
            description: string;
            basePrice: number;
            presaleFee?: number;
            presaleFeePercentage?: number;
            systemFee: number;
            refundDeduction?: number;
            physical: boolean;
            taxRate: number;
            activeFrom?: Date;
            activeUntil?: Date;
            /** Indicates if this variant should be excluded from public facing endpoints */
            private?: boolean;
            /** Indicates if this variant should be for local pricing instead of system pricing */
            local?: boolean;
            priceParts?: Array<{
              description: string;
              price: number;
            }>;
          }>;
          clearingOrderId?: string;
          checkInStartAt?: Date;
          checkInEndAt?: Date;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/tours/${params.tourId}/events`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'sortDir', 'sortBy'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexTourEvents', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** GET /tours/{tour-id}/sales_stats */
  public async showTourSalesStats(params: {
    /** Identifier (UUID). */
    tourId: string;
  }): Promise<
    EntityResponse<
      Array<{
        available: number;
        returned?: number;
        blocked: number;
        reserved: number;
        cart: number;
        /** Number of sold tickets. */
        sold: number;
        /** Number of sold tickets that are hard tickets (paper). */
        soldHard: number;
        /** Number of sold ticket that are online ticket. */
        soldOnline: number;
        /** Number of tickets that have a personalization set. */
        personalized: number;
      }>
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/tours/${params.tourId}/sales_stats`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /users */
  public async indexUsers(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the events by. */
    sortBy?: string | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          firstName?: string;
          lastName?: string;
          email?: string;
          phone?: string;
          createdAt?: Date;
          locale?: string;
        } & {
          /** A file asset */
          avatar?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          memberships?: Array<
            {
              role?: string;
              comment?: string;
              permissions?: string[];
              /** On invites, the email of the invitee is displayed here (and the user is then null). */
              invitedEmail?: string;
              createdAt?: Date;
              /** On invites, indicates when it expires and becomes invalid. */
              expiresAt?: Date;
              /** The .ics URL to the membership's calendar. */
              calendarFeedUrl?: string;
            } & {
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/users`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexUsers', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /users */
  public async createUser(params: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    createdAt?: Date | null;
    locale?: string | null;
    avatar?: FileRepresenting | string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        createdAt?: Date;
        locale?: string;
      } & {
        /** A file asset */
        avatar?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        memberships?: Array<
          {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/users`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['firstName', 'lastName', 'email', 'phone', 'createdAt', 'locale', 'avatar'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /users/{user-id} */
  public async showUser(params: {
    /** Identifier (UUID). */
    userId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        createdAt?: Date;
        locale?: string;
      } & {
        /** A file asset */
        avatar?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        memberships?: Array<
          {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/users/${params.userId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /users/{user-id} */
  public async updateUser(params: {
    /** Identifier (UUID). */
    userId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    createdAt?: Date | null;
    locale?: string | null;
    avatar?: FileRepresenting | string | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        createdAt?: Date;
        locale?: string;
      } & {
        /** A file asset */
        avatar?: {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          url: string;
          originalUrl?: string;
          tags: string[];
          alt?: string;
          altDe?: string;
          altEn?: string;
          /** Object of sizes mapping to urls */
          thumbs?: {
            10: string;
            75: string;
            100: string;
            150: string;
            180: string;
            240: string;
            360: string;
            370: string;
            500: string;
            740: string;
            1000: string;
            1500: string;
          };
        };
        memberships?: Array<
          {
            role?: string;
            comment?: string;
            permissions?: string[];
            /** On invites, the email of the invitee is displayed here (and the user is then null). */
            invitedEmail?: string;
            createdAt?: Date;
            /** On invites, indicates when it expires and becomes invalid. */
            expiresAt?: Date;
            /** The .ics URL to the membership's calendar. */
            calendarFeedUrl?: string;
          } & {
            company?: {
              name?: string;
              address?: {
                company?: string;
                firstName?: string;
                lastName?: string;
                address?: string;
                address2?: string;
                postCode?: string;
                city?: string;
                countryCode?: string;
                email?: string;
                phone?: string;
              };
              bankAccount?: {
                name?: string;
                iban?: string;
                bic?: string;
                bankName?: string;
              };
              vatId?: string;
              vatNumber?: string;
              eventCreditorReference?: string;
              merchCreditorReference?: string;
              merchClearingOrderId?: string;
              features?: string[];
              invoicingCycle?: string;
              billingEmails?: string[];
              assignableRoles?: string[];
              defaultEventPayoutPercentage?: number;
              eventPayoutCycle?: string;
              lastEventPayout?: Date;
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
              slug: string;
            };
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/users/${params.userId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['firstName', 'lastName', 'email', 'phone', 'createdAt', 'locale', 'avatar'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /users/{user-id} */
  public async deleteUser(params: {
    /** Identifier (UUID). */
    userId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/users/${params.userId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /venue_layouts/{venue-layout-id} */
  public async showVenueLayout(params: {
    /** Identifier (UUID). */
    venueLayoutId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name?: string;
        capacity?: number;
        /** A link to a pdf containing a detailed seatmap. */
        detailPdfDownloadUrl?: string;
        /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
        readyForEvents?: boolean;
        /** Indicates the latest status of the import. If null, it was not imported. */
        importStatus?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        pricingCategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venue_layouts/${params.venueLayoutId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /venue_layouts/{venue-layout-id} */
  public async updateVenueLayout(params: {
    /** Identifier (UUID). */
    venueLayoutId: string;
    name?: string | null;
    capacity?: number | null;
    detailPdfDownloadUrl?: string | null;
    archived?: boolean | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name?: string;
        capacity?: number;
        /** A link to a pdf containing a detailed seatmap. */
        detailPdfDownloadUrl?: string;
        /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
        readyForEvents?: boolean;
        /** Indicates the latest status of the import. If null, it was not imported. */
        importStatus?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        pricingCategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/venue_layouts/${params.venueLayoutId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), ['name', 'capacity', 'detailPdfDownloadUrl', 'archived'])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /venue_layouts/{venue-layout-id} */
  public async destroyVenueLayout(params: {
    /** Identifier (UUID). */
    venueLayoutId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/venue_layouts/${params.venueLayoutId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /venue_layouts/{venue-layout-id}/seatmap */
  public async showSeatmap(params: {
    /** Identifier (UUID). */
    venueLayoutId: string;
    /** If 'true', will not cache the seatmap. Useful for debugging reasons, or when Connect needs to display the latest information to the administrator.  */
    debug?: boolean | null;
  }): Promise<
    EntityResponse<{
      /** A list of points. */
      boundingBox?: number[][];
      graphics?: Array<{
        /** A unique identifier in the UUID format. */
        id?: string;
        name?: string;
        /** A list of points. */
        points?: number[][];
        /** An array of 2 number describing a point. */
        center?: number[];
      }>;
      sections?: Array<{
        /** A unique identifier in the UUID format. */
        id?: string;
        name?: string;
        /** A list of points. */
        hullPoints?: number[][];
        generalAdmission?: boolean;
        seats?: Array<{
          id?: string;
          x?: number;
          y?: number;
          row?: string;
          number?: string;
        }>;
      }>;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venue_layouts/${params.venueLayoutId}/seatmap`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['debug'])),
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /venues */
  public async indexVenues(params: {
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** A term that will be used to fuzzily filter the items. It can contain multiple terms devided by `&`. Use `*` as a wildcard character. A precending `-` indicates a negation, i.e. only items where no field contains the term will be returned. */
    term?: string | null;
    /** Case insensitive parameter for sorting. Sets the sort direction. Is only valid in combination with `sort_by`. */
    sortDir?: string | null;
    /** The field to sort the venues by. */
    sortBy?: string | null;
    /** Lists only the venues having at least one layout consisting of only of 1 general admission section. */
    singleGeneralAdmission?: boolean | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          name?: string;
          address?: {
            company?: string;
            firstName?: string;
            lastName?: string;
            address?: string;
            address2?: string;
            postCode?: string;
            city?: string;
            countryCode?: string;
            email?: string;
            phone?: string;
          };
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venues`,
      headers,
      params: humps.decamelizeKeys(
        pick(params, ['page', 'perPage', 'term', 'sortDir', 'sortBy', 'singleGeneralAdmission'])
      ),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexVenues', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /venues */
  public async createVenue(params: {
    slug?: string | null;
    name?: string | null;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        venueLayouts?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/venues`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['slug', 'name', 'address'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /venues/{venue-id} */
  public async showVenue(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        venueLayouts?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venues/${params.venueId}`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /venues/{venue-id} */
  public async updateVenue(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
    name?: string | null;
    address?: {
      company?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      address?: string | null;
      address2?: string | null;
      postCode?: string | null;
      city?: string | null;
      countryCode?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
        slug: string;
      } & {
        name?: string;
        address?: {
          company?: string;
          firstName?: string;
          lastName?: string;
          address?: string;
          address2?: string;
          postCode?: string;
          city?: string;
          countryCode?: string;
          email?: string;
          phone?: string;
        };
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        venueLayouts?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/venues/${params.venueId}`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['name', 'address'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** DELETE /venues/{venue-id} */
  public async destroyVenue(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
  }): Promise<void> {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    await this.httpClient({
      method: 'delete',
      url: `/venues/${params.venueId}`,
      headers,
      params: {},
      data: {},
    });
  }

  /** GET /venues/{venue-id}/events */
  public async indexVenueEvents(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
  }): Promise<
    EntityResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
          slug: string;
        } & {
          /** Representation in the old database. */
          legacyId?: string;
          /** The name of this event. Usually this is some title or the festivals name. (aka ticket line 2) */
          name?: string;
          /** Ticket override for the name attribute */
          nameOverride?: string;
          /** aka ticket line 1 */
          artist?: string;
          /** Ticket override for the artist attribute */
          artistOverride?: string;
          /** aka ticket line 3 */
          ticketSupportLine?: string;
          /** Ticket override for the ticket_support_line attribute */
          ticketSupportLineOverride?: string;
          /** aka ticket line 4 */
          ticketHeadline?: string;
          /** Ticket override for the ticket_headline attribute */
          ticketHeadlineOverride?: string;
          ticketLayout?: string;
          onlineTicketLayout?: string;
          localTicketLayout?: string;
          externalReference?: string;
          /** This represents a box of content with a title and a description. */
          additionalInformation?: {
            title?: string;
            titleDe?: string;
            titleEn?: string;
            description?: string;
            descriptionDe?: string;
            descriptionEn?: string;
          };
          /** Ticket override for the additional_information attribute */
          additionalInformationOverride?: string;
          /** Indicates at which stock quota only immediate payments options are allowed (must be between 0 and 1) */
          immediatePaymentAtStockQuota?: number;
          /** The status of this event */
          status?: string;
          /** Date at which the event starts */
          happeningFrom?: Date;
          /** Date at which the event stops */
          happeningUntil?: Date;
          sku?: string;
          tourOrganizer?: string;
          localOrganizer?: string;
          /** Indicates when this event should be published */
          publishedFrom?: Date;
          /** Indicates when this event should be unpublished */
          publishedUntil?: Date;
          ageLimit?: number;
          personalizationRequirement?: string;
          soldOutMessage?: string;
          /** At which time the doors to the venue open (in the time zone of the venue) */
          doorTime?: string;
          /** At which time the show begins (in the time zone of the venue) */
          stageTime?: string;
          /** The maximum count of items of this product that can be added to a cart. */
          cartLimit?: number;
          /** Link to download a pdf report of the venue layout with the current seat status applied. */
          allocationPdfDownloadUrl?: string;
          /** Link to download a pdf report of the event sales so far. */
          salesReportPdfDownloadUrl?: string;
          description?: string;
          descriptionEn?: string;
          descriptionDe?: string;
          shippableFrom?: Date;
          dhlShippingFrom?: Date;
          publicShippableFrom?: Date;
          customsTariffNumber?: string;
          combinedProductIds?: string[][];
          meta?: {};
          /** Exclude the product from feeds (ex: Facebook, Google). */
          excludeFromFeeds?: boolean;
          publicTransportDescription?: string;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          owningCompany?: {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
            bankAccount?: {
              name?: string;
              iban?: string;
              bic?: string;
              bankName?: string;
            };
            vatId?: string;
            vatNumber?: string;
            eventCreditorReference?: string;
            merchCreditorReference?: string;
            merchClearingOrderId?: string;
            features?: string[];
            invoicingCycle?: string;
            billingEmails?: string[];
            assignableRoles?: string[];
            defaultEventPayoutPercentage?: number;
            eventPayoutCycle?: string;
            lastEventPayout?: Date;
          } & {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            memberships?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                role?: string;
                comment?: string;
                permissions?: string[];
                /** On invites, the email of the invitee is displayed here (and the user is then null). */
                invitedEmail?: string;
                createdAt?: Date;
                /** On invites, indicates when it expires and becomes invalid. */
                expiresAt?: Date;
                /** The .ics URL to the membership's calendar. */
                calendarFeedUrl?: string;
              } & {
                indexPermissions?: string[];
              }
            >;
            /** A file asset */
            icon?: {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              url: string;
              originalUrl?: string;
              tags: string[];
              alt?: string;
              altDe?: string;
              altEn?: string;
              /** Object of sizes mapping to urls */
              thumbs?: {
                10: string;
                75: string;
                100: string;
                150: string;
                180: string;
                240: string;
                360: string;
                370: string;
                500: string;
                740: string;
                1000: string;
                1500: string;
              };
            };
            lastBookedClearingInvoice?: {
              /** A unique identifier in the UUID format. */
              id?: string;
              reference?: string;
              bookedAt?: Date;
              /** A unique identifier in the UUID format. */
              orderId?: string;
            };
          };
          ownerships?: Array<
            {
              /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
              $permissions: string[];
            } & {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              role?: string;
              company?: {
                name?: string;
                address?: {
                  company?: string;
                  firstName?: string;
                  lastName?: string;
                  address?: string;
                  address2?: string;
                  postCode?: string;
                  city?: string;
                  countryCode?: string;
                  email?: string;
                  phone?: string;
                };
                bankAccount?: {
                  name?: string;
                  iban?: string;
                  bic?: string;
                  bankName?: string;
                };
                vatId?: string;
                vatNumber?: string;
                eventCreditorReference?: string;
                merchCreditorReference?: string;
                merchClearingOrderId?: string;
                features?: string[];
                invoicingCycle?: string;
                billingEmails?: string[];
                assignableRoles?: string[];
                defaultEventPayoutPercentage?: number;
                eventPayoutCycle?: string;
                lastEventPayout?: Date;
              } & {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
                slug: string;
              };
            }
          >;
          venue?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            /** A human readable, but still unique, identifier. Normally used to simplify URLs to specific resources and avoid using their UUID identifier.  Since they are typically part of URLs, their name must follow the lowercase separate-words-with-hypens convention (see https://opensource.zalando.com/restful-api-guidelines/#129). */
            slug: string;
          } & {
            name?: string;
            address?: {
              company?: string;
              firstName?: string;
              lastName?: string;
              address?: string;
              address2?: string;
              postCode?: string;
              city?: string;
              countryCode?: string;
              email?: string;
              phone?: string;
            };
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            venueLayouts?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
                capacity?: number;
                /** A link to a pdf containing a detailed seatmap. */
                detailPdfDownloadUrl?: string;
                /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
                readyForEvents?: boolean;
                /** Indicates the latest status of the import. If null, it was not imported. */
                importStatus?: string;
                archived?: boolean;
              } & {
                /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
                $permissions: string[];
              } & {
                pricingCategories?: Array<
                  {
                    /** A unique identifier in the UUID format. */
                    id: string;
                  } & {
                    name?: string;
                  }
                >;
              }
            >;
          };
          logos: Array<
            {
              tags?: string[];
            } & {
              /** A file asset */
              image: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            }
          >;
          venueLayout?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
            capacity?: number;
            /** A link to a pdf containing a detailed seatmap. */
            detailPdfDownloadUrl?: string;
            /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
            readyForEvents?: boolean;
            /** Indicates the latest status of the import. If null, it was not imported. */
            importStatus?: string;
            archived?: boolean;
          } & {
            /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
            $permissions: string[];
          } & {
            pricingCategories?: Array<
              {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                name?: string;
              }
            >;
          };
          categories?: Array<{
            order?: number;
            category: {
              /** The category's name. */
              name?: string;
              /** The type of this category */
              type?: string;
            } & {
              /** Main categories that should be shown in the menu. */
              mainCategory?: boolean;
              parentCategoryId?: string;
              parentCategory?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                /** A unique identifier in the UUID format. */
                shopId?: string;
                name?: string;
                /** The type of this category */
                type?: string;
              };
              /** Subcategories of this category. */
              subcategories?: Array<
                {
                  /** A unique identifier in the UUID format. */
                  id: string;
                } & {
                  /** The category's name. */
                  name?: string;
                  /** The type of this category */
                  type?: string;
                }
              >;
              /** The order of the category relative to it's sibling categories */
              order?: number;
              /** Alternative names for this category */
              alternativeNames?: string[];
              /** Short abstract describing the category */
              abstract?: string;
              /** Longer description of the category */
              description?: string;
              createdAt?: Date;
              updatedAt?: Date;
              featuredAt?: string;
              subtitle?: string;
              /** If false, the subtitle will not be included when the Shop API is reading the category. */
              subtitleInShop?: boolean;
              sortOrderHomepage?: number;
              sortOrderTicketTopLeft?: number;
              sortOrderTicketTopRight?: number;
              sortOrderTicketBottom?: number;
              comment?: string;
              /** Extra information for this category. */
              meta?: {};
              /** A file asset */
              squareImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              portraitImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
              /** A file asset */
              landscapeImage?: {
                /** A unique identifier in the UUID format. */
                id: string;
              } & {
                url: string;
                originalUrl?: string;
                tags: string[];
                alt?: string;
                altDe?: string;
                altEn?: string;
                /** Object of sizes mapping to urls */
                thumbs?: {
                  10: string;
                  75: string;
                  100: string;
                  150: string;
                  180: string;
                  240: string;
                  360: string;
                  370: string;
                  500: string;
                  740: string;
                  1000: string;
                  1500: string;
                };
              };
            };
            /** The URL of the product in the shop of the described category. */
            urlInShop: string;
          }>;
          /** Counts of different grouping of sales. */
          salesStats: {
            available: number;
            returned?: number;
            blocked: number;
            reserved: number;
            cart: number;
            /** Number of sold tickets. */
            sold: number;
            /** Number of sold tickets that are hard tickets (paper). */
            soldHard: number;
            /** Number of sold ticket that are online ticket. */
            soldOnline: number;
            /** Number of tickets that have a personalization set. */
            personalized: number;
          };
          /** A file asset */
          image?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          tourImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          /** A file asset */
          ticketHeaderImage?: {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            url: string;
            originalUrl?: string;
            tags: string[];
            alt?: string;
            altDe?: string;
            altEn?: string;
            /** Object of sizes mapping to urls */
            thumbs?: {
              10: string;
              75: string;
              100: string;
              150: string;
              180: string;
              240: string;
              360: string;
              370: string;
              500: string;
              740: string;
              1000: string;
              1500: string;
            };
          };
          productVariants: Array<{
            id?: string;
            sku?: string;
            description: string;
            basePrice: number;
            presaleFee?: number;
            presaleFeePercentage?: number;
            systemFee: number;
            refundDeduction?: number;
            physical: boolean;
            taxRate: number;
            activeFrom?: Date;
            activeUntil?: Date;
            /** Indicates if this variant should be excluded from public facing endpoints */
            private?: boolean;
            /** Indicates if this variant should be for local pricing instead of system pricing */
            local?: boolean;
            priceParts?: Array<{
              description: string;
              price: number;
            }>;
          }>;
          clearingOrderId?: string;
          checkInStartAt?: Date;
          checkInEndAt?: Date;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venues/${params.venueId}/events`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /venues/{venue-id}/venue_layouts */
  public async indexVenueLayouts(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
    /** Parameter for pagination. Request the `page`th page of paginatable elements. */
    page?: number | null;
    /** Parameter for pagination. Request `per_page` elements per page. Note that this parameter might be ignored if it exceeds an internal maximum. */
    perPage?: number | null;
    /** Lists only the venue layouts having only of 1 general admission section. */
    singleGeneralAdmission?: boolean | null;
  }): Promise<
    PaginatedResponse<
      Array<
        {
          /** A unique identifier in the UUID format. */
          id: string;
        } & {
          name?: string;
          capacity?: number;
          /** A link to a pdf containing a detailed seatmap. */
          detailPdfDownloadUrl?: string;
          /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
          readyForEvents?: boolean;
          /** Indicates the latest status of the import. If null, it was not imported. */
          importStatus?: string;
          archived?: boolean;
        } & {
          /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
          $permissions: string[];
        } & {
          pricingCategories?: Array<
            {
              /** A unique identifier in the UUID format. */
              id: string;
            } & {
              name?: string;
            }
          >;
        }
      >
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/venues/${params.venueId}/venue_layouts`,
      headers,
      params: humps.decamelizeKeys(pick(params, ['page', 'perPage', 'singleGeneralAdmission'])),
      data: {},
    });
    const data = this.camelize(result.data);

    return {
      data,
      page: params.page || 1,
      ...this.extractPaginationData(result.headers, 'indexVenueLayouts', params),
      alternateLink: this.extractAlternateLink(result.headers),
    };
  }

  /** POST /venues/{venue-id}/venue_layouts */
  public async createVenueLayout(params: {
    /** Slug or Identifier (UUID). */
    venueId: string;
    name?: string | null;
    capacity?: number | null;
    detailPdfDownloadUrl?: string | null;
    archived?: boolean | null;
    seatsIoId?: string | null;
    scale?: number | null;
  }): Promise<
    EntityResponse<
      {
        /** A unique identifier in the UUID format. */
        id: string;
      } & {
        name?: string;
        capacity?: number;
        /** A link to a pdf containing a detailed seatmap. */
        detailPdfDownloadUrl?: string;
        /** Indicates if the layout is ready to be used by events (ex: not pending seats map import). */
        readyForEvents?: boolean;
        /** Indicates the latest status of the import. If null, it was not imported. */
        importStatus?: string;
        archived?: boolean;
      } & {
        /** A list of permissions on this object.  Typically are (without being limited to) the _Rails_ action names: - create - show - update - destroy */
        $permissions: string[];
      } & {
        pricingCategories?: Array<
          {
            /** A unique identifier in the UUID format. */
            id: string;
          } & {
            name?: string;
          }
        >;
      }
    >
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'post',
      url: `/venues/${params.venueId}/venue_layouts`,
      headers,
      params: {},
      data: humps.decamelizeKeys(
        pick(processFilesInParams(params), [
          'name',
          'capacity',
          'detailPdfDownloadUrl',
          'archived',
          'seatsIoId',
          'scale',
        ])
      ),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** GET /waiting_queue */
  public async showWaitingQueue(): Promise<
    EntityResponse<{
      /** The number of sessions (users) in the waiting queue */
      totalActiveUsersCount?: number;
      /** The maximum number of allowed sessions (user) in the checkout queue that do not have to wait */
      maximumSpotsCount?: number;
      /** The maximum number of allowed sessions (user) in the catalog queue that do not have to wait */
      maximumCatalogSpotsCount?: number;
      /** The number of sessions (users) in the queue that are waiting */
      waitingUsersCount?: number;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'get',
      url: `/waiting_queue`,
      headers,
      params: {},
      data: {},
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  /** PATCH /waiting_queue */
  public async updateWaitingQueue(params: {
    maximumSpotsCount?: number | null;
    maximumCatalogSpotsCount?: number | null;
  }): Promise<
    EntityResponse<{
      maximumSpotsCount?: number;
      maximumCatalogSpotsCount?: number;
    }>
  > {
    const roleTokenForRequest = await this.membershipAuthenticate!();
    const headers = {
      'x-ks-role': `${roleTokenForRequest}`,
    };
    this.headerInterceptor?.(headers);
    const result = await this.httpClient({
      method: 'patch',
      url: `/waiting_queue`,
      headers,
      params: {},
      data: humps.decamelizeKeys(pick(processFilesInParams(params), ['maximumSpotsCount', 'maximumCatalogSpotsCount'])),
    });
    const data = this.camelize(result.data);
    return { data, alternateLink: this.extractAlternateLink(result.headers) };
  }

  private camelize(object: any): any {
    if (Array.isArray(object)) {
      return object.map((e) => this.camelize(e));
    } else if (isObject(object)) {
      const parsed = {};
      Object.keys(object).forEach((key) => {
        parsed[humps.camelize(key)] = this.camelize(object[key]);
      });
      return parsed;
    } else if (typeof object === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(object)) {
      return new Date(Date.parse(object));
    } else {
      return object;
    }
  }

  private extractAlternateLink(headers: Record<string, string>): string | undefined {
    const alternateLink = (headers['link'] || '').split(',').find((l: string) => l.includes('rel="alternate"'));
    if (alternateLink) {
      return alternateLink.match(/^<(.+)>;/)?.[1];
    }
    return undefined;
  }

  private extractPaginationData<T>(
    headers: Record<string, string>,
    functionName: string,
    params: any
  ): {
    totalCount: number;
    totalPages: number;
    perPage: number;
    nextPage?: number;
    loadNext?: () => Promise<PaginatedResponse<T>>;
    permissions: Set<string>;
  } {
    const totalCount = headers['total-count'] || '0';
    const links = (headers['link'] || '').split(',');

    const lastLink = links.find((l: string) => l.includes('rel="last"')) || '';
    const totalPagesMatch = lastLink.match(/[&?]page=(\d+).*$/);
    const totalPages = totalPagesMatch ? totalPagesMatch[1] : '1';

    const perPageMatch = lastLink.match(/[&?]per_page=(\d+).*$/);
    const perPage = perPageMatch ? perPageMatch[1] : '0';

    const permissions = new Set<string>((headers['x-ks-permissions'] || '').split(','));

    const data: {
      totalCount: number;
      totalPages: number;
      perPage: number;
      nextPage?: number;
      loadNext?: () => Promise<PaginatedResponse<T>>;
      permissions: Set<string>;
    } = {
      totalCount: parseInt(totalCount, 10),
      totalPages: parseInt(totalPages, 10),
      perPage: parseInt(perPage, 10),
      permissions,
    };

    const nextLink = links.find((l: string) => l.includes('rel="next"'));
    if (nextLink) {
      const nextMatch = nextLink.match(/[&?]page=(\d+).*$/);
      if (nextMatch) {
        data.nextPage = parseInt(nextMatch[1], 10);
        const funcString = `this.${functionName}(Object.assign(${JSON.stringify(params)}, { page: ${data.nextPage} }))`;

        data.loadNext = new Function(funcString).bind(this) as () => Promise<PaginatedResponse<T>>;
      }
    }

    return data;
  }
}
/* eslint-enable */
